import { useOffline } from '../contexts/OfflineContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ProductionForecastModal from '../components/ProductionForecastModal';
import { useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUp,
  Boxes,
  BriefcaseBusiness,
  Building2,
  Calendar,
  Camera,
  CalendarClock,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  Copy,
  Download,
  Droplets,
  Edit3,
  History,
  Home,
  Image as ImageIcon,
  Info,
  LocateFixed,
  Maximize2,
  Minimize2,
  Navigation,
  MapPin,
  MessageSquare,
  Minus,
  Package,
  Paperclip,
  Phone,
  Plus,
  Printer,
  RefreshCw,
  ReceiptText,
  Settings,
  ShoppingCart,
  Shield,
  Siren,
  Sofa,
  RotateCcw,
  Search,
  Trash2,
  Truck,
  X,
  XCircle,
  BrushCleaning,
  User,
  UserCog,
  UserCheck,
  WashingMachine
} from 'lucide-react';
import { API_URL } from '../config';
import DiscountSelectorModal from '../components/DiscountSelectorModal';
import DatePicker from '../components/DatePicker';
import Dropdown from '../components/Dropdown';
import FloatingField from '../components/FloatingField';
import AssigneePicker from '../components/AssigneePicker';
import InvoiceEditorModal from '../components/InvoiceEditorModal';
import ItemDetailModal from '../components/ItemDetailModal';
import ItemSelectorModal from '../components/ItemSelectorModal';
import MapCurrentLocationButton from '../components/MapCurrentLocationButton';
import ModalShell from '../components/ModalShell';
import OrderServiceModal from '../components/OrderServiceModal';
import ProtectedImage, { CircularProgress } from '../components/ProtectedImage';
import RouteScheduleAccordion from '../components/RouteScheduleAccordion';
import RouteSuggestions from '../components/RouteSuggestions';
import RoutesMap from '../components/RoutesMap';
import AddressWithoutStreetConfirmModal from '../components/AddressWithoutStreetConfirmModal';
import { ConfirmModal } from '../components/ConfirmModal';
import TimeSlotPicker from '../components/TimeSlotPicker';
import useToast from '../hooks/useToast';
import { MapLoadingSkeleton } from '../components/Skeleton';
import useAuthUserType from '../hooks/useAuthUserType';
import useModalBackButton from '../hooks/useModalBackButton';
import { canCurrentUserApplyDiscount, parseDiscountSnapshot } from '../utils/discountAccess';
import { fetchProtectedBlob, uploadProtectedFileWithProgress } from '../utils/protectedMedia';
import { readOfflineRouteItem, shouldUseOfflineDetailFallback } from '../utils/offlineRoutesCache';
import { buildRoutesReturnPath } from '../utils/navigationContext';
import { formatTimezoneDate, getUserTimezone } from '../utils/timezone';
import { formatPriceCoefficient, getDepartmentPriceCoefficient } from '../utils/priceCoefficient';
import { compressImageFile } from '../utils/imageCompression';
import { haptic } from '../utils/vibration';
import { CURRENT_LOCATION_MAP_ZOOM, replaceCurrentLocationMarker } from '../utils/mapCurrentLocation';
import { getYandexAddressUrl, getYandexMapsUrl, getYandexNavigatorUrl, loadYandexMaps, loadYandexMapsV3, suppressYandexOpenMapsButton } from '../utils/yandexMaps';
import { geocodeAddressWithNominatim, readStoredAddressCoordinates, resolvePreferredAddressCoordinates } from '../utils/addressCoordinates';
import { getServiceAddressValidation, incompleteServiceAddressMessage, isCompleteServiceAddress } from '../utils/serviceAddressValidation';
import MapProviderToggle, { getStoredMapProvider } from '../components/MapProviderToggle';
import yaMapsIcon from '../assets/images/ya_maps___.svg';
import yandexNavigatorIcon from '../assets/images/yandex_navigator_.svg';
import dgisIcon from '../assets/images/dgis_icon.svg';
import { getDgisAddressUrl, getDgisRouteUrl } from '../utils/dgisMaps';
import L from 'leaflet';

const DETAIL_PAGE_BOTTOM_COMPENSATION = 'calc(-1 * max(4rem, calc(max(env(safe-area-inset-bottom, 0px), var(--app-visual-viewport-bottom-inset, 0px)) + 0.75rem)))';
const ORDER_ITEM_BADGE_BUTTON_CLASS = 'cart-btn inline-flex h-5 w-5 shrink-0 appearance-none items-center justify-center rounded-full border-0 p-0 align-middle text-[0] leading-none';
const ORDER_ITEM_BADGE_BUTTON_STYLE = {
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  flex: '0 0 20px',
  padding: 0,
  border: 0,
  outline: 'none',
  boxShadow: 'none',
  boxSizing: 'border-box',
};

const ORDER_MAP_DEFAULT_OPEN_STAGE_IDS = new Set([
  'pickup',
  'approval',
  'delivery',
  'cleaning',
]);

const buildOfflineOrderItem = (line, index, orderId) => ({
  id: -(index + 1),
  order_id: orderId,
  item_name: String(line || '').trim() || `Позиция ${index + 1}`,
  quantity: 1,
  unit: null,
  price: 0,
  subtotal: 0,
  is_additional: false,
  additional_type: null,
  discount_percent: 0,
  processing_status: 'pending',
  processing_route: null,
  processing_route_label: null,
  processing_stage_name: null,
  revision_selected: false,
  revision_locked_ready: false,
  rework_selected: false,
  stage_statuses: [],
  sort_order: index + 1,
});

const buildOfflineOrderFromRouteItem = (routeItem) => {
  const orderId = Number(routeItem?.order_id) || 0;
  const stageName = routeItem?.stage_name || 'pickup';
  const isDeliveryStage = stageName === 'delivery';
  const scheduledDate = routeItem?.scheduled_date || null;
  const addressText = routeItem?.address_text || '';
  const latitude = routeItem?.latitude ?? null;
  const longitude = routeItem?.longitude ?? null;
  const itemsSummary = Array.isArray(routeItem?.items_summary) ? routeItem.items_summary : [];
  const createdDate = routeItem?.updated_at || scheduledDate || new Date().toISOString();
  const finalAmount = Number(routeItem?.final_amount || 0);
  const remainingAmount = Number(routeItem?.remaining_amount || 0);

  return {
    id: orderId,
    order_number: routeItem?.order_number || String(orderId || ''),
    client_id: 0,
    created_by: 0,
    service_type: routeItem?.service_type || (stageName === 'cleaning' ? 'cleaning' : 'washing'),
    client_type: routeItem?.client_type || 'b2c',
    source: null,
    status: stageName,
    payment_status: routeItem?.payment_status || 'pending',
    assigned_to: routeItem?.assigned_to ?? null,
    department_id: routeItem?.department_id ?? null,
    department_name: routeItem?.department_name || null,
    price_coefficient: 1,
    pickup_address_id: null,
    delivery_address_id: null,
    pickup_address_text: isDeliveryStage ? '' : addressText,
    delivery_address_text: isDeliveryStage ? addressText : '',
    self_delivery: false,
    return_delivery_type: 'delivery',
    pickup_lat: isDeliveryStage ? null : latitude,
    pickup_lng: isDeliveryStage ? null : longitude,
    delivery_lat: isDeliveryStage ? latitude : null,
    delivery_lng: isDeliveryStage ? longitude : null,
    pickup_date: isDeliveryStage ? null : scheduledDate,
    pickup_completed_date: null,
    pickup_time_slot: isDeliveryStage ? '' : (routeItem?.time_slot || ''),
    delivery_date: isDeliveryStage ? scheduledDate : null,
    delivery_completed_date: null,
    handover_completed_date: null,
    delivery_time_slot: isDeliveryStage ? (routeItem?.time_slot || '') : '',
    has_active_revision: Boolean(routeItem?.has_active_revision),
    lifecycle_status: routeItem?.lifecycle_status || 'active',
    lifecycle_label: routeItem?.lifecycle_label || 'Выполняется',
    subtotal: finalAmount,
    discount_amount: 0,
    final_amount: finalAmount,
    prepayment: Math.max(0, finalAmount - remainingAmount),
    remaining_amount: remainingAmount,
    payment_method: routeItem?.payment_method || null,
    invoice_number: null,
    invoice_delivery_method: null,
    invoice_email: null,
    invoice_prepared_at: null,
    invoice_prepared_by: null,
    invoice_prepared_by_name: null,
    act_number: null,
    act_prepared_at: null,
    act_prepared_by: null,
    act_prepared_by_name: null,
    invoice_sent_at: null,
    invoice_sent_by: null,
    invoice_sent_by_name: null,
    invoice_send_error: null,
    payment_received_at: null,
    payment_received_by: null,
    payment_received_by_name: null,
    priority: 'normal',
    dispatcher_priority_scope: null,
    dispatcher_priority_stage: null,
    dispatcher_priority_available_stages: [],
    active_rework: false,
    rework_item_ids: [],
    rework_comment: null,
    dispatcher_call_required_stage: routeItem?.dispatcher_call_required_stage || null,
    distance_km: null,
    floors_count: 0,
    notes: routeItem?.notes || '',
    internal_notes: routeItem?.internal_notes || '',
    created_date: createdDate,
    updated_date: routeItem?.updated_at || createdDate,
    completed_date: null,
    client_name: routeItem?.client_name || '',
    client_phone: routeItem?.client_phone || '',
    client_vip: false,
    creator_name: null,
    assignee_name: routeItem?.assignee_name || '',
    client_email_options: [],
    invoice_history: [],
    items: itemsSummary.map((line, index) => buildOfflineOrderItem(line, index, orderId)),
    stages: [{
      id: -1,
      order_id: orderId,
      stage_name: stageName,
      stage_status: 'in_progress',
      assigned_to: routeItem?.assigned_to ?? null,
      assignee_name: routeItem?.assignee_name || null,
      started_by: null,
      started_date: null,
      completed_by: null,
      completed_by_actor: null,
      completed_date: null,
      duration_minutes: null,
    }],
    revisions: [],
    active_revision: null,
    discounts: [],
  };
};

const buildOfflineOrderWorkflow = (routeItem) => ({
  current_user_id: Number(routeItem?.assigned_to) || null,
  is_privileged: false,
  is_current_assignee: false,
  is_creator: false,
  is_limited_archive_access: false,
  permissions: {
    can_add_comments: false,
    can_add_photos: false,
    can_add_items: false,
    can_edit_order_services: false,
    can_edit_discounts: false,
    can_edit_item_processing: false,
    can_select_repeat_items: false,
    can_complete_stage: false,
    can_self_assign_available_order: false,
  },
  requirements: {
    missing_requirements: [],
  },
  storage_cells: {
    required: false,
    options: [],
  },
  washing_flows: {
    needed: [],
    available_to_me: [],
    assigned_to_me: [],
    completed_by_me: [],
  },
});

const STAGES = {
  new: { label: 'Новый', icon: Package, color: 'gray' },
  pickup: { label: 'Доставка от клиента', icon: Truck, color: 'orange' },
  washing: { label: 'Стирка', icon: Droplets, color: 'cyan' },
  washing_laundry: { label: 'Прачечная', icon: WashingMachine, color: 'blue' },
  washing_cleaning: { label: 'Чистка', icon: BrushCleaning, color: 'emerald' },
  drying_packing: { label: 'Сушка и упаковка', icon: Package, color: 'purple' },
  approval: { label: 'Согласование', icon: CheckCircle, color: 'yellow' },
  delivery: { label: 'Доставка клиенту', icon: Home, color: 'green' },
  handover: { label: 'Выдача', icon: UserCheck, color: 'emerald' },
  cleaning: { label: 'Химчистка мебели', icon: Sofa, color: 'blue' },
  completed: { label: 'Завершён', icon: CheckCircle, color: 'green' },
  cancelled: { label: 'Отменён', icon: AlertTriangle, color: 'red' }
};

const StaircaseIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 18h4v-4h4v-4h4V6h4" />
    <path d="M18 6h2v2" />
  </svg>
);

const getDispatcherPriorityStageLabel = (stageName) => (
  STAGES[stageName]?.label || stageName || 'этап'
);

const getDispatcherPrioritySummary = (scope, stageName) => {
  if (scope === 'order') return 'весь заказ';
  if (scope === 'stage' && stageName) return `этап «${getDispatcherPriorityStageLabel(stageName)}»`;
  return 'без приоритета';
};

const getDispatcherCallRequiredSummary = (stageName) => {
  if (stageName === 'pickup') return 'созвон перед забором';
  if (stageName === 'delivery') return 'созвон перед доставкой клиенту';
  return 'созвон не требуется';
};

const getOrderDispatcherPriorityStageOptions = (order) => {
  const responseStages = Array.isArray(order?.dispatcher_priority_available_stages)
    ? order.dispatcher_priority_available_stages.filter(Boolean)
    : [];
  if (responseStages.length > 0) {
    return [...new Set(responseStages)];
  }

  const result = [];
  const pushStage = (stageName) => {
    if (stageName && !result.includes(stageName)) {
      result.push(stageName);
    }
  };

  if (order?.service_type === 'washing') {
    const isSelfDelivery = order?.active_revision
      ? order?.active_revision?.pickup_mode === 'self_delivery'
      : (order?.revision_pickup_mode ? order.revision_pickup_mode === 'self_delivery' : Boolean(order?.self_delivery));
    if (!isSelfDelivery) {
      pushStage('pickup');
    }

    const mainItems = Array.isArray(order?.items)
      ? order.items.filter((item) => !item?.is_additional)
      : [];
    const routes = new Set(
      mainItems
        .map((item) => String(item?.processing_route_snapshot || '').trim().toLowerCase())
        .filter(Boolean)
    );

    if (routes.size === 0 || routes.has('laundry')) {
      pushStage('washing_laundry');
    }
    if (routes.size === 0 || routes.has('cleaning')) {
      pushStage('washing_cleaning');
    }

    pushStage('drying_packing');
    pushStage('approval');
    pushStage(order?.return_delivery_type === 'self_pickup' ? 'handover' : 'delivery');
    return result;
  }

  if (order?.service_type === 'cleaning') {
    pushStage('cleaning');
    return result;
  }

  if (order?.service_type === 'extra_order') {
    const isSelfDelivery = order?.active_revision
      ? order?.active_revision?.pickup_mode === 'self_delivery'
      : (order?.revision_pickup_mode ? order.revision_pickup_mode === 'self_delivery' : Boolean(order?.self_delivery));
    if (order?.has_active_revision && !isSelfDelivery) {
      pushStage('pickup');
    }
    pushStage('handover');
  }

  const normalizedCurrentStage = String(order?.status || '').trim().toLowerCase();
  const currentStageIndex = normalizedCurrentStage === 'washing'
    ? result.findIndex((stage) => stage === 'washing_laundry' || stage === 'washing_cleaning')
    : result.findIndex((stage) => stage === normalizedCurrentStage);
  const stageRows = new Map(
    (Array.isArray(order?.stages) ? order.stages : [])
      .filter((stage) => stage?.stage_name)
      .map((stage) => [String(stage.stage_name).trim().toLowerCase(), stage])
  );

  return result.filter((stage, index) => {
    if (currentStageIndex >= 0 && index < currentStageIndex) {
      return false;
    }
    const stageStatus = String(stageRows.get(stage)?.stage_status || '').trim().toLowerCase();
    return stageStatus !== 'completed' && stageStatus !== 'skipped';
  });
};

const getCurrentDispatcherPriorityStage = (order, currentListStage, availableStages = []) => {
  const normalizedAvailableStages = Array.isArray(availableStages)
    ? [...new Set(availableStages.map((stage) => String(stage || '').trim().toLowerCase()).filter(Boolean))]
    : [];
  const normalizedListStage = String(currentListStage || '').trim().toLowerCase();
  if (normalizedAvailableStages.includes(normalizedListStage)) {
    return normalizedListStage;
  }

  const normalizedStatus = String(order?.status || '').trim().toLowerCase();
  if (normalizedStatus === 'washing') {
    return normalizedAvailableStages.find((stage) => stage === 'washing_laundry' || stage === 'washing_cleaning') || '';
  }

  if (normalizedAvailableStages.includes(normalizedStatus)) {
    return normalizedStatus;
  }

  return normalizedAvailableStages[0] || '';
};

const STAGE_ACCENT_STYLES = {
  amber: {
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-600',
    soft: 'bg-amber-50 dark:bg-amber-950/25',
    border: 'border-amber-200 dark:border-amber-900'
  },
  orange: {
    text: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-600',
    soft: 'bg-orange-50 dark:bg-orange-950/25',
    border: 'border-orange-200 dark:border-orange-900'
  },
  cyan: {
    text: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-600',
    soft: 'bg-cyan-50 dark:bg-cyan-950/25',
    border: 'border-cyan-200 dark:border-cyan-900'
  },
  rose: {
    text: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-600',
    soft: 'bg-rose-50 dark:bg-rose-950/25',
    border: 'border-rose-200 dark:border-rose-900'
  },
  teal: {
    text: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-600',
    soft: 'bg-teal-50 dark:bg-teal-950/25',
    border: 'border-teal-200 dark:border-teal-900'
  },
  lime: {
    text: 'text-lime-700 dark:text-lime-400',
    bg: 'bg-lime-600',
    soft: 'bg-lime-50 dark:bg-lime-950/25',
    border: 'border-lime-200 dark:border-lime-900'
  },
  violet: {
    text: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-600',
    soft: 'bg-violet-50 dark:bg-violet-950/25',
    border: 'border-violet-200 dark:border-violet-900'
  },
  indigo: {
    text: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-600',
    soft: 'bg-indigo-50 dark:bg-indigo-950/25',
    border: 'border-indigo-200 dark:border-indigo-900'
  },
  fuchsia: {
    text: 'text-fuchsia-600 dark:text-fuchsia-400',
    bg: 'bg-fuchsia-600',
    soft: 'bg-fuchsia-50 dark:bg-fuchsia-950/25',
    border: 'border-fuchsia-200 dark:border-fuchsia-900'
  },
  purple: {
    text: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-600',
    soft: 'bg-purple-50 dark:bg-purple-950/25',
    border: 'border-purple-200 dark:border-purple-900'
  },
  yellow: {
    text: 'text-yellow-700 dark:text-yellow-300',
    bg: 'bg-yellow-600',
    soft: 'bg-yellow-50 dark:bg-yellow-950/25',
    border: 'border-yellow-200 dark:border-yellow-900'
  },
  green: {
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-600',
    soft: 'bg-green-50 dark:bg-green-950/25',
    border: 'border-green-200 dark:border-green-900'
  },
  emerald: {
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-600',
    soft: 'bg-emerald-50 dark:bg-emerald-950/25',
    border: 'border-emerald-200 dark:border-emerald-900'
  },
  blue: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-600',
    soft: 'bg-blue-50 dark:bg-blue-950/25',
    border: 'border-blue-200 dark:border-blue-900'
  },
  red: {
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-600',
    soft: 'bg-red-50 dark:bg-red-950/25',
    border: 'border-red-200 dark:border-red-900'
  },
  gray: {
    text: 'text-gray-600 dark:text-gray-300',
    bg: 'bg-gray-700',
    soft: 'bg-gray-50 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-800'
  }
};

const money = (value) => `${Number(value || 0).toFixed(0)} ₽`;

const formatDateInputToApiIso = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = String(dateString).split('-').map(Number);
  if (![year, month, day].every(Number.isFinite)) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
};

const copyText = async (value) => {
  const text = String(value || '');
  if (!text) return false;

  try {
    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn('Clipboard API недоступен, пробуем fallback:', error);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    console.error('Не удалось скопировать через fallback:', error);
    return false;
  }
};

const getNowInSystemTimezone = () => {
  const timezoneOffset = getUserTimezone();
  const now = new Date();
  return new Date(now.getTime() + (timezoneOffset * 60 + now.getTimezoneOffset()) * 60000);
};

const formatDateInputValue = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const isDateTodayInSystemTimezone = (dateStr) => (
  !!dateStr && dateStr === formatDateInputValue(getNowInSystemTimezone())
);

const getCurrentMinutesInSystemTimezone = () => {
  const now = getNowInSystemTimezone();
  return now.getHours() * 60 + now.getMinutes();
};

const isFutureScheduleValue = (dateValue, timeSlot) => {
  const dateMatch = String(dateValue || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const timeMatch = String(timeSlot || '').match(/(\d{1,2}):(\d{2})/);
  if (!dateMatch || !timeMatch) return false;

  const scheduledKey = Number(dateMatch[1]) * 10000 + Number(dateMatch[2]) * 100 + Number(dateMatch[3]);
  const now = getNowInSystemTimezone();
  const todayKey = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  if (scheduledKey !== todayKey) return scheduledKey > todayKey;

  const startMinutes = Number(timeMatch[1]) * 60 + Number(timeMatch[2]);
  return startMinutes > (now.getHours() * 60 + now.getMinutes());
};

const ORDER_LIFECYCLE_STYLES = {
  unassigned: {
    label: 'Нераспределён',
    className: 'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300'
  },
  active: {
    label: 'Выполняется',
    className: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/25 dark:text-blue-300'
  },
  deferred: {
    label: 'Отложено',
    className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-300'
  },
  revision: {
    label: 'Повторно выполняется',
    className: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-300'
  },
  completed: {
    label: 'Выполнен',
    className: 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/25 dark:text-green-300'
  },
  cancelled: {
    label: 'Отменён',
    className: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/25 dark:text-red-300'
  }
};

const getOrderLifecycleStatus = (order) => {
  if (!order) return ORDER_LIFECYCLE_STYLES.active;
  const lifecycleStatus = String(order.lifecycle_status || '').trim();
  if (lifecycleStatus === 'completed') return ORDER_LIFECYCLE_STYLES.completed;
  if (lifecycleStatus === 'cancelled') return ORDER_LIFECYCLE_STYLES.cancelled;
  if (lifecycleStatus === 'revision_active') return ORDER_LIFECYCLE_STYLES.revision;
  if (lifecycleStatus === 'deferred') return ORDER_LIFECYCLE_STYLES.deferred;
  if (lifecycleStatus === 'unassigned') return ORDER_LIFECYCLE_STYLES.unassigned;
  return ORDER_LIFECYCLE_STYLES.active;
};

const buildMyOrdersBackRoute = (search = '') => {
  const params = new URLSearchParams(search || '');
  const stage = params.get('stage') || sessionStorage.getItem('myOrdersStage') || '';
  const tab = params.get('tab') || sessionStorage.getItem('myOrdersActiveTab') || 'my';
  if (!stage) return '/my-orders';
  return `/my-orders?${new URLSearchParams({ tab, stage }).toString()}`;
};

const parseIds = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseAdditionalServicesSnapshot = (snapshotValue, fallbackIdsValue) => {
  if (snapshotValue) {
    try {
      const parsed = JSON.parse(snapshotValue);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // no-op
    }
  }
  return parseIds(fallbackIdsValue).map((id) => ({ id }));
};

const getItemStageProcessingStatus = (item, stageName) => {
  const effectiveStageName = stageName === 'washing' && item?.processing_stage_name
    ? item.processing_stage_name
    : stageName;
  const stageStatus = (item.stage_statuses || []).find((entry) => entry.stage_name === effectiveStageName);
  if (stageStatus?.status) return stageStatus.status;
  if (
    effectiveStageName === 'washing'
    || effectiveStageName === 'cleaning'
    || effectiveStageName === 'washing_laundry'
    || effectiveStageName === 'washing_cleaning'
  ) {
    return item.processing_status || 'pending';
  }
  return 'pending';
};

const getProcessingRouteBadgeMeta = (item) => {
  if (item?.processing_route === 'laundry') {
    return {
      label: item.processing_route_label || 'Прачечная',
      icon: WashingMachine,
      className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    };
  }
  if (item?.processing_route === 'cleaning') {
    return {
      label: item.processing_route_label || 'Чистка',
      icon: BrushCleaning,
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    };
  }
  return null;
};

const toCartItem = (item) => ({
  ...item,
  name: item.item_name,
  isAdditional: item.is_additional,
  additionalType: item.additional_type,
  discountId: item.discount_id,
  discountSnapshot: item.discount_snapshot ? (() => { try { return JSON.parse(item.discount_snapshot); } catch { return null; } })() : null,
  additionalServices: parseAdditionalServicesSnapshot(item.additional_services_snapshot, item.additional_service_ids)
});

const toExistingCartItem = (item) => ({
  id: item.id,
  service_id: item.service_id,
  name: item.item_name,
  price: item.price,
  quantity: item.quantity,
  unit: item.unit,
  isAdditional: item.is_additional,
  additionalType: item.additional_type,
  discountId: item.discount_id
});

const getAdditionalLabel = (item) => {
  if (item.additional_type === 'discount' || item.discount_id) return 'Скидка';
  if (item.additional_type === 'order') return 'Доп. услуга заказа';
  return 'Доп. позиция';
};

const getAdditionalAmount = (item) => {
  const value = Number(item.subtotal ?? item.price ?? 0);
  if (item.additional_type === 'discount' || item.discount_id || value < 0) {
    return `-${money(Math.abs(value))}`;
  }
  return money(value);
};

const getAdditionalNominal = (item) => {
  if (item.notes) return item.notes;
  if (item.additional_type === 'discount' || item.discount_id) return getAdditionalAmount(item);
  if (item.additional_type === 'order') return `+${money(item.price)}`;
  return getAdditionalLabel(item);
};

const getPercentFromNominal = (item) => {
  const match = String(item.notes || '').match(/([+-]?\d+(?:[.,]\d+)?)\s*%/);
  return match ? Math.abs(Number(match[1].replace(',', '.'))) : null;
};

const getAdditionalCalculatedValue = (item, baseAmount) => {
  const percent = getPercentFromNominal(item);
  if (percent !== null) {
    const calculated = baseAmount * percent / 100;
    return (item.additional_type === 'discount' || item.discount_id) ? -calculated : calculated;
  }
  return Number(item.subtotal ?? item.price ?? 0);
};

const getAdditionalCalculatedAmount = (item, baseAmount) => {
  const value = getAdditionalCalculatedValue(item, baseAmount);
  if (item.additional_type === 'discount' || item.discount_id || value < 0) {
    return `-${money(Math.abs(value))}`;
  }
  return money(value);
};

const getItemDisplayNumber = (order, item, fallbackIndex) => (
  `${order?.order_number || order?.id || 'Заказ'}-${item.sort_order || fallbackIndex + 1}`
);

const getAdjustedMainItemUnitPrice = (order, item) => {
  const coefficient = Number(order?.price_coefficient || 1);
  const normalizedCoefficient = coefficient > 0 ? coefficient : 1;
  return Number(item?.price || 0) * normalizedCoefficient;
};

const getAdjustedMainItemRegularUnitPrice = (order, item) => {
  const coefficient = Number(order?.price_coefficient || 1);
  const normalizedCoefficient = coefficient > 0 ? coefficient : 1;
  return Number(item?.regular_price ?? item?.price ?? 0) * normalizedCoefficient;
};

const getAdjustedMainItemPromotionUnitPrice = (order, item) => {
  if (item?.promotion_price == null) return null;
  const coefficient = Number(order?.price_coefficient || 1);
  const normalizedCoefficient = coefficient > 0 ? coefficient : 1;
  return Number(item.promotion_price) * normalizedCoefficient;
};

const pluralizeService = (count) => {
  const abs = Math.abs(Number(count));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return 'услуга';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'услуги';
  return 'услуг';
};

const pluralizeOrder = (count) => {
  const abs = Math.abs(Number(count));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return 'заказ';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'заказа';
  return 'заказов';
};

const itemNeedsMeasurement = (item) => {
  const unit = String(item.unit || '').trim().toLowerCase();
  if (['кг', 'kg', 'weight'].includes(unit)) return !item.weight;
  if (['м²', 'м2', 'кв.м', 'sqm', 'area'].includes(unit)) return !item.area;
  return false;
};

const getMainItemSubtotalByUnitPrice = (item, unitPrice) => {
  const unit = String(item?.unit || '').trim().toLowerCase();
  if (['кг', 'kg', 'weight'].includes(unit)) {
    return Number(item?.weight || 0) > 0 ? unitPrice * Number(item.weight) : null;
  }
  if (['м²', 'м2', 'кв.м', 'sqm', 'area'].includes(unit)) {
    return Number(item?.area || 0) > 0 ? unitPrice * Number(item.area) : null;
  }
  return unitPrice * Number(item?.quantity || 1);
};

const getMainItemBaseSubtotal = (order, item) => (
  getMainItemSubtotalByUnitPrice(item, getAdjustedMainItemUnitPrice(order, item))
);

const getMainItemRegularSubtotal = (order, item) => (
  getMainItemSubtotalByUnitPrice(item, getAdjustedMainItemRegularUnitPrice(order, item))
);

const getMainItemPromotionSubtotal = (order, item) => {
  const promotionUnitPrice = getAdjustedMainItemPromotionUnitPrice(order, item);
  return promotionUnitPrice === null ? null : getMainItemSubtotalByUnitPrice(item, promotionUnitPrice);
};

const getItemAdditionalServicesBreakdown = (order, item) => {
  const services = parseAdditionalServicesSnapshot(item?.additional_services_snapshot, item?.additional_service_ids);
  const baseSubtotal = getMainItemBaseSubtotal(order, item);
  return services.map((service, index) => {
    const rawPrice = Number(service?.price || 0);
    const unitType = String(service?.unit_type || 'rub').toLowerCase();
    const amount = unitType === 'percent' && baseSubtotal !== null
      ? baseSubtotal * (rawPrice / 100)
      : rawPrice;
    return {
      id: service?.id || `service-${index}`,
      name: service?.name || 'Доп. услуга',
      nominal: unitType === 'percent' ? `+${rawPrice}%` : `+${money(rawPrice)}`,
      amount: baseSubtotal === null && unitType === 'percent' ? null : amount
    };
  });
};

const getItemAdditionalServicesTotal = (order, item) => (
  getItemAdditionalServicesBreakdown(order, item).reduce((sum, service) => (
    sum + Number(service.amount || 0)
  ), 0)
);

const getMainItemTotal = (order, item) => {
  const baseSubtotal = getMainItemBaseSubtotal(order, item);
  if (baseSubtotal === null) return null;
  return baseSubtotal + getItemAdditionalServicesTotal(order, item);
};

const getMainItemDisplayAmount = (order, item) => {
  if (itemNeedsMeasurement(item)) return '?';
  const total = getMainItemTotal(order, item);
  if (total === null) return '?';
  return money(total);
};

const getOrderPhotoUrl = (photo, variant = 'file') => {
  if (!photo?.id || !photo?.order_id) return '';
  return `${API_URL}/api/orders/${photo.order_id}/photos/${photo.id}/${variant === 'thumbnail' ? 'thumbnail' : 'file'}`;
};

const ACTIVITY_LABELS = {
  order_created: 'Заказ создан',
  order_deferred: 'Задание отложено',
  order_deferred_resolved: 'Отложенное задание обработано',
  order_stage_started: 'Этап начат',
  order_stage_assigned: 'Поток взят в работу',
  order_stage_completed: 'Этап завершен',
  order_assignee_changed: 'Исполнитель изменен',
  order_stage_assignee_changed: 'Исполнитель потока изменен',
  order_item_added: 'Предмет добавлен',
  order_item_removed: 'Предмет удален',
  order_item_updated: 'Предмет изменен',
  order_item_completed: 'Предмет выполнен',
  order_item_reopened: 'Предмет возвращён в работу',
  order_all_items_completed: 'Все предметы выполнены',
  order_internal_notes_changed: 'Доп. информация изменена',
  order_dispatcher_priority_changed: 'Диспетчерский приоритет изменён',
  order_dispatcher_call_required_stage_changed: 'Признак созвона изменен',
  order_discount_added: 'Скидка добавлена',
  order_discount_removed: 'Скидка удалена',
  order_service_added: 'Услуга добавлена',
  order_service_removed: 'Услуга удалена',
  order_return_updated: 'Условия возврата обновлены',
  order_revision_started: 'Повтор открыт',
  order_revision_completed: 'Повтор завершен',
  order_revision_cancelled: 'Повтор отменен',
  order_revision_pickup_updated: 'Повторный забор обновлен',
  order_revision_return_updated: 'Условия возврата повтора обновлены',
  order_revision_item_selected: 'Предмет отмечен на повтор',
  order_revision_item_unselected: 'Предмет снят с повтора',
  order_rework_requested: 'Возврат на доработку',
  order_rework_completed: 'Доработка выполнена',
  order_department_changed: 'Подразделение изменено',
  order_comment_added: 'Комментарий добавлен',
  order_comment_updated: 'Комментарий обновлен',
  order_comment_deleted: 'Комментарий удален',
  order_comment_read: 'Комментарий прочитан',
  order_photo_added: 'Фото добавлено',
  order_photo_removed: 'Фото удалено',
  order_invoice_prepared: 'Счёт подготовлен',
  order_invoice_resent: 'Счёт отправлен повторно',
  order_invoice_send_failed: 'Не удалось отправить счёт',
  order_act_prepared: 'Акт подготовлен',
  order_documents_edited_post_completion: 'Документы отредактированы после закрытия',
  order_paid: 'Заказ оплачен',
  order_cancelled: 'Заказ отменен',
  order_deleted: 'Заказ удалён',
  order_stage_skipped: 'Этап пропущен',
  unassign_order_due_user_access_change: 'Исполнитель снят из-за изменения доступа',
  unassign_order_due_user_delete: 'Исполнитель снят из-за удаления пользователя',
  client_intake_confirmed: 'Заявка из приложения подтверждена',
  client_intake_changes_requested: 'Запрошены изменения по заявке',
  client_intake_rejected: 'Заявка отклонена',
  client_chat_message_sent: 'Сообщение клиенту отправлено'
};

const getActivityLabel = (action) => ACTIVITY_LABELS[action] || 'Служебное изменение';

const parseActivityValue = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getActivityStageLabel = (stage, stageLabel) => {
  const explicitLabel = String(stageLabel || '').trim();
  if (explicitLabel) {
    return STAGES[explicitLabel]?.label || explicitLabel;
  }

  const stageName = String(stage || '').trim();
  if (!stageName) return '';
  if (STAGES[stageName]?.label) return STAGES[stageName].label;

  // Старые записи истории могут содержать уже готовую русскую подпись.
  // Неизвестные технические коды наружу не показываем.
  return /[А-Яа-яЁё]/.test(stageName) ? stageName : 'Этап заказа';
};

const getActivityDetails = (item) => {
  const newValue = parseActivityValue(item.new_value);
  const oldValue = parseActivityValue(item.old_value);
  const value = newValue || oldValue;
  if (!value) return '';
  if (item.action === 'order_created') {
    const snap = value.initial_snapshot;
    if (snap && Array.isArray(snap.items) && snap.items.length === 0) {
      return 'Создан без предметов (заявка на забор)';
    }
    if (snap && Array.isArray(snap.items) && snap.items.length > 0) {
      const itemCount = snap.items.length;
      const servCount = Array.isArray(snap.services) ? snap.services.length : 0;
      return `Состав при создании: ${itemCount} предм.${servCount > 0 ? `, ${servCount} усл.` : ''}`;
    }
    if (value.text && value.text !== 'Заказ создан') return value.text;
    return '';
  }
  if (value.text) return value.text;
  if (value.item_name) {
    const number = value.item_number && item.action?.startsWith('order_item_') ? `${value.item_number} ` : '';
    const qty = value.quantity ? `, ${value.quantity} ${value.unit || 'шт'}` : '';
    const nominal = value.nominal ? `, ${value.nominal}` : '';
    return `${number}${value.item_name}${qty}${nominal}`;
  }
  if (value.stage_label || value.stage) return getActivityStageLabel(value.stage, value.stage_label);
  return '';
};

const getActivitySnapshot = (item) => {
  const value = parseActivityValue(item.new_value);
  const snapshot = value?.initial_snapshot;
  if (!snapshot || typeof snapshot !== 'object') return null;
  const hasContent = ['items', 'services', 'discounts'].some((key) => Array.isArray(snapshot[key]) && snapshot[key].length > 0);
  return hasContent ? snapshot : null;
};

const getActivityRevisionNumber = (item) => {
  const newValue = parseActivityValue(item?.new_value);
  const oldValue = parseActivityValue(item?.old_value);
  const rawValue = newValue?.revision_number ?? oldValue?.revision_number ?? null;
  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getRevisionEndDate = (revision) => (
  revision?.cancelled_date || revision?.completed_date || null
);

const getRevisionStatusMeta = (revision) => {
  if (revision?.cancelled_date) {
    return {
      label: 'Отменён',
      className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200'
    };
  }
  if (revision?.completed_date) {
    return {
      label: 'Завершён',
      className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200'
    };
  }
  return {
    label: 'В работе',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100'
  };
};

const getActivityRepeatBadge = (item, revisions = []) => {
  if (!item) return null;

  const directRevisionNumber = getActivityRevisionNumber(item);
  if (directRevisionNumber) {
    return { label: 'Повтор', revisionNumber: directRevisionNumber };
  }

  const createdTimestamp = Date.parse(item.created_date || '');
  if (!Number.isFinite(createdTimestamp)) return null;

  const matchedRevision = (revisions || []).find((revision) => {
    const startedTimestamp = Date.parse(revision?.started_date || '');
    if (!Number.isFinite(startedTimestamp)) return false;
    const completedTimestamp = getRevisionEndDate(revision)
      ? Date.parse(getRevisionEndDate(revision)) + 60 * 1000
      : Number.POSITIVE_INFINITY;
    return createdTimestamp >= startedTimestamp && createdTimestamp <= completedTimestamp;
  });

  if (!matchedRevision) return null;

  return {
    label: 'Повтор',
    revisionNumber: Number.isFinite(Number(matchedRevision.revision_number))
      ? Number(matchedRevision.revision_number)
      : null
  };
};

const getSnapshotNumber = (line) => (
  line.item_number || (line.fallback_number ? `#${line.fallback_number}` : '')
);

const getSnapshotMeta = (line) => {
  const parts = [];
  if (line.quantity) parts.push(`${line.quantity} ${line.unit || 'шт'}`);
  if (line.area) parts.push(line.width && line.length ? `${line.area} м² (${line.width}×${line.length} м)` : `${line.area} м²`);
  if (line.weight) parts.push(`${line.weight} кг`);
  if (line.nominal) parts.push(line.nominal);
  return parts.join(' · ');
};

const SnapshotGroup = ({ title, items, tone, sign = '' }) => {
  if (!items?.length) return null;
  const styles = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/10 dark:text-blue-300',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/10 dark:text-purple-300',
    orange: 'bg-orange-50 text-orange-600 dark:bg-orange-900/10 dark:text-orange-300'
  };

  return (
    <div className="space-y-1">
      <div className="px-1 text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400">{title}</div>
      {items.map((line, index) => (
        <div key={`${title}-${line.id || index}`} className={`rounded-lg px-2.5 py-2 ${styles[tone]}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                {getSnapshotNumber(line) && <span className="shrink-0 font-mono text-[11px] opacity-80">{getSnapshotNumber(line)}</span>}
                <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{line.item_name || line.name || 'Позиция'}</span>
              </div>
              {getSnapshotMeta(line) && <div className="mt-0.5 text-[11px] opacity-80">{getSnapshotMeta(line)}</div>}
              {line.additional_services && (
                <div className="mt-0.5 text-[11px] text-blue-500 dark:text-blue-300">Доп. услуги: {line.additional_services}</div>
              )}
            </div>
            <span className="shrink-0 text-xs font-semibold">{sign}{money(Math.abs(Number(line.subtotal || 0)))}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const InitialOrderSnapshot = ({ snapshot }) => (
  <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
    <SnapshotGroup title="Предметы" items={snapshot.items} tone="blue" />
    <SnapshotGroup title="Услуги заказа" items={snapshot.services} tone="purple" sign="+" />
    <SnapshotGroup title="Скидки" items={snapshot.discounts} tone="orange" sign="-" />
    {snapshot.totals && (
      <div className="rounded-lg bg-gray-50 px-2.5 py-2 text-xs dark:bg-gray-800">
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">До скидок</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{money(snapshot.totals.subtotal)}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-gray-500">Скидки</span>
          <span className="font-medium text-orange-600 dark:text-orange-300">-{money(snapshot.totals.discount_amount)}</span>
        </div>
        <div className="mt-1 flex justify-between gap-3 border-t border-gray-200 pt-1 dark:border-gray-700">
          <span className="font-medium text-gray-700 dark:text-gray-200">Итого</span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{money(snapshot.totals.final_amount)}</span>
        </div>
      </div>
    )}
  </div>
);

const getOrderCoords = (order, status) => {
  if (!order) return null;
  const activeRevision = order.active_revision;
  if (
    status === 'delivery'
    && activeRevision?.return_lat !== null
    && activeRevision?.return_lat !== undefined
    && activeRevision?.return_lng !== null
    && activeRevision?.return_lng !== undefined
  ) {
    return { lat: activeRevision.return_lat, lng: activeRevision.return_lng };
  }
  if (
    status !== 'delivery'
    && activeRevision?.pickup_lat !== null
    && activeRevision?.pickup_lat !== undefined
    && activeRevision?.pickup_lng !== null
    && activeRevision?.pickup_lng !== undefined
  ) {
    return { lat: activeRevision.pickup_lat, lng: activeRevision.pickup_lng };
  }
  if (status === 'delivery' && order.delivery_lat && order.delivery_lng) {
    return { lat: order.delivery_lat, lng: order.delivery_lng };
  }
  if (status !== 'delivery' && order.pickup_lat && order.pickup_lng) {
    return { lat: order.pickup_lat, lng: order.pickup_lng };
  }
  if (order.pickup_lat && order.pickup_lng) {
    return { lat: order.pickup_lat, lng: order.pickup_lng };
  }
  if (order.delivery_lat && order.delivery_lng) {
    return { lat: order.delivery_lat, lng: order.delivery_lng };
  }
  return null;
};

const getPhoneHref = (phone) => {
  if (!phone) return null;
  if (String(phone).includes('*')) return null;
  const digits = String(phone).replace(/\D/g, '');
  const normalized = digits.length === 11 && digits[0] === '8' ? `7${digits.slice(1)}` : digits;
  if (normalized.length === 11 && normalized[0] === '7') {
    return `tel:+${normalized}`;
  }
  if (String(phone).trim().startsWith('+')) {
    return `tel:${String(phone).replace(/[^\d+]/g, '')}`;
  }
  return digits ? `tel:${digits}` : null;
};

const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  const normalized = digits.length === 11 && digits[0] === '8' ? `7${digits.slice(1)}` : digits;
  if (normalized.length === 11 && normalized[0] === '7') {
    return `+7(${normalized.slice(1, 4)})-${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
  }
  return phone;
};

const formatPaymentMethod = (method) => {
  const labels = {
    cash: 'Наличный',
    card: 'Безналичный',
    bank: 'Безналичный',
    transfer: 'Безналичный',
    cashless: 'Безналичный',
    non_cash: 'Безналичный',
    settlement_account: 'Расчётный счёт'
  };
  return labels[method] || method || 'Не выбран';
};

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Наличный' },
  { value: 'card', label: 'Безналичный' }
];

const SETTLEMENT_PAYMENT_METHOD = 'settlement_account';
const INVOICE_DELIVERY_METHOD_PAPER = 'paper';

const formatInvoiceDeliveryMethod = (method) => {
  if (method === INVOICE_DELIVERY_METHOD_PAPER) return 'Бумажный счёт';
  return 'Не выбран';
};

const sanitizeInvoiceFilePart = (value, fallback = 'Клиент', maxLength = 50) => {
  let normalized = String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_.,-]+|[_.,-]+$/g, '');
  if (maxLength && normalized.length > maxLength) {
    normalized = normalized.slice(0, maxLength).replace(/[_.,-]+$/g, '');
  }
  return normalized || fallback;
};

const resolveClientAttachmentName = (order) => {
  return (
    order?.short_name
    || order?.company_name
    || order?.client_name
    || 'Клиент'
  );
};

const buildInvoiceAttachmentFileName = (order, invoiceDraft) => {
  const orderNumber = sanitizeInvoiceFilePart(order?.order_number || order?.id || 'заказ', 'заказ', 20);
  const clientName = sanitizeInvoiceFilePart(resolveClientAttachmentName(order), 'Клиент', 50);
  const invoiceDate = sanitizeInvoiceFilePart(
    invoiceDraft?.invoice_date
      || order?.created_date?.slice?.(0, 10)
      || order?.updated_date?.slice?.(0, 10)
      || order?.completed_date?.slice?.(0, 10)
      || 'без_даты',
    'без_даты',
    20
  );
  return `Счет_${orderNumber}_${clientName}_${invoiceDate}.pdf`;
};

const buildActAttachmentFileName = (order, actDraft) => {
  const orderNumber = sanitizeInvoiceFilePart(order?.order_number || order?.id || 'заказ', 'заказ', 20);
  const clientName = sanitizeInvoiceFilePart(resolveClientAttachmentName(order), 'Клиент', 50);
  const actDate = sanitizeInvoiceFilePart(
    actDraft?.act_date
      || order?.completed_date?.slice?.(0, 10)
      || order?.updated_date?.slice?.(0, 10)
      || order?.created_date?.slice?.(0, 10)
      || 'без_даты',
    'без_даты',
    20
  );
  return `Акт_${orderNumber}_${clientName}_${actDate}.pdf`;
};

const buildDocumentsPackageFileName = (order, invoiceDraft, actDraft) => {
  const orderNumber = sanitizeInvoiceFilePart(order?.order_number || order?.id || 'заказ', 'заказ', 20);
  const clientName = sanitizeInvoiceFilePart(resolveClientAttachmentName(order), 'Клиент', 50);
  const packageDate = sanitizeInvoiceFilePart(
    actDraft?.act_date
      || invoiceDraft?.invoice_date
      || order?.completed_date?.slice?.(0, 10)
      || order?.updated_date?.slice?.(0, 10)
      || order?.created_date?.slice?.(0, 10)
      || 'без_даты',
    'без_даты',
    20
  );
  return `Документы_${orderNumber}_${clientName}_${packageDate}.pdf`;
};

const getClientTypeLabel = (type) => {
  if (['b2b', 'company'].includes(type)) return 'Юрлицо';
  if (['b2c', 'individual'].includes(type)) return 'Физ. лицо';
  return 'Клиент';
};

const getOrderAddress = (order) => {
  if (!order) return '';
  const activeRevision = order.active_revision;
  if (order.status === 'delivery') {
    return (
      activeRevision?.return_address_text
      || order.delivery_address_text
      || activeRevision?.pickup_address_text
      || order.pickup_address_text
      || ''
    );
  }
  return (
    activeRevision?.pickup_address_text
    || order.pickup_address_text
    || activeRevision?.return_address_text
    || order.delivery_address_text
    || ''
  );
};

const getClientAddressType = (order) => (
  ['b2b', 'company'].includes(order?.client_type) ? 'company' : 'individual'
);

const getReturnDeliveryType = (order) => {
  const repeatReturnDeliveryType = order?.active_revision?.return_delivery_type;
  if (order?.active_revision) {
    if (repeatReturnDeliveryType === 'self_pickup' || order?.status === 'handover') return 'self_pickup';
    if (repeatReturnDeliveryType === 'delivery' || order?.status === 'delivery') return 'delivery';
    return '';
  }
  if (order?.return_delivery_type === 'self_pickup' || order?.status === 'handover') return 'self_pickup';
  if (order?.return_delivery_type === 'delivery' || order?.status === 'delivery') return 'delivery';
  return '';
};

const isRepeatLifecycleOrder = (order) => String(order?.lifecycle_status || '').trim() === 'revision_active';
const getLatestRevision = (order) => {
  const revisions = Array.isArray(order?.revisions) ? order.revisions : [];
  if (!revisions.length) return null;
  return [...revisions].sort((left, right) => Number(right?.revision_number || 0) - Number(left?.revision_number || 0))[0] || null;
};
const getRepeatPickupDate = (order) => order?.active_revision?.pickup_date || null;
const getRepeatPickupTimeSlot = (order) => order?.active_revision?.pickup_time_slot || '';
const getRepeatReturnDate = (order) => order?.active_revision?.return_date || null;
const getRepeatReturnTimeSlot = (order) => order?.active_revision?.return_time_slot || '';
const getCurrentPickupAddressText = (order) => order?.active_revision?.pickup_address_text || order?.pickup_address_text || order?.delivery_address_text || '';
const getCurrentPickupAddressId = (order) => order?.active_revision?.pickup_address_id || order?.pickup_address_id || order?.delivery_address_id || null;
const getCurrentPickupLat = (order) => order?.active_revision?.pickup_lat ?? order?.pickup_lat ?? order?.delivery_lat ?? null;
const getCurrentPickupLng = (order) => order?.active_revision?.pickup_lng ?? order?.pickup_lng ?? order?.delivery_lng ?? null;
const getCurrentReturnDate = (order) => order?.active_revision ? (order.active_revision.return_date || null) : (order?.delivery_date || null);
const getCurrentReturnTimeSlot = (order) => order?.active_revision ? (order.active_revision.return_time_slot || '') : (order?.delivery_time_slot || '');
const getCurrentReturnAddressText = (order) => order?.active_revision ? (order.active_revision.return_address_text || '') : (order?.delivery_address_text || '');
const getCurrentReturnAddressId = (order) => order?.active_revision ? (order.active_revision.return_address_id || null) : (order?.delivery_address_id || null);
const getCurrentReturnLat = (order) => order?.active_revision ? (order.active_revision.return_lat ?? null) : (order?.delivery_lat ?? null);
const getCurrentReturnLng = (order) => order?.active_revision ? (order.active_revision.return_lng ?? null) : (order?.delivery_lng ?? null);

const getSuggestionAddressText = (suggestion) => suggestion?.value || suggestion?.unrestricted_value || '';

const parseSuggestionCoordinate = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getStageDateTime = (order) => {
  if (!order) return null;
  if (order.status === 'delivery') {
    return {
      label: 'Дата и время доставки',
      date: isRepeatLifecycleOrder(order) && getReturnDeliveryType(order) === 'delivery'
        ? getRepeatReturnDate(order)
        : order.delivery_date,
      time: isRepeatLifecycleOrder(order) && getReturnDeliveryType(order) === 'delivery'
        ? getRepeatReturnTimeSlot(order)
        : order.delivery_time_slot
    };
  }
  if (order.status === 'pickup' || order.service_type === 'cleaning') {
    return {
      label: order.status === 'pickup' ? 'Дата и время забора' : 'Дата и время',
      date: isRepeatLifecycleOrder(order)
        ? (getRepeatPickupDate(order) || order.pickup_date || order.delivery_date)
        : (order.pickup_date || order.delivery_date),
      time: isRepeatLifecycleOrder(order)
        ? (getRepeatPickupTimeSlot(order) || order.pickup_time_slot || order.delivery_time_slot)
        : (order.pickup_time_slot || order.delivery_time_slot)
    };
  }
  return null;
};

const getEffectivePickupDate = (order) => order?.pickup_completed_date || getRepeatPickupDate(order) || order?.pickup_date || null;
const getEffectiveDeliveryDate = (order) => (
  order?.delivery_completed_date
  || (isRepeatLifecycleOrder(order) ? getRepeatReturnDate(order) : order?.delivery_date)
  || null
);
const getEffectiveHandoverDate = (order) => (
  order?.handover_completed_date
  || (isRepeatLifecycleOrder(order) ? getRepeatReturnDate(order) : order?.delivery_date)
  || null
);

const getDateParts = (dateStr) => {
  if (!dateStr) return null;
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    iso: `${match[1]}-${match[2]}-${match[3]}`
  };
};

const getTimeSlotEndMinutes = (timeSlot) => {
  if (!timeSlot) return null;
  const matches = String(timeSlot).match(/\d{1,2}:\d{2}/g);
  if (!matches?.length) return null;
  const end = matches[matches.length - 1];
  const [hours, minutes] = end.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const isStageDateOverdue = (stageDateTime, orderStatus) => {
  if (!stageDateTime?.date || ['completed', 'cancelled'].includes(orderStatus)) return false;
  const parts = getDateParts(stageDateTime.date);
  if (!parts) return false;

  const now = new Date();
  const todayKey = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const stageKey = parts.year * 10000 + parts.month * 100 + parts.day;
  if (stageKey < todayKey) return true;
  if (stageKey > todayKey) return false;

  const endMinutes = getTimeSlotEndMinutes(stageDateTime.time);
  if (endMinutes === null) return false;
  return now.getHours() * 60 + now.getMinutes() > endMinutes;
};

const ORDER_MAP_DEFAULT_ZOOM = 15;
const ORDER_MAP_3D_TILT = (40 * Math.PI) / 180;
const UNASSIGN_ASSIGNEE_VALUE = '__none__';

const AccordionSection = ({ title, icon: Icon, count, unread, open, onToggle, children, contentClassName = 'p-4', className = '' }) => {
  const resolvedChildren = open
    ? (typeof children === 'function' ? children() : children)
    : null;

  return (
    <section className={`-mx-3 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:-mx-1 lg:mx-0 ${className}`}>
      <button
        type="button"
        onClick={() => {
          haptic(open ? 'close' : 'open');
          onToggle();
        }}
        className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left"
      >
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />}
          <span className="truncate">{title}</span>
          {count !== undefined && (
            <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {count}
            </span>
          )}
          {unread && (
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 ring-2 ring-red-100 dark:ring-red-950" />
          )}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className={`border-t border-gray-100 dark:border-gray-800 ${contentClassName}`}>{resolvedChildren}</div>}
    </section>
  );
};

const CommentBottomSheet = ({ open, initialText = '', onClose, onSubmit, loading }) => {
  const [text, setText] = useState(initialText);
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setText(initialText || '');
    }
  }, [open, initialText]);

  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };
  const submitWithHaptic = () => {
    haptic('selection');
    onSubmit(text);
  };
  return (
    <ModalShell open={open} onClose={closeWithHaptic} variant="sheet" zIndexClass="z-[70]" panelClassName="sm:max-w-lg">
          <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Новый комментарий</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Добавится в историю заказа</p>
            </div>
            <button type="button" onClick={closeWithHaptic} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="p-4">
            <FloatingField label="Комментарий">
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                rows={4}
                autoFocus
                placeholder="Напишите комментарий..."
                className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </FloatingField>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={closeWithHaptic}
                className="h-10 flex-1 rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={submitWithHaptic}
                disabled={loading || !text.trim()}
                className="h-10 flex-1 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white disabled:opacity-50"
              >
                Добавить
              </button>
            </div>
          </div>
    </ModalShell>
  );
};

const CancelOrderBottomSheet = ({
  open,
  initialReason = '',
  onClose,
  onSubmit,
  loading,
  title = 'Отменить заказ',
  description = 'Заказ будет переведён в статус отменённого. Укажите причину, она попадёт в историю действий.',
  reasonLabel = 'Причина отмены',
  placeholder = 'Например: клиент отказался от заказа',
  submitLabel = 'Отменить заказ'
}) => {
  const [reason, setReason] = useState(initialReason);
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setReason(initialReason || '');
    }
  }, [open, initialReason]);

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 shadow-sm ring-1 ring-red-100 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-900/40">
            <XCircle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>

        <FloatingField label={reasonLabel}>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            autoFocus
            className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-red-900/30"
            placeholder={placeholder}
          />
        </FloatingField>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => onSubmit(reason)}
            disabled={loading || !reason.trim()}
            className="min-h-[40px] flex-1 rounded-lg bg-red-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Выполняю...' : submitLabel}
          </button>
        </div>
    </ModalShell>
  );
};

const DEFERRED_REASON_OPTIONS = [
  { value: 'client_unavailable', label: 'Клиент недоступен' },
  { value: 'client_rescheduled', label: 'Клиент попросил перенести' },
  { value: 'access_problem', label: 'Нет доступа к адресу' },
  { value: 'technical_problem', label: 'Техническая проблема' },
  { value: 'other', label: 'Другая причина' },
];

const DeferOrderBottomSheet = ({ open, onClose, onSubmit, loading }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setReasonCode('');
      setComment('');
    }
  }, [open]);

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40">
          <Clock className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Отложить задание</h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
            Задание снимется с вас и перейдёт диспетчеру. Этап заказа не изменится.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Dropdown
            label="Причина"
            value={reasonCode}
            onChange={(value) => setReasonCode(String(value))}
            options={DEFERRED_REASON_OPTIONS}
            placeholder="Выберите причину"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-end gap-3 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
            <span className="font-normal normal-case">{comment.length}/300</span>
          </div>
          <FloatingField label="Комментарий">
            <textarea
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 300))}
              rows={4}
              maxLength={300}
              className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-amber-900/30"
              placeholder="Коротко опишите, что произошло"
            />
          </FloatingField>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button type="button" onClick={onClose} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 dark:bg-gray-800 dark:text-gray-100">Отмена</button>
        <button
          type="button"
          onClick={() => onSubmit({ reasonCode, comment })}
          disabled={loading || !reasonCode || comment.trim().length < 3}
          className="min-h-[40px] flex-1 rounded-lg bg-amber-500 px-3 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
        >
          {loading ? 'Передаю...' : 'Отложить'}
        </button>
      </div>
    </ModalShell>
  );
};

const ResolveDeferredBottomSheet = ({
  open,
  onClose,
  onSubmit,
  loading,
  assignees,
  assigneesLoading,
  routeSuggestions,
  routeSuggestionsLoading,
}) => {
  const [resolution, setResolution] = useState('available');
  const [scheduledDate, setScheduledDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setResolution('available');
      setScheduledDate('');
      setTimeSlot('');
      setAssignedTo('');
    }
  }, [open]);

  const assigneeOptions = (assignees || []).map((user) => ({
    value: String(user.id),
    label: user.name || user.login || `Пользователь #${user.id}`,
  }));

  return (
    <ModalShell open={open} onClose={onClose} variant="window" panelClassName="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
          <RefreshCw className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Вернуть в работу</h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Укажите новые дату и время, затем выберите, куда вернуть задание.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <DatePicker
              value={scheduledDate}
              onChange={setScheduledDate}
              allowPastDates={false}
              minDate={formatDateInputValue(getNowInSystemTimezone())}
              placeholder="Новая дата"
              inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
            />
          </div>
          <div className="space-y-2">
            <TimeSlotPicker
              value={timeSlot}
              onChange={setTimeSlot}
              inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
            />
          </div>
        </div>

        <RouteSuggestions
          suggestions={routeSuggestions}
          loading={routeSuggestionsLoading}
          onApply={(suggestion) => {
            setScheduledDate(suggestion.date_key || '');
            setTimeSlot(suggestion.time_slot || '');
          }}
          showWhenEmpty
        />

        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Куда вернуть</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: 'available', label: 'В доступные' },
              { value: 'assigned', label: 'Исполнителю' },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setResolution(option.value)}
                className={`rounded-xl border px-3 py-3 text-sm font-medium ${resolution === option.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
                  : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {resolution === 'assigned' && (
          <div className="space-y-2">
            <Dropdown
              label="Исполнитель"
              value={assignedTo}
              onChange={(value) => setAssignedTo(String(value))}
              options={assigneeOptions}
              disabled={assigneesLoading}
              placeholder={assigneesLoading ? 'Загружаю сотрудников...' : 'Выберите исполнителя'}
            />
          </div>
        )}

      </div>

      <div className="mt-4 flex gap-2">
        <button type="button" onClick={onClose} className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 dark:bg-gray-800 dark:text-gray-100">Отмена</button>
        <button
          type="button"
          onClick={() => onSubmit({ resolution, scheduledDate, timeSlot, assignedTo })}
          disabled={loading || !scheduledDate || !timeSlot || (resolution === 'assigned' && !assignedTo)}
          className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Возвращаю...' : 'Вернуть в работу'}
        </button>
      </div>
    </ModalShell>
  );
};

const RepeatOrderBottomSheet = ({
  open,
  initialReason = '',
  initialPickupMode = 'pickup',
  initialPickupDate = '',
  initialPickupTimeSlot = '',
  pickupAddressText = '',
  serviceType = '',
  loading,
  onClose,
  onSubmit
}) => {
  const isCleaningService = serviceType === 'cleaning';
  const allowSelfDelivery = !isCleaningService;
  const [reason, setReason] = useState(initialReason);
  const [pickupMode, setPickupMode] = useState(allowSelfDelivery ? initialPickupMode : 'pickup');
  const [pickupDate, setPickupDate] = useState(initialPickupDate);
  const [pickupTimeSlot, setPickupTimeSlot] = useState(initialPickupTimeSlot);
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setReason(initialReason || '');
      setPickupMode(allowSelfDelivery ? (initialPickupMode || 'pickup') : 'pickup');
      setPickupDate(initialPickupDate || '');
      setPickupTimeSlot(initialPickupTimeSlot || '');
    }
  }, [open, initialReason, initialPickupMode, initialPickupDate, initialPickupTimeSlot, allowSelfDelivery]);

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 shadow-sm ring-1 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40">
          <History className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Открыть повтор</h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
            {isCleaningService
              ? 'Заказ вернётся в работу без повторного начисления денег и сразу откроется на этапе Химчистка мебели.'
              : 'Заказ вернётся в работу без повторного начисления денег. Состав повтора сотрудник отметит на этапе приёмки.'}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {!isCleaningService && (
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Как клиент передаст вещи</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'pickup', label: 'Заберём у клиента' },
                { value: 'self_delivery', label: 'Самопривоз' }
              ].map((option) => {
                const selected = pickupMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setPickupMode(option.value);
                    }}
                    className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${
                      selected
                        ? 'border-amber-500 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-950/30 dark:text-amber-300'
                        : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {pickupMode === 'pickup' && (
          <>
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                {isCleaningService ? 'Адрес выезда' : 'Откуда заберём'}
              </div>
              <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm leading-5 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                {pickupAddressText || 'Адрес забора в заказе не найден'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <DatePicker
                  value={pickupDate}
                  onChange={setPickupDate}
                  allowPastDates={false}
                  minDate={formatDateInputValue(getNowInSystemTimezone())}
                  placeholder={isCleaningService ? 'Дата выезда' : 'Дата забора'}
                  inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                />
              </div>

              <div className="space-y-2">
                <TimeSlotPicker
                  value={pickupTimeSlot}
                  onChange={setPickupTimeSlot}
                  inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                />
              </div>
            </div>
          </>
        )}

        {pickupMode === 'self_delivery' && (
          <div className="rounded-xl bg-slate-50 px-3 py-3 text-sm leading-5 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            Клиент сам привезёт вещи. Дату повторного самопривоза сейчас не фиксируем в маршрутах.
          </div>
        )}

        <div className="space-y-2">
          <FloatingField label="Причина">
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={4}
              autoFocus
              className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-amber-900/30"
              placeholder="Например: клиент сообщил о пятне, нужна перестирка одного ковра"
            />
          </FloatingField>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => {
            haptic('heavy');
            onSubmit({ reason, pickupMode, pickupDate, pickupTimeSlot });
          }}
          disabled={loading || !reason.trim() || (pickupMode === 'pickup' && (!pickupDate || !pickupTimeSlot))}
          className="min-h-[40px] flex-1 rounded-lg bg-amber-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-50"
        >
          {loading ? 'Выполняю...' : 'Открыть повтор'}
        </button>
      </div>
    </ModalShell>
  );
};

const ReworkBottomSheet = ({ open, items = [], loading, onClose, onSubmit, source = 'drying' }) => {
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [comment, setComment] = useState('');
  const [showConsequences, setShowConsequences] = useState(false);
  const [showReworkConfirm, setShowReworkConfirm] = useState(false);
  const isDeferredDelivery = source === 'deferred_delivery';
  useModalBackButton(open, onClose);
  useModalBackButton(showConsequences, () => setShowConsequences(false));
  useModalBackButton(showReworkConfirm, () => setShowReworkConfirm(false));

  useEffect(() => {
    setShowConsequences(false);
    setShowReworkConfirm(false);
    if (open) {
      setSelectedItemIds([]);
      setComment('');
    }
  }, [open]);

  const selectedSet = useMemo(() => new Set(selectedItemIds), [selectedItemIds]);
  const allSelected = items.length > 0 && selectedItemIds.length === items.length;
  const toggleItem = (itemId) => {
    haptic('selection');
    setSelectedItemIds((current) => (
      current.includes(itemId)
        ? current.filter((value) => value !== itemId)
        : [...current, itemId]
    ));
  };

  return (
    <ModalShell open={open} onClose={onClose} variant="window" panelClassName="sm:max-w-lg">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 shadow-sm ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900/50">
          <RotateCcw className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isDeferredDelivery ? 'Вернуть доставку на доработку?' : 'Вернуть на доработку?'}
          </h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
            {isDeferredDelivery
              ? 'Выбранные предметы вернутся с отложенной доставки на стирку и заново пройдут последующие этапы.'
              : 'Выбранные предметы вернутся на стирку с повышенным приоритетом. После доработки их нужно будет повторно высушить и упаковать.'}
            </p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('open');
            setShowConsequences(true);
          }}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-amber-200 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-300 dark:hover:bg-amber-950/40"
          title="Что произойдёт после подтверждения"
          aria-label="Показать последствия возврата на доработку"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Предметы · выбрано {selectedItemIds.length}
            </div>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  setSelectedItemIds(allSelected ? [] : items.map((item) => item.id));
                }}
                className="text-xs font-semibold text-blue-600 dark:text-blue-300"
              >
                {allSelected ? 'Снять все' : 'Выбрать все'}
              </button>
            )}
          </div>
          <div className="max-h-60 space-y-1.5 overflow-y-auto rounded-xl border border-gray-200 p-2 dark:border-gray-700">
            {items.map((item, index) => {
              const selected = selectedSet.has(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleItem(item.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    selected
                      ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/25'
                      : 'border-transparent bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/70 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                    selected
                      ? 'border-amber-600 bg-amber-600 text-white'
                      : 'border-gray-300 bg-white text-transparent dark:border-gray-600 dark:bg-gray-900'
                  }`}>
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.item_name}</span>
                    <span className="mt-0.5 block truncate font-mono text-[11px] text-gray-500 dark:text-gray-400">
                      {item.item_number || `Предмет ${index + 1}`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <FloatingField label="Что нужно доработать">
            <textarea
              value={comment}
              onChange={(event) => setComment(event.target.value.slice(0, 1000))}
              rows={4}
              maxLength={1000}
              placeholder="Например: на ковре осталось пятно у края, требуется повторная стирка"
              className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-amber-900/30"
            />
          </FloatingField>
          <div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-gray-500 dark:text-gray-400">
            <span>Комментарий добавится в общие комментарии заказа</span>
            <span>{comment.length}/1000</span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="min-h-[42px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => {
            haptic('warning');
            setShowReworkConfirm(true);
          }}
          disabled={loading || selectedItemIds.length === 0 || comment.trim().length < 3}
          className="min-h-[42px] flex-[1.35] rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
        >
          {loading ? 'Возвращаю...' : 'Вернуть на стирку'}
        </button>
      </div>

      <ModalShell
        open={showConsequences}
        onClose={() => setShowConsequences(false)}
        variant="window"
        zIndexClass="z-[80]"
        panelClassName="sm:max-w-md"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900/50">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Что произойдёт?</h3>
            <ul className="mt-2 list-disc space-y-1.5 pl-4 text-sm leading-5 text-gray-600 dark:text-gray-300">
              {isDeferredDelivery && <li>Отложенная доставка закроется как обработанная, вернуть её обратно одной кнопкой нельзя.</li>}
              <li>У выбранных предметов сбросится текущее выполнение стирки и сушки; прежние исполнители сохранятся в истории.</li>
              <li>Исполнители на повторную стирку и последующие этапы автоматически не назначаются.</li>
              {isDeferredDelivery && <li>Старая дата доставки очистится. После сушки заказ вернётся на согласование, где нужно записать новую доставку.</li>}
            </ul>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('close');
            setShowConsequences(false);
          }}
          className="mt-5 min-h-[42px] w-full rounded-lg bg-amber-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
        >
          Понятно
        </button>
      </ModalShell>

      <ModalShell
        open={showReworkConfirm}
        onClose={() => setShowReworkConfirm(false)}
        variant="window"
        zIndexClass="z-[80]"
        panelClassName="sm:max-w-md"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:ring-amber-900/50">
            <RotateCcw className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Вернуть заказ на стирку?</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
              Заказ перейдёт на этап «Стирка». Выбранные предметы потребуется выполнить повторно.
            </p>
            {isDeferredDelivery && (
              <p className="mt-2 text-sm font-medium leading-5 text-amber-700 dark:text-amber-300">
                Отложенная доставка будет закрыта, а прежняя дата доставки — очищена.
              </p>
            )}
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={() => setShowReworkConfirm(false)}
            disabled={loading}
            className="min-h-[42px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Назад
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('heavy');
              onSubmit({ itemIds: selectedItemIds, comment: comment.trim() });
            }}
            disabled={loading}
            className="min-h-[42px] flex-[1.35] rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
          >
            {loading ? 'Возвращаю...' : 'Вернуть на стирку'}
          </button>
        </div>
      </ModalShell>
    </ModalShell>
  );
};

const DispatcherNotesModal = ({ open, currentText = '', loading, onClose, onSave }) => {
  const [draft, setDraft] = useState(currentText);
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (open) {
      setDraft(currentText || '');
    }
  }, [open, currentText]);

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:ring-indigo-900/40">
            <Info className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Доп. информация</h3>
            <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Добавьте или обновите дополнительную информацию заказа.</p>
          </div>
        </div>

        <div className="space-y-3">
          {!!(currentText && String(currentText).trim()) && (
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Текущая информация:</span>
              <div className="mt-1 rounded-lg bg-gray-50 p-2 text-sm whitespace-pre-wrap break-all text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                {currentText}
              </div>
            </div>
          )}

          <FloatingField label="Доп. информация">
            <textarea
              value={draft}
              onChange={(event) => {
                if (event.target.value.length <= 70) setDraft(event.target.value);
              }}
              rows={3}
              maxLength={70}
              autoFocus
              className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-indigo-900/30"
              placeholder="Введите дополнительную информацию..."
            />
          </FloatingField>

          <div className="text-right text-xs text-gray-500 dark:text-gray-400">
            {draft.length}/70 символов
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            disabled={loading || draft.trim() === (currentText || '').trim()}
            className="min-h-[40px] flex-1 rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
          >
            Сохранить
          </button>
        </div>
    </ModalShell>
  );
};

const DispatcherPriorityModal = ({
  open,
  currentScope = 'none',
  currentStage = '',
  targetStage = '',
  loading,
  onClose,
  onSave
}) => {
  const [scope, setScope] = useState(currentScope || 'none');
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;
    setScope(currentScope || 'none');
  }, [open, currentScope]);

  const initialScope = currentScope || 'none';
  const initialStage = currentStage || '';
  const normalizedTargetStage = String(targetStage || '').trim().toLowerCase();
  const CurrentStageIcon = STAGES[normalizedTargetStage]?.icon || Package;
  const isUnchanged = scope === initialScope && (scope !== 'stage' || normalizedTargetStage === initialStage);
  const saveDisabled = loading || (scope === 'stage' && !normalizedTargetStage) || isUnchanged;

  const optionClassName = (active, accentClasses) => (
    `rounded-xl border px-3 py-3 text-left transition-colors ${
      active
        ? accentClasses
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
    }`
  );

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 shadow-sm ring-1 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-900/40">
          <ArrowUp className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Приоритет очереди</h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
            Поднимет заказ выше обычных в очереди, но оставит ниже платной срочности.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid gap-2">
          <button
            type="button"
            onClick={() => setScope('none')}
            className={optionClassName(
              scope === 'none',
              'border-gray-300 bg-gray-100 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
            )}
          >
            <div className="text-sm font-semibold">Без приоритета</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Заказ останется в обычной очереди.</div>
          </button>

          <button
            type="button"
            onClick={() => setScope('order')}
            className={optionClassName(
              scope === 'order',
              'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100'
            )}
          >
            <div className="text-sm font-semibold">На весь заказ</div>
            <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">Заказ поднимется во всех очередях, где будет появляться.</div>
          </button>

          <button
            type="button"
            onClick={() => setScope('stage')}
            className={optionClassName(
              scope === 'stage',
              'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100'
            )}
          >
            <div className="text-sm font-semibold">На текущий этап</div>
            <div className="mt-1 text-xs text-blue-700 dark:text-blue-300">
              {normalizedTargetStage
                ? `Приоритет сработает только в очереди этапа «${getDispatcherPriorityStageLabel(normalizedTargetStage)}».`
                : 'Сейчас для этого заказа нельзя определить этап для приоритета.'}
            </div>
          </button>
        </div>

        {scope === 'stage' && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/80">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Этап
            </div>
            {normalizedTargetStage ? (
              <div className="flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-100 px-3 py-2 text-blue-900 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-100">
                <CurrentStageIcon className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium">{getDispatcherPriorityStageLabel(normalizedTargetStage)}</span>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Для этого заказа сейчас нет доступного текущего этапа для приоритета.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => onSave({ scope, stageName: normalizedTargetStage })}
          disabled={saveDisabled}
          className="min-h-[40px] flex-1 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          Сохранить
        </button>
      </div>
    </ModalShell>
  );
};

const DispatcherCallRequirementModal = ({
  open,
  currentStage = '',
  canSelectPickup = false,
  canSelectDelivery = false,
  loading,
  onClose,
  onSave
}) => {
  const normalizedCurrentStage = currentStage === 'pickup' || currentStage === 'delivery'
    ? currentStage
    : 'none';
  const [stage, setStage] = useState(normalizedCurrentStage);
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;
    setStage(normalizedCurrentStage);
  }, [open, normalizedCurrentStage]);

  const initialStage = normalizedCurrentStage;
  const saveDisabled = loading || stage === initialStage;
  const optionClassName = (active, accentClasses) => (
    `rounded-xl border px-3 py-3 text-left transition-colors ${
      active
        ? accentClasses
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
    }`
  );

  return (
    <ModalShell open={open} onClose={onClose} variant="window">
      <div className="mb-3 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 shadow-sm ring-1 ring-sky-100 dark:bg-sky-900/20 dark:text-sky-300 dark:ring-sky-900/40">
          <Phone className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Созвон с клиентом</h3>
          <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
            Исполнитель увидит значок трубки в "Моих заказах" на нужном выездном этапе.
          </p>
        </div>
      </div>

      <div className="grid gap-2">
        <button
          type="button"
          onClick={() => setStage('none')}
          className={optionClassName(
            stage === 'none',
            'border-gray-300 bg-gray-100 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
          )}
        >
          <div className="text-sm font-semibold">Не нужен</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Значок трубки не будет показываться.</div>
        </button>

        {canSelectPickup && (
          <button
            type="button"
            onClick={() => setStage('pickup')}
            className={optionClassName(
              stage === 'pickup',
              'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-100'
            )}
          >
            <div className="text-sm font-semibold">Перед забором</div>
            <div className="mt-1 text-xs text-sky-700 dark:text-sky-300">Покажется на этапе «Доставка от клиента».</div>
          </button>
        )}

        {canSelectDelivery && (
          <button
            type="button"
            onClick={() => setStage('delivery')}
            className={optionClassName(
              stage === 'delivery',
              'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-100'
            )}
          >
            <div className="text-sm font-semibold">Перед доставкой клиенту</div>
            <div className="mt-1 text-xs text-sky-700 dark:text-sky-300">Покажется на этапе «Доставка клиенту».</div>
          </button>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={() => onSave(stage === 'none' ? null : stage)}
          disabled={saveDisabled}
          className="min-h-[40px] flex-1 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
        >
          Сохранить
        </button>
      </div>
    </ModalShell>
  );
};

const ConfirmBottomSheet = ({
  open,
  title,
  text,
  confirmLabel,
  loading,
  onConfirm,
  onClose,
  icon: Icon,
  iconClassName = 'bg-blue-50 text-blue-600 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40',
  confirmClassName = 'bg-blue-600 text-white',
  confirmDisabled = false,
  zIndexClass = 'z-[70]',
  children
}) => {
  useModalBackButton(open, onClose);
  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };
  const confirmWithHaptic = () => {
    haptic('heavy');
    onConfirm();
  };
  return (
    <ModalShell open={open} onClose={closeWithHaptic} variant="sheet" zIndexClass={zIndexClass} panelClassName="p-4 sm:max-w-sm">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              {Icon && (
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ring-1 ${iconClassName}`}>
                  <Icon className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">{text}</p>
              </div>
            </div>
            <button type="button" onClick={closeWithHaptic} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X className="h-5 w-5" />
            </button>
          </div>
          {children && <div className="mb-4">{children}</div>}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={closeWithHaptic}
              disabled={loading}
              className="h-9 rounded-lg border-2 border-gray-300 px-3 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={confirmWithHaptic}
              disabled={loading || confirmDisabled}
              className={`h-9 rounded-lg px-3 text-sm font-semibold disabled:opacity-50 ${confirmClassName}`}
            >
              {loading ? 'Выполняю...' : confirmLabel}
            </button>
          </div>
    </ModalShell>
  );
};

const AdaptiveStorageCellSelector = ({
  cells = [],
  selectedCellId = '',
  onSelect,
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const hasLongNames = useMemo(
    () => cells.some((cell) => (cell.name || '').length > 14),
    [cells]
  );
  const isListView = cells.length > 8 || hasLongNames;

  const filteredCells = useMemo(() => {
    if (!isListView || !searchQuery.trim()) return cells;
    const query = searchQuery.trim().toLowerCase();
    return cells.filter((cell) => (cell.name || '').toLowerCase().includes(query));
  }, [cells, isListView, searchQuery]);

  if (!isListView) {
    return (
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-0.5">
        {cells.map((cell) => {
          const isSelected = selectedCellId === String(cell.id);
          return (
            <button
              key={cell.id}
              type="button"
              onClick={() => onSelect(String(cell.id))}
              disabled={disabled}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
                isSelected
                  ? 'bg-purple-600 text-white shadow-sm ring-2 ring-purple-300 dark:ring-purple-700'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 active:scale-95 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
              }`}
            >
              <Boxes className={`h-3.5 w-3.5 ${isSelected ? 'text-white' : 'text-purple-600 dark:text-purple-400'}`} />
              <span>{cell.name}</span>
              {isSelected && <Check className="h-3.5 w-3.5" />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {cells.length > 8 && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Поиск ячейки..."
            className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:ring-1 focus:ring-purple-500 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      <div className="max-h-44 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700/70 bg-white dark:bg-gray-900/60 p-1 space-y-1">
        {filteredCells.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-gray-400">
            Ячейки не найдены
          </div>
        ) : (
          filteredCells.map((cell) => {
            const isSelected = selectedCellId === String(cell.id);
            return (
              <button
                key={cell.id}
                type="button"
                onClick={() => onSelect(String(cell.id))}
                disabled={disabled}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                  isSelected
                    ? 'bg-purple-600 text-white font-semibold shadow-sm'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/70 font-medium'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Boxes className={`h-4 w-4 shrink-0 ${isSelected ? 'text-white' : 'text-purple-600 dark:text-purple-400'}`} />
                  <span className="truncate">{cell.name}</span>
                </div>
                {isSelected && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const StorageCellAssignmentSheet = ({
  open,
  mode,
  items = [],
  cells = [],
  storageRequired = false,
  loading,
  onClose,
  onSubmit,
}) => {
  const [assignments, setAssignments] = useState({});
  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return;
    const initialAssignments = {};
    items.forEach((item) => {
      const storage = getItemDryingStorage(item);
      if (storage?.id) initialAssignments[String(item.id)] = String(storage.id);
    });
    setAssignments(initialAssignments);
  }, [open, items]);

  const isSingleItem = (items || []).length <= 1;
  const isSingleCompletion = mode === 'complete-one';
  const isAssignmentOnly = mode === 'assign-one';
  const isAllAssignmentOnly = mode === 'assign-all';
  const cleaningItems = items.filter((item) => item?.processing_route === 'cleaning');
  const requiresSelection = isAssignmentOnly || isAllAssignmentOnly || storageRequired;
  const missingSelection = requiresSelection && cleaningItems.some((item) => !assignments[String(item.id)]);
  const title = isSingleCompletion
    ? 'Куда положили предмет?'
    : isAssignmentOnly
      ? 'Ячейка предмета'
      : isAllAssignmentOnly
        ? 'Указать ячейки'
      : 'Выполнить предметы';
  const confirmLabel = isAssignmentOnly
    ? 'Сохранить ячейку'
    : isAllAssignmentOnly
      ? 'Сохранить ячейки'
    : isSingleCompletion
      ? 'Положить и выполнить'
      : `Выполнить ${items.length}`;

  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };

  const submit = () => {
    if (missingSelection || loading) return;
    const normalizedAssignments = Object.fromEntries(
      Object.entries(assignments)
        .filter(([, cellId]) => cellId)
        .map(([itemId, cellId]) => [itemId, Number(cellId)])
    );
    haptic('heavy');
    onSubmit(normalizedAssignments);
  };

  return (
    <ModalShell
      open={open}
      onClose={closeWithHaptic}
      variant="sheet"
      zIndexClass="z-[75]"
      expandable={true}
      expandedHeight="95dvh"
      panelClassName={`flex ${isSingleItem ? 'max-h-[85dvh]' : 'h-[75dvh] max-h-[92dvh] sm:h-auto sm:max-h-[min(88dvh,760px)]'} flex-col sm:max-w-lg transition-all duration-200`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-800">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600 ring-1 ring-purple-100 dark:bg-purple-950/30 dark:text-purple-300 dark:ring-purple-900/60">
            <Boxes className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
        </div>
        <button
          type="button"
          onClick={closeWithHaptic}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-2">
        {requiresSelection && cleaningItems.length > 0 && cells.length === 0 && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/25 dark:text-red-200">
            Нет активных ячеек. Добавьте их в «Управление → Система».
          </div>
        )}

        <div className="space-y-2.5">
          {items.map((item, index) => {
            const isCleaning = item?.processing_route === 'cleaning';
            const processingRouteLabel = item?.processing_route_label
              || (isCleaning ? 'Чистка' : (item?.processing_route === 'laundry' ? 'Прачечная' : 'Обработка'));
            const currentStorage = getItemDryingStorage(item);
            const selectedCellId = assignments[String(item.id)] || '';
            const measurementDisplay = item?.area
              ? `${item.area} м²`
              : (item?.weight ? `${item.weight} кг` : null);
            return (
              <div key={item.id} className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-300">
                        {item.item_number || `#${index + 1}`}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        isCleaning
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                      }`}>
                        {processingRouteLabel}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{item.item_name}</div>
                    {measurementDisplay && (
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {measurementDisplay}
                      </div>
                    )}
                  </div>
                  {item?.processing_route === 'laundry' && (
                    <span className="shrink-0 rounded-full bg-gray-200 px-2 py-1 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      Без ячейки
                    </span>
                  )}
                </div>

                {isCleaning && requiresSelection && (
                  <div className="mt-2.5">
                    <div className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      Ячейка для размещения:
                    </div>
                    {cells.length === 0 ? (
                      <div className="text-xs text-red-500">Нет активных ячеек</div>
                    ) : (
                      <AdaptiveStorageCellSelector
                        cells={cells}
                        selectedCellId={selectedCellId}
                        onSelect={(cellId) => {
                          haptic('selection');
                          setAssignments((previous) => ({
                            ...previous,
                            [String(item.id)]: selectedCellId === cellId ? '' : cellId,
                          }));
                        }}
                        disabled={loading}
                      />
                    )}
                  </div>
                )}

                {isCleaning && !requiresSelection && currentStorage && (
                  <div className="mt-2 inline-flex rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
                    Ячейка {currentStorage.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 border-t border-gray-200 p-4 dark:border-gray-800">
        <button
          type="button"
          onClick={closeWithHaptic}
          disabled={loading}
          className="h-10 rounded-xl border-2 border-gray-300 px-3 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={loading || missingSelection || items.length === 0 || (requiresSelection && cleaningItems.length > 0 && cells.length === 0)}
          className="h-10 rounded-xl bg-purple-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-45"
        >
          {loading ? 'Сохраняю...' : confirmLabel}
        </button>
      </div>
    </ModalShell>
  );
};

const PhotoBottomSheet = ({ open, order, items, loading, uploadProgress = null, onClose, onSubmit }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [itemId, setItemId] = useState('');
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const itemOptions = useMemo(() => ([
    { value: '', label: 'К заказу' },
    ...items.map((item, index) => ({
      value: String(item.id),
      label: `${getItemDisplayNumber(order, item, index)} ${item.item_name}`
    }))
  ]), [items, order]);

  useModalBackButton(open, onClose);
  const closeWithHaptic = () => {
    if (loading || submitting || submittingRef.current) return;
    haptic('close');
    onClose();
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected) {
      haptic('selection');
      setFile(selected);
    }
    event.target.value = '';
  };

  const clearSelectedFile = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (loading || submitting || submittingRef.current) return;
    haptic('selection');
    setFile(null);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreviewUrl('');
      setItemId('');
      setCaption('');
      setSubmitting(false);
      submittingRef.current = false;
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  }, [open]);

  useEffect(() => {
    if (!loading) {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [file]);

  const submit = async () => {
    if (!file || loading || submitting || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    haptic('selection');
    try {
      await onSubmit({ file, itemId: itemId ? Number(itemId) : null, caption });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const isBusy = loading || submitting;

  return (
    <ModalShell
      open={open}
      onClose={closeWithHaptic}
      variant="sheet"
      zIndexClass="z-[70]"
      panelClassName="sm:max-w-lg"
    >
          <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-800">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Добавить фото</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Можно привязать к заказу или конкретному предмету</p>
            </div>
            <button type="button" onClick={closeWithHaptic} disabled={isBusy} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-800">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-3 p-4">
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={isBusy}
              onChange={handleFileChange}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              disabled={isBusy}
              onChange={handleFileChange}
            />

            {!file ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      cameraInputRef.current?.click();
                    }}
                    disabled={isBusy}
                    className="flex flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-blue-400/80 bg-blue-50/70 p-4 text-blue-700 transition-all hover:border-blue-500 hover:bg-blue-100/70 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-900/40"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/60 dark:text-blue-300">
                      <Camera className="h-5 w-5" />
                    </div>
                    <div className="text-center">
                      <span className="block text-sm font-semibold leading-tight">Снять на камеру</span>
                      <span className="mt-0.5 block text-[11px] text-blue-600/80 dark:text-blue-400/80">Открыть камеру</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      galleryInputRef.current?.click();
                    }}
                    disabled={isBusy}
                    className="flex flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/80 p-4 text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-100/80 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-800/60"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                    <div className="text-center">
                      <span className="block text-sm font-semibold leading-tight">Выбрать из галереи</span>
                      <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Файлы и медиа</span>
                    </div>
                  </button>
                </div>
                <p className="text-center text-xs text-gray-400 dark:text-gray-500">До 12 МБ (JPG, PNG, WEBP)</p>
              </div>
            ) : (
              <div className="relative flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-900/50">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-900 shadow-inner">
                    <img
                      src={previewUrl}
                      alt="Превью выбранного фото"
                      className={`h-full w-full object-cover transition-all ${isBusy ? 'filter blur-[1px] brightness-50 grayscale-[30%]' : ''}`}
                    />
                    {isBusy && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <CircularProgress
                          progress={uploadProgress}
                          size={38}
                          strokeWidth={3}
                          indicatorColor="text-white"
                          trackColor="text-white/25"
                          showPercent={true}
                        />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {file.name}
                    </span>
                    {isBusy ? (
                      <span className="mt-1 block text-xs font-semibold text-blue-600 dark:text-blue-400">
                        {typeof uploadProgress === 'number' && uploadProgress < 100
                          ? `Загрузка на сервер: ${uploadProgress}%`
                          : 'Сохранение в хранилище...'}
                      </span>
                    ) : (
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        {file.size > 1024 * 1024
                          ? `${(file.size / (1024 * 1024)).toFixed(1)} МБ`
                          : `${Math.round(file.size / 1024)} КБ`}
                      </span>
                    )}
                  </div>
                </div>

                {!isBusy && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        cameraInputRef.current?.click();
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-blue-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-blue-400"
                      title="Переснять на камеру"
                      aria-label="Переснять на камеру"
                    >
                      <Camera className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        galleryInputRef.current?.click();
                      }}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-blue-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-blue-400"
                      title="Выбрать другое из галереи"
                      aria-label="Выбрать другое из галереи"
                    >
                      <ImageIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={clearSelectedFile}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-red-400"
                      title="Убрать выбранное фото"
                      aria-label="Убрать выбранное фото"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div>
              <Dropdown
                label="Относится к"
                value={itemId}
                options={itemOptions}
                onChange={(value) => setItemId(value)}
                placeholder="К заказу"
              />
            </div>

            <FloatingField label="Подпись">
              <input
                value={caption}
                disabled={isBusy}
                onChange={(event) => setCaption(event.target.value)}
                placeholder="Например: пятно до чистки"
                className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </FloatingField>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={closeWithHaptic}
                disabled={isBusy}
                className="h-9 rounded-lg border-2 border-gray-300 px-3 text-sm font-medium text-gray-700 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={isBusy || !file}
                className="flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {isBusy ? (
                  <>
                    <CircularProgress size={16} strokeWidth={2.5} showPercent={false} indicatorColor="text-white" trackColor="text-white/30" />
                    <span>{typeof uploadProgress === 'number' && uploadProgress < 100 ? `Загрузка ${uploadProgress}%` : 'Сохраняю...'}</span>
                  </>
                ) : 'Добавить'}
              </button>
            </div>
          </div>
    </ModalShell>
  );
};

const getItemDryingStorage = (item) => {
  const stageStatus = (item?.stage_statuses || []).find((entry) => entry.stage_name === 'drying_packing');
  if (!stageStatus?.storage_cell_id) return null;
  return {
    id: Number(stageStatus.storage_cell_id),
    name: stageStatus.storage_cell_name || `Ячейка #${stageStatus.storage_cell_id}`,
    placedBy: stageStatus.storage_placed_by || null,
    placedDate: stageStatus.storage_placed_date || null,
  };
};

const PhotoViewer = ({ photo, onClose, onDelete, loading, canDelete = true }) => {
  const viewportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const gestureStateRef = useRef({
    mode: null,
    startX: 0,
    startY: 0,
    startDistance: 0,
    startScale: 1,
  });
  const [zoomState, setZoomState] = useState({ scale: 1, x: 0, y: 0 });
  useModalBackButton(!!photo, onClose);

  useEffect(() => {
    setZoomState({ scale: 1, x: 0, y: 0 });
    gestureStateRef.current = {
      mode: null,
      startX: 0,
      startY: 0,
      startDistance: 0,
      startScale: 1,
    };
  }, [photo?.id]);

  const getMaxOffset = (scale) => {
    const viewport = viewportRef.current;
    const width = viewport?.clientWidth || window.innerWidth || 0;
    const height = viewport?.clientHeight || window.innerHeight || 0;
    return {
      x: Math.max(0, ((scale - 1) * width) / 2),
      y: Math.max(0, ((scale - 1) * height) / 2),
    };
  };

  const clampZoomState = (nextScale, nextX, nextY) => {
    const scale = Math.min(4, Math.max(1, nextScale));
    if (scale <= 1) {
      return { scale: 1, x: 0, y: 0 };
    }
    const maxOffset = getMaxOffset(scale);
    return {
      scale,
      x: Math.min(maxOffset.x, Math.max(-maxOffset.x, nextX)),
      y: Math.min(maxOffset.y, Math.max(-maxOffset.y, nextY)),
    };
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    const [first, second] = touches;
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  };

  const handleTouchStart = (event) => {
    if (event.touches.length >= 2) {
      gestureStateRef.current = {
        ...gestureStateRef.current,
        mode: 'pinch',
        startDistance: getTouchDistance(event.touches),
        startScale: zoomState.scale,
      };
      return;
    }

    if (event.touches.length === 1 && zoomState.scale > 1) {
      const touch = event.touches[0];
      gestureStateRef.current = {
        ...gestureStateRef.current,
        mode: 'pan',
        startX: touch.clientX - zoomState.x,
        startY: touch.clientY - zoomState.y,
      };
    }
  };

  const handleTouchMove = (event) => {
    if (event.touches.length >= 2) {
      const distance = getTouchDistance(event.touches);
      const baseDistance = gestureStateRef.current.startDistance || distance;
      if (!baseDistance) return;
      event.preventDefault();
      const scale = gestureStateRef.current.startScale * (distance / baseDistance);
      setZoomState((prev) => clampZoomState(scale, prev.x, prev.y));
      return;
    }

    if (gestureStateRef.current.mode === 'pan' && event.touches.length === 1 && zoomState.scale > 1) {
      event.preventDefault();
      const touch = event.touches[0];
      const nextX = touch.clientX - gestureStateRef.current.startX;
      const nextY = touch.clientY - gestureStateRef.current.startY;
      setZoomState((prev) => clampZoomState(prev.scale, nextX, nextY));
    }
  };

  const handleTouchEnd = () => {
    gestureStateRef.current.mode = null;
    setZoomState((prev) => clampZoomState(prev.scale, prev.x, prev.y));
  };

  const handleDoubleClick = () => {
    haptic('selection');
    setZoomState((prev) => (
      prev.scale > 1
        ? { scale: 1, x: 0, y: 0 }
        : { scale: 2, x: 0, y: 0 }
    ));
  };

  const handleDownload = async () => {
    const fileUrl = getOrderPhotoUrl(photo, 'file');
    const extension = String(photo.file_path || '').split('.').pop() || 'jpg';
    const baseName = [
      photo.item_number ? `photo_${photo.item_number}` : 'order_photo',
      photo.id || 'image',
    ].filter(Boolean).join('_');
    setDownloading(true);
    try {
      const blob = await fetchProtectedBlob(fileUrl);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${baseName}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      haptic('success');
    } catch (error) {
      console.error('Ошибка скачивания фото:', error);
      haptic('error');
    } finally {
      setDownloading(false);
    }
  };

  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };
  const deleteWithHaptic = () => {
    haptic('warning');
    onDelete(photo);
  };
  return (
    <ModalShell
      open={Boolean(photo)}
      onClose={closeWithHaptic}
      variant="fullscreen"
      closeOnOverlay={false}
    >
      {photo ? (
        <>
        <div className="flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{photo.item_number ? `${photo.item_number} ${photo.item_name}` : 'Фото заказа'}</div>
            {photo.caption && <div className="truncate text-xs text-white/70">{photo.caption}</div>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white disabled:opacity-50"
              title="Скачать фото"
              aria-label="Скачать фото"
            >
              <Download className="h-4 w-4" />
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={deleteWithHaptic}
                disabled={loading}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button type="button" onClick={closeWithHaptic} className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div
          ref={viewportRef}
          className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2 select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onDoubleClick={handleDoubleClick}
          style={{ touchAction: zoomState.scale > 1 ? 'none' : 'pan-y' }}
        >
          <ProtectedImage
            src={getOrderPhotoUrl(photo, 'file')}
            thumbnailSrc={getOrderPhotoUrl(photo, 'thumbnail')}
            showLoader={true}
            loaderText="Загрузка оригинала..."
            alt={photo.caption || photo.item_name || 'Фото заказа'}
            className="max-h-full max-w-full object-contain transition-transform duration-150 ease-out"
            style={{
              transform: `translate3d(${zoomState.x}px, ${zoomState.y}px, 0) scale(${zoomState.scale})`,
              transformOrigin: 'center center',
            }}
            draggable={false}
          />
        </div>
        </>
      ) : null}
    </ModalShell>
  );
};

const OrderDetailSkeleton = () => (
  <div className="space-y-3">
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-6 w-40 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-28 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="h-8 w-20 shrink-0 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2 rounded-xl bg-gray-50 p-3 dark:bg-gray-800/70">
            <div className="h-3 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-40 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-28 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="space-y-2 rounded-xl bg-gray-50 p-3 dark:bg-gray-800/70">
            <div className="h-3 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-32 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-24 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="h-3 w-32 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-5/6 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-3/5 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </section>

    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="h-5 w-28 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="h-48 animate-pulse rounded-xl bg-gradient-to-br from-gray-100 via-gray-50 to-gray-200 dark:from-gray-800 dark:via-gray-800/80 dark:to-gray-700" />
      </div>
    </section>

    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="h-5 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="space-y-2">
          <div className="h-4 w-full animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="h-4 w-4/5 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          <div className="h-4 w-3/4 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </section>
  </div>
);

const DISPATCHER_ICON_BUTTON_BASE =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors disabled:opacity-50';
const DISPATCHER_ICON_BUTTON_INACTIVE =
  'border-gray-200 bg-gray-50 text-gray-300 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-600 dark:hover:bg-gray-800';

const OrderDetail = ({ orderId, onNavigate, accessSource = '', forceLimitedArchiveAccess = false }) => {
  const offline = useOffline();
  const connectionUnavailable = ['offline', 'server_unavailable'].includes(offline?.networkStatus);
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [offlineOrderSnapshotAt, setOfflineOrderSnapshotAt] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  const [invoicePrinting, setInvoicePrinting] = useState(false);
  const [actDownloading, setActDownloading] = useState(false);
  const [actPrinting, setActPrinting] = useState(false);
  const [documentsPackageDownloading, setDocumentsPackageDownloading] = useState(false);
  const [documentsPackagePrinting, setDocumentsPackagePrinting] = useState(false);
  const [invoiceDraftLoading, setInvoiceDraftLoading] = useState(false);
  const [invoiceDraftSaving, setInvoiceDraftSaving] = useState(false);
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);
  const [invoiceDraftData, setInvoiceDraftData] = useState(null);
  const [yandexApiKey, setYandexApiKey] = useState('');
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [showOrderServiceModal, setShowOrderServiceModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showProductionForecastModal, setShowProductionForecastModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [showEmergencyDispatchConfirm, setShowEmergencyDispatchConfirm] = useState(false);
  const [showCompleteAllConfirm, setShowCompleteAllConfirm] = useState(false);
  const [storageCellSheet, setStorageCellSheet] = useState(null);
  const [showTakeOrderConfirm, setShowTakeOrderConfirm] = useState(false);
  const [showRepeatOrderModal, setShowRepeatOrderModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeferModal, setShowDeferModal] = useState(false);
  const [showDeferredResolveModal, setShowDeferredResolveModal] = useState(false);
  const [deferredAssignees, setDeferredAssignees] = useState([]);
  const [deferredAssigneesLoading, setDeferredAssigneesLoading] = useState(false);
  const [deferredRouteSuggestions, setDeferredRouteSuggestions] = useState([]);
  const [deferredRouteSuggestionsLoading, setDeferredRouteSuggestionsLoading] = useState(false);
  const [showResetApprovalConfirm, setShowResetApprovalConfirm] = useState(false);
  const [showPaidConfirm, setShowPaidConfirm] = useState(false);
  const [approvedCostChangeConfirm, setApprovedCostChangeConfirm] = useState(null);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState('');
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [availableAssigneeFlows, setAvailableAssigneeFlows] = useState([]);
  const [assigneeStageDraft, setAssigneeStageDraft] = useState('');
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [comments, setComments] = useState([]);
  const [activityItems, setActivityItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [expandedItemBreakdownIds, setExpandedItemBreakdownIds] = useState({});
  const [cartBadgeHint, setCartBadgeHint] = useState(null);
  const [mapConfirmModal, setMapConfirmModal] = useState(null);
  const [phoneActionModal, setPhoneActionModal] = useState(null);
  const [dispatcherModeEnabled, setDispatcherModeEnabled] = useState(false);
  const [showDispatcherReturnModal, setShowDispatcherReturnModal] = useState(false);
  const [dispatcherPickupDraft, setDispatcherPickupDraft] = useState({
    mode: '',
    pickupDate: '',
    pickupTime: ''
  });
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [departmentDraft, setDepartmentDraft] = useState('');
  const [departmentRepriceDraft, setDepartmentRepriceDraft] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [showDispatcherNotesModal, setShowDispatcherNotesModal] = useState(false);
  const [showDispatcherPriorityModal, setShowDispatcherPriorityModal] = useState(false);
  const [showDispatcherCallRequirementModal, setShowDispatcherCallRequirementModal] = useState(false);
  const [showDispatcherAddressModal, setShowDispatcherAddressModal] = useState(false);
  const [dispatcherAddressDraft, setDispatcherAddressDraft] = useState({
    address: '',
    addressId: null,
    lat: null,
    lng: null,
    dadataLat: null,
    dadataLng: null,
    yandexLat: null,
    yandexLng: null,
    isCompleteAddress: false
  });
  const [dispatcherAddressSuggestions, setDispatcherAddressSuggestions] = useState([]);
  const [showDispatcherAddressSuggestions, setShowDispatcherAddressSuggestions] = useState(false);
  const [addressWithoutStreetSuggestion, setAddressWithoutStreetSuggestion] = useState(null);
  const [dispatcherAddressesOpen, setDispatcherAddressesOpen] = useState(false);
  const [isOrderMapFullscreen, setIsOrderMapFullscreen] = useState(false);
  const [orderMapDisclosure, setOrderMapDisclosure] = useState({ key: '', expanded: true });
  const [orderMapEverOpenedKey, setOrderMapEverOpenedKey] = useState('');
  const [isOrderMapTilted, setIsOrderMapTilted] = useState(false);
  const [isOrderMap3DSupported, setIsOrderMap3DSupported] = useState(true);
  const [isDarkTheme, setIsDarkTheme] = useState(() => document.documentElement.classList.contains('dark'));
  const [resolvedMapCoords, setResolvedMapCoords] = useState(null);
  const [isResolvingMapCoords, setIsResolvingMapCoords] = useState(false);
  const [isInitializingOrderMap, setIsInitializingOrderMap] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [paymentMethodDraft, setPaymentMethodDraft] = useState('');
  const [expandedActivityIds, setExpandedActivityIds] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    repeatInfo: false,
    dispatcher: false,
    payments: false,
    invoice: false,
    cart: false,
    approvalReturn: false,
    floors: false,
    comments: false,
    photos: false,
    history: false,
    attachments: false
  });
  const [expandedRevisionIds, setExpandedRevisionIds] = useState({});
  const [expandedRevisionStageLogIds, setExpandedRevisionStageLogIds] = useState({});
  const [floorsDraft, setFloorsDraft] = useState(0);
  const [approvalDraft, setApprovalDraft] = useState({
    returnType: '',
    deliveryDate: '',
    deliveryTime: '',
    deliveryAddress: '',
    deliveryAddressId: null,
    deliveryLat: null,
    deliveryLng: null,
    deliveryDadataLat: null,
    deliveryDadataLng: null,
    deliveryYandexLat: null,
    deliveryYandexLng: null,
    deliveryAddressComplete: false,
    callRequired: false,
    returnAssignedTo: ''
  });
  const [approvalAssignees, setApprovalAssignees] = useState([]);
  const [approvalAssigneesLoading, setApprovalAssigneesLoading] = useState(false);
  const [approvalEditMode, setApprovalEditMode] = useState(false);
  const [clientAddresses, setClientAddresses] = useState([]);
  const [approvalAddressEditorOpen, setApprovalAddressEditorOpen] = useState(false);
  const [approvalSavedAddressesOpen, setApprovalSavedAddressesOpen] = useState(false);
  const [approvalRoutesOpen, setApprovalRoutesOpen] = useState(false);
  const [approvalRouteItems, setApprovalRouteItems] = useState([]);
  const [approvalRoutesLoading, setApprovalRoutesLoading] = useState(false);
  const [approvalRouteSuggestions, setApprovalRouteSuggestions] = useState([]);
  const [approvalRouteSuggestionsLoading, setApprovalRouteSuggestionsLoading] = useState(false);
  const [showApprovalRouteSettings, setShowApprovalRouteSettings] = useState(false);
  const [approvalRouteDepartmentScope, setApprovalRouteDepartmentScope] = useState(() => localStorage.getItem('new_order_routes_department_scope') || 'all');
  const [approvalRouteDepartmentView, setApprovalRouteDepartmentView] = useState(() => localStorage.getItem('approval_routes_department_view') || 'grouped');
  const [approvalRouteShowDepartmentBadge, setApprovalRouteShowDepartmentBadge] = useState(() => localStorage.getItem('approval_routes_show_department_badge') !== 'false');
  const [approvalRouteShowExecutor, setApprovalRouteShowExecutor] = useState(() => localStorage.getItem('approval_routes_show_executor') !== 'false');
  const [approvalRouteHideRegion, setApprovalRouteHideRegion] = useState(() => localStorage.getItem('approval_routes_hide_region_in_address') === 'true');
  const [approvalRoutesPanelView, setApprovalRoutesPanelView] = useState('list');
  const [approvalRoutesMapFocusRequest, setApprovalRoutesMapFocusRequest] = useState(null);
  const [approvalAddressSuggestions, setApprovalAddressSuggestions] = useState([]);
  const [showApprovalAddressSuggestions, setShowApprovalAddressSuggestions] = useState(false);
  const approvalAddressInputRef = useRef(null);
  const approvalAddressDebounceRef = useRef(null);
  const approvalAddressRequestIdRef = useRef(0);
  const dispatcherCoordinateRequestRef = useRef(0);
  const approvalCoordinateRequestRef = useRef(0);
  const [mapProvider, setMapProvider] = useState(getStoredMapProvider);
  const mapWrapperRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapMarkerRef = useRef(null);
  const currentLocationCoordsRef = useRef(null);
  const currentLocationMarkerRef = useRef(null);
  const mapEngineRef = useRef('v3');
  const forceLegacyOrderMapRef = useRef(false);
  const mapLocationRef = useRef({
    center: null,
    zoom: ORDER_MAP_DEFAULT_ZOOM,
    azimuth: 0,
    tilt: 0
  });
  const mapGeocodeCacheRef = useRef(new Map());
  const persistedMapCoordsRef = useRef(new Set());
  const notificationBackPreparedRef = useRef(false);
  const orderBackPreparedRef = useRef(false);
  const cartBadgeHintTimeoutRef = useRef(null);
  const dispatcherModeReloadRef = useRef(null);
  const syncCurrentLocationMarker = useCallback((coordinates = currentLocationCoordsRef.current) => {
    currentLocationMarkerRef.current = replaceCurrentLocationMarker({
      map: mapInstanceRef.current,
      engine: mapEngineRef.current,
      previousMarker: currentLocationMarkerRef.current,
      coordinates,
    });
  }, []);
  useModalBackButton(!!mapConfirmModal, () => setMapConfirmModal(null));
  useModalBackButton(!!phoneActionModal, () => setPhoneActionModal(null));
  useModalBackButton(!!showAssigneeModal, () => setShowAssigneeModal(false));
  useModalBackButton(!!showDispatcherReturnModal, () => setShowDispatcherReturnModal(false));
  useModalBackButton(!!showDepartmentModal, () => setShowDepartmentModal(false));
  useModalBackButton(!!showDispatcherAddressModal, () => setShowDispatcherAddressModal(false));
  useModalBackButton(!!showApprovalRouteSettings, () => setShowApprovalRouteSettings(false));
  useModalBackButton(!!showProductionForecastModal, () => setShowProductionForecastModal(false));
  useModalBackButton(!!showEmergencyDispatchConfirm, () => setShowEmergencyDispatchConfirm(false));
  const dispatcherAddressInputRef = useRef(null);

  useEffect(() => () => {
    if (cartBadgeHintTimeoutRef.current) {
      window.clearTimeout(cartBadgeHintTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!cartBadgeHint) return undefined;
    const hideHint = () => setCartBadgeHint(null);
    window.addEventListener('resize', hideHint);
    window.addEventListener('scroll', hideHint, true);
    return () => {
      window.removeEventListener('resize', hideHint);
      window.removeEventListener('scroll', hideHint, true);
    };
  }, [cartBadgeHint]);

  const handleCartBadgeTap = (event, badgeKey, label) => {
    event.preventDefault();
    event.stopPropagation();
    haptic('tap');

    if (cartBadgeHintTimeoutRef.current) {
      window.clearTimeout(cartBadgeHintTimeoutRef.current);
      cartBadgeHintTimeoutRef.current = null;
    }
    if (cartBadgeHint?.key === badgeKey) {
      setCartBadgeHint(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 320;
    const maxBubbleWidth = Math.min(260, viewportWidth - 24);
    setCartBadgeHint({
      key: badgeKey,
      label,
      style: {
        left: `${rect.left + rect.width / 2}px`,
        top: `${rect.bottom + 8}px`,
        maxWidth: `${maxBubbleWidth}px`,
      },
    });
    cartBadgeHintTimeoutRef.current = window.setTimeout(() => {
      setCartBadgeHint((current) => (current?.key === badgeKey ? null : current));
      cartBadgeHintTimeoutRef.current = null;
    }, 2200);
  };

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDarkTheme(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    forceLegacyOrderMapRef.current = false;
    mapEngineRef.current = 'v3';
    setIsOrderMap3DSupported(true);
  }, [yandexApiKey]);

  const syncCurrentOrderMapViewport = () => {
    if (!mapInstanceRef.current) return;
    if (mapEngineRef.current === 'leaflet') {
      try {
        if (mapRef.current && !mapRef.current.classList.contains('leaflet-container')) {
          mapRef.current.classList.add(
            'leaflet-container',
            'leaflet-touch',
            'leaflet-retina',
            'leaflet-fade-anim',
            'leaflet-grab',
            'leaflet-touch-drag',
            'leaflet-touch-zoom'
          );
        }
        mapInstanceRef.current.invalidateSize?.({ animate: false });
      } catch (err) {
        console.warn('Ошибка syncCurrentOrderMapViewport (Leaflet):', err);
      }
      return;
    }

    if (!mapLocationRef.current.center) return;

    if (mapEngineRef.current === 'v3') {
      try {
        mapInstanceRef.current.update({
          location: {
            center: mapLocationRef.current.center,
            zoom: mapLocationRef.current.zoom
          },
          camera: {
            azimuth: mapLocationRef.current.azimuth,
            tilt: mapLocationRef.current.tilt
          }
        });
      } catch {}
      return;
    }

    try {
      mapInstanceRef.current.container?.fitToViewport?.();
      mapInstanceRef.current.setCenter?.(
        [mapLocationRef.current.center[1], mapLocationRef.current.center[0]],
        mapLocationRef.current.zoom || ORDER_MAP_DEFAULT_ZOOM,
        { duration: 0 }
      );
    } catch {}
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const nextFullscreen = document.fullscreenElement === mapWrapperRef.current;
      setIsOrderMapFullscreen(nextFullscreen);
      window.requestAnimationFrame(() => {
        syncCurrentOrderMapViewport();
      });
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const timers = [
      window.setTimeout(() => syncCurrentOrderMapViewport(), 20),
      window.setTimeout(() => syncCurrentOrderMapViewport(), 100),
      window.setTimeout(() => syncCurrentOrderMapViewport(), 250),
      window.setTimeout(() => syncCurrentOrderMapViewport(), 500)
    ];
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [isOrderMapFullscreen]);

  useEffect(() => {
    if (!mapRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (mapEngineRef.current === 'leaflet' && mapInstanceRef.current) {
        syncCurrentOrderMapViewport();
      }
    });
    ro.observe(mapRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => () => {
    if (document.fullscreenElement === mapWrapperRef.current) {
      document.exitFullscreen?.().catch?.(() => {});
    }
  }, []);

  const stageConfig = STAGES[order?.status] || STAGES.new;
  const StageIcon = stageConfig.icon;
  const accent = STAGE_ACCENT_STYLES[stageConfig.color] || STAGE_ACCENT_STYLES.gray;
  const lifecycleStatus = getOrderLifecycleStatus(order);
  const mainItems = useMemo(() => (order?.items || []).filter((item) => !item.is_additional), [order]);
  const mainItemsCount = useMemo(
    () => mainItems.reduce((sum, item) => sum + Math.max(1, Number(item.quantity || 1)), 0),
    [mainItems]
  );
  const isMixedProcessingFlowOrder = useMemo(() => {
    const routes = new Set(
      mainItems
        .map((item) => String(item?.processing_route || item?.processing_route_snapshot || '').trim().toLowerCase())
        .filter((route) => route === 'laundry' || route === 'cleaning')
    );
    return routes.has('laundry') && routes.has('cleaning');
  }, [mainItems]);
  const additionalItems = useMemo(() => (order?.items || []).filter((item) => item.is_additional), [order]);
  const orderServiceItems = useMemo(() => additionalItems.filter((item) => item.additional_type === 'order'), [additionalItems]);
  const hasFloorLiftService = useMemo(
    () => orderServiceItems.some((item) => item.service_system_code === 'floor_lift'),
    [orderServiceItems]
  );
  const discountOrderContext = useMemo(() => ({
    selfDelivery: !!order?.self_delivery,
    hasPickupDelivery: !order?.self_delivery && !!(
      getRepeatPickupDate(order)
      || getRepeatPickupTimeSlot(order)
      || order?.pickup_date
      || order?.pickup_time_slot
      || order?.pickup_address_id
      || order?.pickup_address_text
    ),
    hasReturnDelivery: !!(
      getRepeatReturnDate(order)
      || getRepeatReturnTimeSlot(order)
      || order?.delivery_date
      || order?.delivery_time_slot
      || order?.delivery_address_id
      || order?.delivery_address_text
    )
  }), [order]);
  const discountItems = useMemo(() => additionalItems.filter((item) => item.additional_type === 'discount' || item.discount_id), [additionalItems]);
  const existingMainItems = useMemo(() => mainItems.map(toCartItem), [mainItems]);
  const existingAdditionalItems = useMemo(() => additionalItems.map(toExistingCartItem), [additionalItems]);
  const paymentTotals = useMemo(() => {
    const base = mainItems.reduce((sum, item) => {
      const total = getMainItemTotal(order, item);
      return sum + (total === null ? Number(item.subtotal ?? item.price ?? 0) : total);
    }, 0);
    const services = orderServiceItems.reduce((sum, item) => sum + getAdditionalCalculatedValue(item, base), 0);
    const discounts = discountItems.reduce((sum, item) => sum + Math.abs(getAdditionalCalculatedValue(item, base)), 0);
    const final = base + services - discounts;
    return { base, services, discounts, final };
  }, [order, mainItems, orderServiceItems, discountItems]);
  const hasUnreadComments = comments.some((comment) => !comment.is_read);
  const openedFromMyAvailable = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return accessSource === 'my-orders' && params.get('tab') === 'available';
  }, [accessSource, location.search]);
  const openedFromOrdersLog = accessSource === 'orders-log';
  const currentListStage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('stage') || '';
  }, [location.search]);
  const { userType: currentUserType, loaded: currentUserTypeLoaded } = useAuthUserType();
  const isPrivilegedUser = !!workflow?.is_privileged;
  const hasEffectivePrivilegedAccess = isPrivilegedUser && dispatcherModeEnabled;
  const isLimitedArchiveAccess = !!workflow?.is_limited_archive_access || forceLimitedArchiveAccess;
  const hasJournalPrivilegedAccess = isPrivilegedUser && openedFromOrdersLog && !isLimitedArchiveAccess;
  const isAvailableReadOnlyMode = openedFromMyAvailable && !hasEffectivePrivilegedAccess && !workflow?.is_current_assignee;
  const currentWorkflowUserId = Number(workflow?.current_user_id) || null;
  const activeRevision = order?.active_revision || null;
  const latestRevision = getLatestRevision(order);
  const revisionsForDisplay = useMemo(() => (
    [...(Array.isArray(order?.revisions) ? order.revisions : [])]
      .sort((left, right) => Number(right?.revision_number || 0) - Number(left?.revision_number || 0))
  ), [order?.revisions]);
  const isRepeatOrder = !!(order?.has_active_revision || activeRevision);
  const activeRevisionNumber = Number(activeRevision?.revision_number || 0);
  const isCleaningRepeatOrder = isRepeatOrder && order?.service_type === 'cleaning';
  const repeatPickupSourceAddressText = String(order?.pickup_address_text || order?.delivery_address_text || '').trim();
  const neededWashingFlowStages = useMemo(() => (
    Array.isArray(workflow?.washing_flows?.needed) ? workflow.washing_flows.needed : []
  ), [workflow?.washing_flows?.needed]);
  const availableWashingFlowStages = workflow?.washing_flows?.available_to_me || [];
  const assignedWashingFlowStages = workflow?.washing_flows?.assigned_to_me || [];
  const completedWashingFlowStages = workflow?.washing_flows?.completed_by_me || [];
  const ownedWashingFlowStages = Array.from(new Set([
    ...assignedWashingFlowStages,
    ...completedWashingFlowStages,
  ]));
  const isWashingFlowOrder = order?.status === 'washing' && neededWashingFlowStages.length > 0;
  const normalizedListStage = String(currentListStage || '').trim().toLowerCase();
  const normalizedWashingFlowStage = ['washing_laundry', 'washing_cleaning'].includes(normalizedListStage)
    ? normalizedListStage
    : '';
  const orderMapContextStage = order?.status === 'washing'
    ? (
      normalizedWashingFlowStage
      || (
        neededWashingFlowStages.includes('washing_cleaning')
        && !neededWashingFlowStages.includes('washing_laundry')
          ? 'washing_cleaning'
          : 'washing'
      )
    )
    : String(order?.status || '').trim().toLowerCase();
  const orderMapDisclosureKey = `${order?.id || ''}:${order?.status || ''}:${orderMapContextStage}`;
  const shouldOpenOrderMapByDefault = !order?.is_deferred && ORDER_MAP_DEFAULT_OPEN_STAGE_IDS.has(orderMapContextStage);
  const isOrderMapExpanded = orderMapDisclosure.key === orderMapDisclosureKey
    ? orderMapDisclosure.expanded
    : shouldOpenOrderMapByDefault;
  const orderMapHasBeenOpened = orderMapEverOpenedKey === orderMapDisclosureKey;
  useEffect(() => {
    if (isOrderMapExpanded && !orderMapHasBeenOpened) {
      setOrderMapEverOpenedKey(orderMapDisclosureKey);
    }
  }, [isOrderMapExpanded, orderMapDisclosureKey, orderMapHasBeenOpened]);
  const washingFlowAssigneeSummaries = useMemo(() => (
    neededWashingFlowStages.map((stageName) => {
      const stageRow = (order?.stages || []).find((entry) => entry.stage_name === stageName);
      return {
        stage_name: stageName,
        label: STAGES[stageName]?.label || stageName,
        icon: STAGES[stageName]?.icon || UserCheck,
        assignee_name: stageRow?.assignee_name || null,
        assigned_to: stageRow?.assigned_to ?? null,
        stage_status: stageRow?.stage_status || 'pending'
      };
    })
  ), [neededWashingFlowStages, order?.stages]);
  const activeReworkFlowSummaries = useMemo(() => {
    if (order?.status !== 'drying_packing' || !order?.active_rework) {
      return [];
    }
    const reworkItemIds = new Set(
      (order?.items || [])
        .filter((it) => it.rework_selected)
        .map((it) => it.id)
    );
    const flowStages = new Set();
    (order?.items || []).forEach((it) => {
      if (reworkItemIds.has(it.id)) {
        const route = it.processing_route || 'laundry';
        if (route === 'cleaning') flowStages.add('washing_cleaning');
        else flowStages.add('washing_laundry');
      }
    });
    return Array.from(flowStages).map((stageName) => {
      const stageRow = (order?.stages || []).find((entry) => entry.stage_name === stageName);
      return {
        stage_name: stageName,
        label: STAGES[stageName]?.label || stageName,
        icon: STAGES[stageName]?.icon || UserCheck,
        assignee_name: stageRow?.assignee_name || null,
        assigned_to: stageRow?.assigned_to ?? null,
        stage_status: stageRow?.stage_status || 'pending',
      };
    });
  }, [order?.status, order?.active_rework, order?.items, order?.stages]);
  const hasWashingFlowAssigneeSummary = isWashingFlowOrder && washingFlowAssigneeSummaries.length > 0;
  const hasSingleWashingFlowAssigneeSummary = hasWashingFlowAssigneeSummary && washingFlowAssigneeSummaries.length === 1;
  const hasMultipleWashingFlowAssigneeSummary = hasWashingFlowAssigneeSummary && washingFlowAssigneeSummaries.length > 1;
  const hasAnyWashingFlowAssignee = washingFlowAssigneeSummaries.some((flow) => !!flow.assigned_to);
  const allWashingFlowsAssigned = washingFlowAssigneeSummaries.length > 0
    && washingFlowAssigneeSummaries.every((flow) => flow.stage_status === 'completed' || !!flow.assigned_to);
  const isFlowAssigneeSelection = availableAssigneeFlows.length > 0;
  const selectedAssigneeFlow = isFlowAssigneeSelection
    ? (availableAssigneeFlows.find((flow) => flow.stage_name === assigneeStageDraft) || availableAssigneeFlows[0] || null)
    : null;
  const selectedAssigneeUsers = useMemo(() => (
    isFlowAssigneeSelection
      ? (Array.isArray(selectedAssigneeFlow?.users) ? selectedAssigneeFlow.users : [])
      : availableAssignees
  ), [isFlowAssigneeSelection, selectedAssigneeFlow, availableAssignees]);
  const selectedAssigneeStageLabel = selectedAssigneeFlow?.label || 'поток';
  const selectedAssigneeStageIcon = selectedAssigneeFlow?.stage_name
    ? (STAGES[selectedAssigneeFlow.stage_name]?.icon || UserCheck)
    : UserCheck;
  const selectedAssigneeCurrentValue = isFlowAssigneeSelection
    ? String(selectedAssigneeFlow?.assigned_to || UNASSIGN_ASSIGNEE_VALUE)
    : String(order?.assigned_to || UNASSIGN_ASSIGNEE_VALUE);
  const canTakeAvailableWashingFlow = openedFromMyAvailable
    && (currentListStage === 'washing_laundry' || currentListStage === 'washing_cleaning')
    && availableWashingFlowStages.includes(currentListStage);
  const canTakeAvailableOrder = openedFromMyAvailable
    && !!workflow?.permissions?.can_self_assign_available_order
    && !!currentWorkflowUserId;
  const isDeferredOrder = !!order?.is_deferred;
  const canResolveDeferredOrder = isDeferredOrder && !!workflow?.permissions?.can_resolve_deferred && !isLimitedArchiveAccess;
  const canToggleDispatcherMode = isPrivilegedUser
    && (!isDeferredOrder || canResolveDeferredOrder)
    && !isLimitedArchiveAccess
    && !['completed', 'cancelled'].includes(order?.status);
  const canUseDispatcherCorrections = hasEffectivePrivilegedAccess && dispatcherModeEnabled && !isDeferredOrder && !isLimitedArchiveAccess;
  const canUseWorkActions = !!(workflow?.is_current_assignee || canUseDispatcherCorrections) && !isLimitedArchiveAccess && !isAvailableReadOnlyMode;
  const canDeferOrder = !!workflow?.permissions?.can_defer_order && canUseWorkActions && !isDeferredOrder;
  const isProductionStage = ['washing', 'cleaning', 'drying_packing'].includes(order?.status);
  const canShowProductionForecast = order?.service_type === 'washing'
    && !['completed', 'cancelled'].includes(order?.status);
  const isApprovalStage = order?.status === 'approval';
  const isHandoverStage = order?.status === 'handover';
  const canAddComments = !isAvailableReadOnlyMode && workflow?.permissions?.can_add_comments !== false;
  const canAddPhotos = !isAvailableReadOnlyMode && workflow?.permissions?.can_add_photos !== false;
  const canEditCart = !!workflow?.permissions?.can_add_items && canUseWorkActions;
  const isApprovedCostStage = ['approval', 'delivery', 'handover'].includes(order?.status);
  const canChangeOrderComposition = canEditCart
    && (!isProductionStage || canUseDispatcherCorrections);
  const canEditOrderServices = !!workflow?.permissions?.can_edit_order_services
    && canUseWorkActions
    && (!isProductionStage || canUseDispatcherCorrections);
  const canEditDiscounts = !!workflow?.permissions?.can_edit_discounts
    && canUseWorkActions
    && (!isProductionStage || canUseDispatcherCorrections);
  const canEditAppliedDiscount = (item) => {
    const snapshot = parseDiscountSnapshot(item?.discount_snapshot);
    if (!snapshot) return true;
    if (!currentUserTypeLoaded) return false;
    return canCurrentUserApplyDiscount(snapshot, currentUserType);
  };
  const canProcessItems = !!workflow?.permissions?.can_edit_item_processing && canUseWorkActions && !isApprovalStage;
  const storageCellsRequired = !!workflow?.storage_cells?.required;
  const cleaningStorageCells = useMemo(() => (
    Array.isArray(workflow?.storage_cells?.options) ? workflow.storage_cells.options : []
  ), [workflow?.storage_cells?.options]);
  const isDryer = order?.status === 'drying_packing' && Number(order?.assigned_to) === Number(currentWorkflowUserId);
  const canProcessSpecificItem = (item) => {
    if (hasEffectivePrivilegedAccess) return true;
    if (!isProductionStage) return true;
    if (order?.status === 'washing') {
      if (ownedWashingFlowStages.length === 0) return true;
      return ownedWashingFlowStages.includes(item?.processing_stage_name);
    }
    if (order?.status === 'drying_packing' && order?.active_rework) {
      const isReworkItem = !!item?.rework_selected;
      const washingStatus = getItemStageProcessingStatus(item, 'washing');
      const isWashingDone = washingStatus === 'done' || washingStatus === 'completed';

      const ownsThisItemFlow = ownedWashingFlowStages.includes(item?.processing_stage_name);
      if (ownsThisItemFlow && !isWashingDone && isReworkItem) {
        return true;
      }

      if (isDryer) {
        return !isReworkItem || isWashingDone;
      }
      return false;
    }
    return true;
  };
  const hasScopedWashingFlowAccess = order?.status === 'washing'
    && !hasEffectivePrivilegedAccess
    && ownedWashingFlowStages.length > 0;
  const canSelectRepeatItems = !!workflow?.permissions?.can_select_repeat_items && canUseWorkActions;
  const canRequestRework = !!workflow?.permissions?.can_request_rework && canUseWorkActions && order?.status === 'drying_packing' && !order?.active_rework;
  const canRequestDeferredRework = !!workflow?.permissions?.can_request_deferred_rework
    && canResolveDeferredOrder
    && order?.status === 'delivery';
  const canCompleteStage = !!workflow?.permissions?.can_complete_stage && canUseWorkActions;
  const canDeleteCartItems = canChangeOrderComposition;
  const canOpenItemEditor = canChangeOrderComposition || (canProcessItems && isProductionStage && !isRepeatOrder);
  const requiresApprovedCostChangeConfirm = isApprovedCostStage;
  const canCancelOrder = canToggleDispatcherMode;
  const canManageAssignee = canToggleDispatcherMode;
  const canStartRepeat = (hasEffectivePrivilegedAccess || hasJournalPrivilegedAccess) && !isLimitedArchiveAccess && order?.status === 'completed' && !isRepeatOrder;
  const isPickupStage = order?.status === 'pickup';
  const isDeliveryStage = order?.status === 'delivery';
  const isReturnSettingsStage = ['approval', 'delivery', 'handover'].includes(order?.status);
  const showReturnSettingsSection = isApprovalStage;
  const isReturnSettingsFinalStage = isDeliveryStage || isHandoverStage;
  const isFinalPaymentStage = ['delivery', 'handover', 'cleaning'].includes(order?.status);
  const canEditRepeatDispatcherPickup = canToggleDispatcherMode && isRepeatOrder && isPickupStage;
  const canEditRepeatDispatcherPickupAddress = canEditRepeatDispatcherPickup && (activeRevision?.pickup_mode || 'pickup') !== 'self_delivery';
  const dispatcherAddressTarget = (() => {
    if (!order || ['completed', 'cancelled'].includes(order.status)) return null;
    if (isRepeatOrder) {
      return isPickupStage && (activeRevision?.pickup_mode || 'pickup') !== 'self_delivery' ? 'pickup' : null;
    }
    if (isHandoverStage) return 'delivery';
    if (order.service_type === 'cleaning') return 'pickup';
    if (['new', 'pickup'].includes(order.status)) {
      return order.self_delivery ? null : 'pickup';
    }
    return getReturnDeliveryType(order) === 'delivery' ? 'delivery' : null;
  })();
  const canEditDispatcherScheduleSettings = canToggleDispatcherMode && (
    (!isRepeatOrder && (isPickupStage || isDeliveryStage || isHandoverStage))
    || canEditRepeatDispatcherPickup
    || (isRepeatOrder && (isDeliveryStage || isHandoverStage))
  );
  const canEditDispatcherDepartment = canToggleDispatcherMode && currentUserTypeLoaded && ['admin', 'developer'].includes(currentUserType);
  const canEditDispatcherAddress = canToggleDispatcherMode
    && !['completed', 'cancelled'].includes(order?.status)
    && !!dispatcherAddressTarget
    && (!isRepeatOrder || canEditRepeatDispatcherPickupAddress);
  const dispatcherCallRequiredStage = order?.dispatcher_call_required_stage || null;
  const dispatcherPriorityScope = order?.dispatcher_priority_scope || null;
  const dispatcherPriorityStage = order?.dispatcher_priority_stage || '';
  const dispatcherPriorityAvailableStages = useMemo(() => (
    getOrderDispatcherPriorityStageOptions(order)
  ), [order]);
  const currentDispatcherPriorityStage = useMemo(() => (
    getCurrentDispatcherPriorityStage(order, currentListStage, dispatcherPriorityAvailableStages)
  ), [order, currentListStage, dispatcherPriorityAvailableStages]);
  const dispatcherPrioritySummary = useMemo(() => (
    getDispatcherPrioritySummary(dispatcherPriorityScope, dispatcherPriorityStage)
  ), [dispatcherPriorityScope, dispatcherPriorityStage]);
  const canSelectPickupDispatcherCall = !isRepeatOrder
    ? (!order?.self_delivery && ['new', 'pickup'].includes(order?.status))
    : (order?.status === 'pickup' && (activeRevision?.pickup_mode || 'pickup') !== 'self_delivery');
  const returnDeliveryTypeForCall = getReturnDeliveryType(order);
  const canSelectDeliveryDispatcherCall = returnDeliveryTypeForCall === 'delivery' && !['handover', 'completed', 'cancelled'].includes(order?.status);
  const pickupDispatcherCallUnavailableReason = (() => {
    if (canSelectPickupDispatcherCall) return '';
    if (isRepeatOrder) {
      if (!isPickupStage) return 'Созвон перед забором можно поставить только на этапе забора повтора.';
      if ((activeRevision?.pickup_mode || 'pickup') === 'self_delivery') return 'У повтора выбрана самодоставка, созвон перед забором не нужен.';
      return 'Созвон перед забором сейчас недоступен.';
    }
    if (order?.self_delivery) return 'Забор оформлен как самодоставка, созвон перед забором не нужен.';
    if (!['new', 'pickup'].includes(order?.status)) return 'Забор уже прошёл, созвон перед забором поставить нельзя.';
    return 'Созвон перед забором сейчас недоступен.';
  })();
  const deliveryDispatcherCallUnavailableReason = (() => {
    if (canSelectDeliveryDispatcherCall) return '';
    if (['completed', 'cancelled'].includes(order?.status)) return 'Заказ уже завершён или отменён.';
    if (order?.status === 'handover' || returnDeliveryTypeForCall === 'self_pickup') {
      return 'Возврат выбран как самовывоз, поэтому созвон перед доставкой не нужен.';
    }
    if (returnDeliveryTypeForCall !== 'delivery') {
      return 'Доставка клиенту ещё не выбрана. Сначала укажите возврат доставкой на согласовании.';
    }
    return 'Созвон перед доставкой сейчас недоступен.';
  })();
  const dispatcherCallUnavailableReason = (() => {
    if (canSelectPickupDispatcherCall || canSelectDeliveryDispatcherCall || dispatcherCallRequiredStage) return '';
    if (!isRepeatOrder && ['new', 'pickup'].includes(order?.status) && order?.self_delivery && returnDeliveryTypeForCall !== 'delivery') {
      return 'Сейчас нет выезда для созвона: забор оформлен как самодоставка, а доставка клиенту ещё не выбрана.';
    }
    return deliveryDispatcherCallUnavailableReason || pickupDispatcherCallUnavailableReason || 'Созвон можно поставить только на выезд: забор или доставку клиенту.';
  })();
  const dispatcherCallButtonTitle = dispatcherCallRequiredStage
    ? `Созвон: ${getDispatcherCallRequiredSummary(dispatcherCallRequiredStage)}`
    : (
      canSelectPickupDispatcherCall || canSelectDeliveryDispatcherCall
        ? 'Отметить созвон с клиентом'
        : dispatcherCallUnavailableReason
    );
  const isUrgentOrder = order?.priority === 'urgent';
  const isEmergencyDispatch = Boolean(order?.emergency_dispatch);
  const canUseEmergencyDispatch = Boolean(order?.assigned_to) && !['completed', 'cancelled'].includes(order?.status)
    && (isPickupStage || isDeliveryStage || isHandoverStage);
  const emergencyDispatchUnavailableReason = !order?.assigned_to
    ? 'Сначала назначьте исполнителя'
    : (!isPickupStage && !isDeliveryStage && !isHandoverStage)
      ? 'Экстренный выезд доступен только на выездном этапе'
      : 'Экстренный выезд сейчас недоступен';
  const canOpenDispatcherScheduleSettings = canToggleDispatcherMode;
  const canOpenDispatcherDepartment = canToggleDispatcherMode && !isDeferredOrder;
  const canOpenDispatcherNotes = canToggleDispatcherMode;
  const canOpenDispatcherPriority = canToggleDispatcherMode;
  const canOpenDispatcherCallRequirement = canToggleDispatcherMode;
  const canOpenDispatcherAddress = canToggleDispatcherMode;
  const canOpenDispatcherAssignee = canToggleDispatcherMode;
  const canOpenDispatcherCancel = canToggleDispatcherMode && !isDeferredOrder;
  const dispatcherCancelButtonTitle = isRepeatOrder ? 'Отменить повтор' : 'Отменить заказ';
  const canUseDispatcherScheduleSettings = canEditDispatcherScheduleSettings && !isDeferredOrder;
  const dispatcherScheduleUnavailableReason = isDeferredOrder
    ? 'Изменить график отложенного заказа можно через «Вернуть в работу»'
    : isRepeatOrder && !isPickupStage && !isDeliveryStage && !isHandoverStage
      ? 'Дату повторного выезда можно изменить только на этапе забора, доставки или выдачи'
      : !isPickupStage && !isDeliveryStage && !isHandoverStage
        ? 'Дата и способ выезда редактируются только на этапах забора, доставки и выдачи'
        : 'Сейчас нельзя изменить дату и способ выезда';
  const canUseDispatcherAssignee = canManageAssignee && !isDeferredOrder;
  const dispatcherAssigneeUnavailableReason = isDeferredOrder
    ? 'Назначить исполнителя отложенного заказа можно через «Вернуть в работу»'
    : 'Сейчас нельзя изменить исполнителя';
  const canUseDispatcherDepartment = canEditDispatcherDepartment;
  const dispatcherDepartmentUnavailableReason = !currentUserTypeLoaded
    ? 'Права пользователя ещё загружаются'
    : !['admin', 'developer'].includes(currentUserType)
      ? 'Сменить подразделение может только администратор или разработчик'
      : 'Сейчас нельзя сменить подразделение';
  const canUseDispatcherAddress = canEditDispatcherAddress;
  const dispatcherAddressUnavailableReason = !dispatcherAddressTarget
      ? (
        ['new', 'pickup'].includes(order?.status) && order?.self_delivery
          ? 'Для самодоставки адрес забора не используется'
          : 'Адрес возврата недоступен: выбран самовывоз или условия возврата ещё не настроены'
      )
      : 'Сейчас нельзя изменить адрес';
  const canUseDispatcherCancel = canCancelOrder;
  const dispatcherCancelUnavailableReason = 'Сейчас нельзя отменить заказ';
  const dispatcherActionClass = (available, activeClassName) => `${DISPATCHER_ICON_BUTTON_BASE} ${
    available ? activeClassName : `${DISPATCHER_ICON_BUTTON_INACTIVE} cursor-not-allowed`
  }`;
  const cancelModalConfig = isRepeatOrder && !isDeferredOrder
    ? {
        title: 'Отменить повтор',
        description: 'Повтор будет закрыт, а заказ вернётся в завершённые. Укажите причину, она попадёт в историю действий.',
        reasonLabel: 'Причина отмены повтора',
        placeholder: 'Например: клиент передумал повторно отдавать вещи',
        submitLabel: 'Отменить повтор'
      }
    : {
        title: 'Отменить заказ',
        description: 'Заказ будет переведён в статус отменённого. Укажите причину, она попадёт в историю действий.',
        reasonLabel: 'Причина отмены',
        placeholder: 'Например: клиент отказался от заказа',
        submitLabel: 'Отменить заказ'
      };
  const canShowDispatcherAccordion = canToggleDispatcherMode || (hasJournalPrivilegedAccess && !isDeferredOrder);
  const isCompanyClient = ['b2b', 'company'].includes(order?.client_type);
  const dispatcherAssigneeButtonTitle = isWashingFlowOrder
    ? (hasAnyWashingFlowAssignee ? 'Изменить исполнителей потоков' : 'Назначить исполнителей потоков')
    : (order?.assigned_to ? 'Сменить исполнителя' : 'Назначить исполнителя');
  const floorLiftRequirementMissing = hasFloorLiftService && isDeliveryStage && Number(order?.floors_count || 0) <= 0;
  const missingRequirements = workflow?.requirements?.missing_requirements || [];
  const normalizedPaymentMethod = String(order?.payment_method || '').trim().toLowerCase();
  const invoiceDocumentRequirementMissing = isCompanyClient && missingRequirements.some((item) => {
    const normalized = String(item || '').toLowerCase();
    return normalized.includes('счёт');
  });
  const actDocumentRequirementMissing = isCompanyClient && missingRequirements.some((item) => {
    const normalized = String(item || '').toLowerCase();
    return normalized.includes('акт');
  });
  const legalDocumentsRequirementMissing = invoiceDocumentRequirementMissing || actDocumentRequirementMissing;
  const approvalRequirementMissing = isApprovalStage && missingRequirements.some((item) => (
    item.includes('способ возврата')
      || item.includes('возврата клиенту')
      || item.includes('адрес возврата')
      || item.includes('повторного возврата')
      || item.includes('новую дату повторного')
  ));
  const invoicePreparedRequirementMissing = isFinalPaymentStage && legalDocumentsRequirementMissing;
  const paymentRequirementMissing = isFinalPaymentStage && (
    isCompanyClient
      ? ((!!normalizedPaymentMethod && normalizedPaymentMethod !== SETTLEMENT_PAYMENT_METHOD) || invoicePreparedRequirementMissing)
      : (!normalizedPaymentMethod || String(order?.payment_status || '').trim().toLowerCase() !== 'paid')
  );
  const cartRequirementMissing = missingRequirements.some((item) => {
    const normalized = String(item || '').toLowerCase();
    return (
      normalized.includes('предмет')
      || normalized.includes('площад')
      || normalized.includes('вес')
    );
  });
  const mapCoords = getOrderCoords(order, order?.status);
  const repeatSelectionLocked = !!activeRevision?.selected_date;
  const repeatProcessingItems = isRepeatOrder && repeatSelectionLocked
    ? mainItems.filter((item) => item?.revision_selected)
    : mainItems;
  const scopedProcessingItems = hasScopedWashingFlowAccess
    ? repeatProcessingItems.filter((item) => canProcessSpecificItem(item))
    : repeatProcessingItems;
  const scopedItemsMissingRequiredStorage = storageCellsRequired && order?.status === 'drying_packing'
    ? scopedProcessingItems.filter((item) => (
        item?.processing_route === 'cleaning'
        && !item?.revision_locked_ready
        && !getItemDryingStorage(item)
      ))
    : [];
  const completeAllSheetItems = scopedProcessingItems.filter((item) => (
    getItemStageProcessingStatus(item, order?.status) !== 'done'
    || scopedItemsMissingRequiredStorage.some((missingItem) => missingItem.id === item.id)
  ));
  const areAllMainItemsDone = scopedProcessingItems.length > 0
    && scopedProcessingItems.every((item) => getItemStageProcessingStatus(item, order?.status) === 'done');
  const effectiveMapCoords = mapCoords || resolvedMapCoords;
  const hasCoords = !!effectiveMapCoords;
  const serviceTypeForModal = order?.service_type === 'washing' ? 'laundry' : order?.service_type;
  const phoneHref = getPhoneHref(order?.client_phone);
  const formattedPhone = formatPhone(order?.client_phone);
  const orderAddress = getOrderAddress(order);
  const dispatcherAddressField = dispatcherAddressTarget === 'delivery' ? 'delivery_address' : 'pickup_address';
  const dispatcherAddressCurrentText = dispatcherAddressField === 'delivery_address'
    ? getCurrentReturnAddressText(order)
    : getCurrentPickupAddressText(order);
  const dispatcherAddressLabel = isRepeatOrder && isPickupStage
    ? 'Адрес повторного забора'
    : dispatcherAddressTarget === 'delivery'
      ? 'Адрес возврата клиенту'
      : ['new', 'pickup'].includes(order?.status)
        ? 'Адрес забора'
        : 'Адрес выезда';
  const stageDateTime = getStageDateTime(order);
  const orderMapsUrl = getYandexMapsUrl(orderAddress) || getYandexAddressUrl(orderAddress) || '#';
  const orderNavigatorUrl = getYandexNavigatorUrl(orderAddress) || getYandexAddressUrl(orderAddress) || '#';
  const orderDgisUrl = getDgisRouteUrl({
    latitude: effectiveMapCoords?.lat ?? order?.latitude,
    longitude: effectiveMapCoords?.lng ?? order?.longitude,
    address: orderAddress
  }) || getDgisAddressUrl(orderAddress) || '#';
  const stageDateOverdue = isStageDateOverdue(stageDateTime, order?.status);
  const stageDateClass = stageDateOverdue
    ? 'text-red-600 dark:text-red-400'
    : 'text-gray-700 dark:text-gray-200';
  const stageDateIconClass = stageDateOverdue
    ? 'text-red-500 dark:text-red-400'
    : 'text-gray-400';
  const createdDateText = order?.created_date ? formatTimezoneDate(order.created_date, false) : 'Дата не указана';
  const pickupDateText = getStageDateTime(order)?.date ? formatTimezoneDate(getStageDateTime(order).date, false) : 'Дата не указана';
  const effectivePickupDateText = getEffectivePickupDate(order) ? formatTimezoneDate(getEffectivePickupDate(order), false) : 'Дата не указана';
  const effectiveDeliveryDateText = getEffectiveDeliveryDate(order) ? formatTimezoneDate(getEffectiveDeliveryDate(order), false) : 'Дата не указана';
  const pickupTimeText = stageDateTime?.time || 'Время не указано';
  const deliveryTimeText = getCurrentReturnTimeSlot(order) || 'Время не указано';
  const orderItemsCount = Array.isArray(order?.items) ? order.items.filter((item) => !item?.is_additional).length : 0;
  const stageSummaryMeta = (() => {
    if (order?.status === 'delivery') {
      return {
        primary: effectiveDeliveryDateText,
        secondary: deliveryTimeText,
        primaryIcon: Truck,
        secondaryIcon: Clock,
        primaryClassName: stageDateClass,
        secondaryClassName: stageDateClass,
        primaryIconClassName: stageDateIconClass,
        secondaryIconClassName: stageDateIconClass
      };
    }

    if (order?.status === 'handover') {
      return {
        primary: createdDateText,
        secondary: order?.self_delivery
          ? 'Самопривоз'
          : effectivePickupDateText,
        primaryIcon: Calendar,
        secondaryIcon: Truck,
        primaryClassName: 'text-gray-700 dark:text-gray-200',
        secondaryClassName: stageDateClass,
        primaryIconClassName: 'text-gray-400',
        secondaryIconClassName: order?.self_delivery ? 'text-gray-400' : stageDateIconClass
      };
    }

    if (order?.status === 'cleaning') {
      return {
        primary: effectivePickupDateText,
        secondary: getRepeatPickupTimeSlot(order) || order?.pickup_time_slot || getCurrentReturnTimeSlot(order) || 'Время не указано',
        primaryIcon: Truck,
        secondaryIcon: Clock,
        primaryClassName: stageDateClass,
        secondaryClassName: stageDateClass,
        primaryIconClassName: stageDateIconClass,
        secondaryIconClassName: stageDateIconClass
      };
    }

    if (['washing', 'drying_packing', 'approval'].includes(order?.status)) {
      return {
        primary: createdDateText,
        secondary: order?.self_delivery
          ? 'Самопривоз'
          : effectivePickupDateText,
        primaryIcon: Calendar,
        secondaryIcon: Truck,
        primaryClassName: 'text-gray-700 dark:text-gray-200',
        secondaryClassName: stageDateClass,
        primaryIconClassName: 'text-gray-400',
        secondaryIconClassName: order?.self_delivery ? 'text-gray-400' : stageDateIconClass
      };
    }

    return {
      primary: order?.self_delivery ? 'Самопривоз' : (stageDateTime?.date ? formatTimezoneDate(stageDateTime.date, false) : 'Дата не указана'),
      secondary: order?.self_delivery
        ? ''
        : (stageDateTime?.time || 'Время не указано'),
      primaryIcon: Truck,
      secondaryIcon: Clock,
      primaryClassName: order?.self_delivery ? 'text-gray-700 dark:text-gray-200' : stageDateClass,
      secondaryClassName: order?.self_delivery ? 'text-gray-500 dark:text-gray-400' : stageDateClass,
      primaryIconClassName: order?.self_delivery ? 'text-gray-400' : stageDateIconClass,
      secondaryIconClassName: order?.self_delivery ? 'text-gray-400' : stageDateIconClass
    };
  })();
  const extraInfoParts = [
    order?.internal_notes,
    order?.additional_info,
    order?.extra_info
  ].filter((value) => value && String(value).trim());
  const extraInfoUpdatedAt = useMemo(() => {
    const noteChanges = activityItems
      .filter((item) => item?.action === 'order_internal_notes_changed' && item?.created_date)
      .sort((first, second) => Date.parse(second.created_date) - Date.parse(first.created_date));
    return noteChanges[0]?.created_date || (extraInfoParts.length > 0 ? order?.created_date : null);
  }, [activityItems, extraInfoParts.length, order?.created_date]);

  const effectiveActivityItems = useMemo(() => {
    if (!order) return activityItems;
    const hasCreated = activityItems.some((item) => item?.action === 'order_created');
    if (hasCreated || !order.created_date) {
      return activityItems;
    }
    const fallbackCreatedItem = {
      id: `synthetic-order-created-${order.id}`,
      action: 'order_created',
      entity_type: 'order',
      entity_id: order.id,
      order_id: order.id,
      user_name: order.creator_name || 'Не указан',
      created_date: order.created_date,
      new_value: {
        text: 'Заказ создан',
        initial_snapshot: {
          items: [],
          services: [],
          discounts: []
        }
      }
    };
    return [...activityItems, fallbackCreatedItem];
  }, [activityItems, order]);
  const StageSummaryPrimaryIcon = stageSummaryMeta.primaryIcon;
  const StageSummarySecondaryIcon = stageSummaryMeta.secondaryIcon;
  const hasExtraInfo = extraInfoParts.length > 0;
  const clientTypeLabel = getClientTypeLabel(order?.client_type);
  const ClientIcon = isCompanyClient ? BriefcaseBusiness : User;
  const pickupAddressQuickText = (!order?.self_delivery ? getCurrentPickupAddressText(order) : '') || '';
  const washingFlowStatsSummaryText = washingFlowAssigneeSummaries
    .map((flow) => `${flow.label}: ${flow.assignee_name || 'не назначен'}`)
    .join('; ');
  const primaryAssigneeName = hasSingleWashingFlowAssigneeSummary
    ? (washingFlowAssigneeSummaries[0]?.assignee_name || 'Не назначен')
    : (order?.assignee_name || 'Не назначен');
  const approvalSavedAddresses = useMemo(() => {
    const byId = new Map();
    (clientAddresses || []).forEach((address) => {
      if (address?.address_full && !byId.has(address.id)) {
        byId.set(address.id, address);
      }
    });
    return Array.from(byId.values());
  }, [clientAddresses]);
  const approvalDraftIsSaved = useMemo(() => {
    if (!order) return true;
    const savedType = getReturnDeliveryType(order);
    const savedDate = getDateParts(getCurrentReturnDate(order))?.iso || '';
    const savedTime = getCurrentReturnTimeSlot(order) || '';
    const savedAddress = getCurrentReturnAddressText(order) || '';
    const savedAddressId = getCurrentReturnAddressId(order);
    const savedCallRequired = order?.dispatcher_call_required_stage === 'delivery';
    const targetStageName = savedType === 'self_pickup' ? 'handover' : (savedType === 'delivery' ? 'delivery' : null);
    const targetStageRow = targetStageName ? (order?.stages || []).find((s) => s.stage_name === targetStageName) : null;
    const savedReturnAssignedTo = targetStageRow?.assigned_to ? String(targetStageRow.assigned_to) : '';
    return (
      (approvalDraft.returnType || '') === savedType
      && (approvalDraft.deliveryDate || '') === savedDate
      && (approvalDraft.deliveryTime || '') === savedTime
      && (approvalDraft.deliveryAddress || '') === savedAddress
      && (approvalDraft.deliveryAddressId || null) === savedAddressId
      && !!approvalDraft.callRequired === savedCallRequired
      && (approvalDraft.returnAssignedTo || '') === savedReturnAssignedTo
    );
  }, [approvalDraft, order]);

  const savedReturnAssigneeName = useMemo(() => {
    const targetStageName = approvalDraft.returnType === 'self_pickup' ? 'handover' : (approvalDraft.returnType === 'delivery' ? 'delivery' : null);
    const targetStageRow = targetStageName ? (order?.stages || []).find((s) => s.stage_name === targetStageName) : null;
    if (targetStageRow?.assignee_name) return targetStageRow.assignee_name;
    if (targetStageRow?.assigned_to) {
      const match = approvalAssignees.find((u) => String(u.id) === String(targetStageRow.assigned_to));
      if (match?.name) return match.name;
    }
    if (approvalDraft.returnAssignedTo) {
      const match = approvalAssignees.find((u) => String(u.id) === String(approvalDraft.returnAssignedTo));
      if (match?.name) return match.name;
    }
    return '';
  }, [approvalDraft.returnType, approvalDraft.returnAssignedTo, order?.stages, approvalAssignees]);
  const approvalHasSavedReturnSettings = useMemo(() => {
    const savedType = getReturnDeliveryType(order);
    if (savedType === 'self_pickup') return true;
    if (savedType !== 'delivery') return false;
    return !!(
      getCurrentReturnDate(order)
      && getCurrentReturnTimeSlot(order)
      && isFutureScheduleValue(getCurrentReturnDate(order), getCurrentReturnTimeSlot(order))
      && getCurrentReturnAddressText(order)
      && (
        getCurrentReturnAddressId(order)
        || (getCurrentReturnLat(order) !== null && getCurrentReturnLng(order) !== null)
      )
    );
  }, [order]);
  const canEditReturnSettings = canUseWorkActions && isReturnSettingsStage;
  const canEditApprovalFields = canEditReturnSettings && (!approvalHasSavedReturnSettings || approvalEditMode);
  const approvalSelectedAddressText = approvalDraft.deliveryAddress.trim();
  const approvalAddressConfirmed = !!(
    approvalDraft.deliveryAddressId
    || (
      approvalDraft.deliveryAddressComplete
      && approvalDraft.deliveryLat !== null
      && approvalDraft.deliveryLng !== null
    )
  );
  const getApprovalRouteItemKey = (item) => `${item?.entity_type === 'task' ? 'task' : 'order'}:${item?.order_id ?? ''}`;
  const showApprovalRouteItemOnMap = (item) => {
    if (!item) return;
    setApprovalRoutesPanelView('map');
    setApprovalRoutesMapFocusRequest({
      orderId: item.order_id,
      entityType: item.entity_type === 'task' ? 'task' : 'order',
      routeKey: getApprovalRouteItemKey(item),
      scheduledDateKey: item.scheduled_date_key || approvalDraft.deliveryDate || '',
      issuedAt: Date.now()
    });
  };
  const draftReturnRouteItem = useMemo(() => {
    if (approvalDraft.returnType !== 'delivery' || !approvalDraft.deliveryDate || !approvalSelectedAddressText) {
      return null;
    }

    return {
      entity_type: 'draft_order',
      is_draft_order: true,
      order_id: `return_${order?.id || 'order'}`,
      order_number: order?.order_number || '',
      draft_label: 'Возврат заказа',
      draft_short_label: 'Возврат',
      stage_name: 'draft_order',
      scheduled_date_key: approvalDraft.deliveryDate,
      scheduled_date: `${approvalDraft.deliveryDate}T00:00:00`,
      time_slot: '',
      address_text: approvalSelectedAddressText,
      latitude: approvalDraft.deliveryLat,
      longitude: approvalDraft.deliveryLng
    };
  }, [
    approvalDraft.deliveryDate,
    approvalDraft.deliveryLat,
    approvalDraft.deliveryLng,
    approvalDraft.returnType,
    approvalSelectedAddressText,
    order?.id,
    order?.order_number
  ]);
  const invoiceDraft = invoiceDraftData?.invoice || null;
  const actDraft = invoiceDraftData?.act || null;
  const invoicePreparedAt = order?.invoice_prepared_at || invoiceDraftData?.invoice_prepared_at || null;
  const invoicePreparedByName = order?.invoice_prepared_by_name || invoiceDraftData?.invoice_prepared_by_name || '';
  const actPreparedAt = order?.act_prepared_at || invoiceDraftData?.act_prepared_at || null;
  const actPreparedByName = order?.act_prepared_by_name || invoiceDraftData?.act_prepared_by_name || '';
  const isOrderCompleted = order?.status === 'completed';
  const canEditOrderDocuments = canUseWorkActions || (isOrderCompleted && (isPrivilegedUser || ['dispatcher', 'admin', 'developer'].includes(currentUserType)));

  const invoiceIsPrepared = !!invoicePreparedAt;
  const actIsPrepared = !!actPreparedAt;
  const documentsPackageIsPrepared = invoiceIsPrepared && actIsPrepared;

  const hasPreClosingDocuments = !!(
    order?.has_pre_closing_documents
    || order?.closing_invoice_number
    || order?.closing_act_number
    || invoiceDraftData?.has_pre_closing_documents
  );
  const closingInvoicePreparedAt = order?.closing_invoice_prepared_at || invoiceDraftData?.closing_invoice_prepared_at || null;
  const closingActPreparedAt = order?.closing_act_prepared_at || invoiceDraftData?.closing_act_prepared_at || null;
  const closingInvoiceIsPrepared = !!(closingInvoicePreparedAt || order?.closing_invoice_number || invoiceDraftData?.closing_invoice_number);
  const closingActIsPrepared = !!(closingActPreparedAt || order?.closing_act_number || invoiceDraftData?.closing_act_number);
  const closingPackageIsPrepared = closingInvoiceIsPrepared && closingActIsPrepared;

  const preClosingAttachmentsCount = (closingInvoiceIsPrepared ? 1 : 0) + (closingActIsPrepared ? 1 : 0) + (closingPackageIsPrepared ? 1 : 0);
  const documentAttachmentsCount = (invoiceIsPrepared ? 1 : 0) + (actIsPrepared ? 1 : 0) + (documentsPackageIsPrepared ? 1 : 0) + preClosingAttachmentsCount;
  const hasInvoiceAttachment = isCompanyClient && (invoiceIsPrepared || actIsPrepared || hasPreClosingDocuments);
  const invoiceAttachmentFileName = buildInvoiceAttachmentFileName(order, invoiceDraft);
  const actAttachmentFileName = buildActAttachmentFileName(order, actDraft);
  const documentsPackageFileName = buildDocumentsPackageFileName(order, invoiceDraft, actDraft);
  const invoicePdfUnavailableReason = invoiceIsPrepared ? '' : 'Сначала сохраните счёт, потом его можно скачать или распечатать';
  const actPdfUnavailableReason = actIsPrepared ? '' : 'Сначала сохраните акт, потом его можно скачать или распечатать';
  const documentsPackageUnavailableReason = documentsPackageIsPrepared ? '' : 'Сначала сохраните счёт и акт, потом пакет можно скачать или распечатать';
  const canDownloadInvoicePdf = invoiceIsPrepared && !invoiceDownloading;
  const canPrintInvoicePdf = invoiceIsPrepared && !invoicePrinting;
  const canDownloadActPdf = actIsPrepared && !actDownloading;
  const canPrintActPdf = actIsPrepared && !actPrinting;
  const canDownloadDocumentsPackagePdf = documentsPackageIsPrepared && !documentsPackageDownloading;
  const canPrintDocumentsPackagePdf = documentsPackageIsPrepared && !documentsPackagePrinting;
  const companyDocumentsStatusLabel = documentsPackageIsPrepared
    ? 'Счёт и акт готовы'
    : (invoiceIsPrepared || actIsPrepared ? 'Пакет заполнен не полностью' : 'Не подготовлены');
  const dispatcherPickupDraftIsSaved = useMemo(() => {
    if (!order) return true;
    const savedMode = isCleaningRepeatOrder
      ? 'pickup'
      : (order?.active_revision?.pickup_mode || (order.self_delivery ? 'self_delivery' : 'pickup'));
    const savedDate = getDateParts(getRepeatPickupDate(order) || order.pickup_date)?.iso || '';
    const savedTime = getRepeatPickupTimeSlot(order) || order.pickup_time_slot || '';
    return (
      (dispatcherPickupDraft.mode || '') === savedMode
      && (dispatcherPickupDraft.pickupDate || '') === savedDate
      && (dispatcherPickupDraft.pickupTime || '') === savedTime
    );
  }, [dispatcherPickupDraft, isCleaningRepeatOrder, order]);
  const pageAccent = {
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-gray-200 dark:border-gray-800',
    bg: 'bg-blue-600'
  };
  const openedFromNotification = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('notification') === '1';
  }, [location.search]);

  const orderBackRoute = useMemo(() => {
    if (openedFromNotification) {
      return buildMyOrdersBackRoute('');
    }
    if (accessSource === 'my-orders') {
      return buildMyOrdersBackRoute(location.search);
    }
    if (accessSource === 'orders-log') {
      return '/orders-log';
    }
    if (accessSource === 'routes') {
      const routeDateKey = new URLSearchParams(location.search || '').get('route_date') || '';
      return buildRoutesReturnPath(routeDateKey);
    }
    return '';
  }, [accessSource, location.search, openedFromNotification]);

  useEffect(() => {
    if (!isPrivilegedUser || isLimitedArchiveAccess || ['completed', 'cancelled'].includes(order?.status)) {
      setDispatcherModeEnabled(false);
    }
  }, [isPrivilegedUser, isLimitedArchiveAccess, order?.status]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('orderdispatchermodechange', {
      detail: {
        enabled: Boolean(dispatcherModeEnabled && hasEffectivePrivilegedAccess && !isLimitedArchiveAccess)
      }
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('orderdispatchermodechange', {
        detail: { enabled: false }
      }));
    };
  }, [dispatcherModeEnabled, hasEffectivePrivilegedAccess, isLimitedArchiveAccess]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleRevisionAccordion = (revisionId) => {
    setExpandedRevisionIds((prev) => ({
      ...prev,
      [revisionId]: !prev[revisionId]
    }));
  };

  const toggleRevisionStageLog = (revisionId) => {
    setExpandedRevisionStageLogIds((prev) => ({
      ...prev,
      [revisionId]: !prev[revisionId]
    }));
  };

  const toggleActivitySnapshot = (id) => {
    setExpandedActivityIds((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const { showToast } = useToast();

  useEffect(() => {
    if (!revisionsForDisplay.length) return;
    setExpandedRevisionIds((prev) => {
      const next = { ...prev };
      let changed = false;
      revisionsForDisplay.forEach((revision, index) => {
        if (next[revision.id] === undefined) {
          next[revision.id] = index === 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [revisionsForDisplay]);

  const apiRequest = async (url, options = {}) => {
    if (connectionUnavailable || navigator.onLine === false || (offlineOrderSnapshotAt && !['GET', 'HEAD'].includes((options.method || 'GET').toUpperCase()))) {
      throw new Error('Нет соединения. Сохранённая карточка доступна только для просмотра.');
    }
    const token = localStorage.getItem('token');
    const isFormData = options.body instanceof FormData;
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
        'Authorization': `Bearer ${token}`,
        ...(dispatcherModeEnabled ? { 'X-Dispatcher-Access': '1' } : {}),
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = data.detail;
      let message = 'Действие не выполнено';
      if (typeof detail === 'object' && detail?.missing_requirements) {
        message = detail.missing_requirements.join('\n');
      } else if (typeof detail === 'object' && detail?.items) {
        message = detail.items.join('\n');
      } else if (typeof detail === 'object' && detail?.message) {
        message = detail.message;
      } else if (typeof detail === 'string') {
        message = detail;
      }
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const withDispatcherAccess = (options = {}) => ({
    ...options,
    headers: {
      'X-Dispatcher-Access': '1',
      ...(options.headers || {})
    }
  });

  const loadOrder = async ({ showLoading = true } = {}) => {
    let hasCached = false;
    if (showLoading) {
      try {
        const cached = await readOfflineRouteItem('order', orderId);
        if (cached?.item) {
          setOrder(cached.detail || buildOfflineOrderFromRouteItem(cached.item));
          setWorkflow(buildOfflineOrderWorkflow(cached.item));
          setComments([]);
          setPhotos([]);
          setActivityItems([]);
          setOfflineOrderSnapshotAt(cached.snapshot?.syncedAt || '');
          setLoadError(null);
          hasCached = true;
        }
      } catch (cacheErr) {
        // ignore
      }
      if (!hasCached) {
        setLoading(true);
      }
    }
    try {
      const [orderData, workflowData] = await Promise.all([
        apiRequest(`${API_URL}/api/orders/${orderId}`),
        apiRequest(`${API_URL}/api/orders/${orderId}/workflow-state`)
      ]);
      setOrder(orderData);
      setWorkflow(workflowData);
      setOfflineOrderSnapshotAt('');
      setLoadError(null);
    } catch (error) {
      if (!hasCached) {
        if (shouldUseOfflineDetailFallback(error)) {
          const cached = await readOfflineRouteItem('order', orderId).catch(() => null);
          if (cached?.item) {
            setOrder(cached.detail || buildOfflineOrderFromRouteItem(cached.item));
            setWorkflow(buildOfflineOrderWorkflow(cached.item));
            setComments([]);
            setPhotos([]);
            setActivityItems([]);
            setOfflineOrderSnapshotAt(cached.snapshot?.syncedAt || '');
            setLoadError(null);
            return;
          }

          setOrder(null);
          setWorkflow(null);
          setOfflineOrderSnapshotAt('');
          setLoadError({
            kind: 'offline_unavailable',
            title: 'Заявка недоступна офлайн',
            message: 'Эта заявка не сохранена на устройстве. Когда связь вернётся, страница попробует загрузить её автоматически.',
          });
          return;
        }

        setOrder(null);
        setWorkflow(null);
        setOfflineOrderSnapshotAt('');
        setLoadError({
          kind: error?.status === 404 ? 'not_found' : 'load_failed',
          title: error?.status === 404 ? 'Заказ не найден' : 'Не удалось открыть заказ',
          message: error?.message || 'Попробуйте обновить страницу.',
        });
        showToast('error', error.message);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadInvoiceDraft = async ({ silent = false } = {}) => {
    if (!orderId || !['b2b', 'company'].includes(order?.client_type)) {
      setInvoiceDraftData(null);
      return null;
    }

    setInvoiceDraftLoading(true);
    try {
      const data = await apiRequest(`${API_URL}/api/orders/${orderId}/documents-draft`);
      setInvoiceDraftData(data);
      return data;
    } catch (error) {
      if (!silent) {
        showToast('error', error.message);
      }
      return null;
    } finally {
      setInvoiceDraftLoading(false);
    }
  };

  const openInvoiceEditor = async () => {
    if (!order?.id || !['b2b', 'company'].includes(order?.client_type)) return;
    haptic('open');
    setShowInvoiceEditor(true);
    await loadInvoiceDraft({ silent: false });
  };

  const loadCommentsAndHistory = async () => {
    if (!orderId) return;
    if (offlineOrderSnapshotAt) {
      setComments([]);
      setPhotos([]);
      setActivityItems([]);
      return;
    }
    try {
      const [commentsData, photosData, activityData] = await Promise.all([
        apiRequest(`${API_URL}/api/orders/${orderId}/comments`).catch(() => []),
        apiRequest(`${API_URL}/api/orders/${orderId}/photos`).catch(() => []),
        apiRequest(`${API_URL}/api/activity-log/order/${orderId}`).catch(() => [])
      ]);
      setComments(Array.isArray(commentsData) ? commentsData : []);
      setPhotos(Array.isArray(photosData) ? photosData : []);
      setActivityItems(Array.isArray(activityData) ? activityData : []);
    } catch (error) {
      console.error('Ошибка загрузки комментариев/истории:', error);
    }
  };

  useEffect(() => {
    setPaymentMethodDraft(String(order?.payment_method || ''));
  }, [order?.payment_method]);

  const openAssigneeModal = async () => {
    if (!canOpenDispatcherAssignee || actionLoading) return;
    haptic('open');
    setAssigneeDraft(order?.assigned_to ? String(order.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
    setAvailableAssigneeFlows([]);
    setAssigneeStageDraft('');
    setShowAssigneeModal(true);
    setAssigneesLoading(true);
    try {
      const data = await apiRequest(`${API_URL}/api/orders/${order.id}/available-assignees`);
      if (data?.is_flow_assignment && Array.isArray(data.flows) && data.flows.length > 0) {
        const flows = data.flows;
        const initialFlow = flows.find((flow) => flow.stage_name === currentListStage)
          || flows.find((flow) => flow.can_assign !== false)
          || flows[0]
          || null;
        setAvailableAssigneeFlows(flows);
        setAvailableAssignees([]);
        setAssigneeStageDraft(initialFlow?.stage_name || '');
        setAssigneeDraft(initialFlow?.assigned_to ? String(initialFlow.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
      } else {
        setAvailableAssigneeFlows([]);
        setAssigneeStageDraft('');
        setAvailableAssignees(Array.isArray(data.users) ? data.users : []);
        setAssigneeDraft(order?.assigned_to ? String(order.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
      }
    } catch (error) {
      showToast('error', error.message);
      setAvailableAssignees([]);
      setAvailableAssigneeFlows([]);
      setAssigneeStageDraft('');
    } finally {
      setAssigneesLoading(false);
    }
  };

  useEffect(() => {
    if (!showAssigneeModal || !isFlowAssigneeSelection) return;
    const nextFlow = availableAssigneeFlows.find((flow) => flow.stage_name === assigneeStageDraft)
      || availableAssigneeFlows[0]
      || null;
    if (!nextFlow) return;
    if (nextFlow.stage_name !== assigneeStageDraft) {
      setAssigneeStageDraft(nextFlow.stage_name);
      return;
    }
    const nextAssigneeDraft = nextFlow.assigned_to ? String(nextFlow.assigned_to) : UNASSIGN_ASSIGNEE_VALUE;
    // When the dispatcher switches between flows, reset the dropdown to that flow's current assignee.
    setAssigneeDraft(nextAssigneeDraft);
  }, [
    showAssigneeModal,
    isFlowAssigneeSelection,
    assigneeStageDraft,
    availableAssigneeFlows
  ]);

  const loadDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const data = await apiRequest(`${API_URL}/api/departments`);
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast('error', error.message || 'Не удалось загрузить подразделения');
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const openDepartmentModal = async () => {
    if (actionLoading) return;
    if (!canUseDispatcherDepartment) {
      showToast('info', dispatcherDepartmentUnavailableReason);
      return;
    }
    haptic('open');
    setDepartmentDraft(order?.department_id ? String(order.department_id) : '');
    setDepartmentRepriceDraft(false);
    setShowDepartmentModal(true);
    if (!departments.length) {
      await loadDepartments();
    }
  };

  const openDispatcherNotesModal = () => {
    if (!canOpenDispatcherNotes || actionLoading) return;
    haptic('open');
    setShowDispatcherNotesModal(true);
  };

  const openDispatcherPriorityModal = () => {
    if (!canOpenDispatcherPriority || actionLoading) return;
    if (isUrgentOrder) {
      showToast('info', 'Для срочного заказа диспетчерский приоритет недоступен');
      return;
    }
    haptic('open');
    setShowDispatcherPriorityModal(true);
  };

  const openDispatcherCallRequirementModal = () => {
    if (!canOpenDispatcherCallRequirement || actionLoading) return;
    if (!canSelectPickupDispatcherCall && !canSelectDeliveryDispatcherCall && !dispatcherCallRequiredStage) {
      showToast('info', dispatcherCallUnavailableReason || 'Созвон можно поставить только на выезд: забор или доставку клиенту.');
      return;
    }
    haptic('open');
    setShowDispatcherCallRequirementModal(true);
  };

  const saveDispatcherNotes = async (draftValue) => {
    const nextValue = String(draftValue || '');
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          internal_notes: nextValue.trim() || null
        })
      });
      showToast('success', nextValue.trim() ? 'Доп. информация сохранена' : 'Доп. информация очищена');
      setShowDispatcherNotesModal(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const saveDispatcherPriority = async ({ scope, stageName }) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`${API_URL}/api/orders/${order.id}/dispatcher-priority`, {
        method: 'PUT',
        body: JSON.stringify(
          scope === 'stage'
            ? { scope, stage_name: stageName }
            : { scope }
        )
      });
      showToast('success', response?.message || 'Приоритет очереди обновлён');
      setShowDispatcherPriorityModal(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleEmergencyDispatch = async () => {
    if (!canUseEmergencyDispatch) {
      showToast('info', emergencyDispatchUnavailableReason);
      return;
    }
    setShowEmergencyDispatchConfirm(true);
  };

  const confirmEmergencyDispatch = async () => {
    setShowEmergencyDispatchConfirm(false);
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({ emergency_dispatch: !isEmergencyDispatch })
      });
      showToast('success', isEmergencyDispatch ? 'Экстренный выезд снят' : 'Экстренный выезд установлен');
      await loadOrder({ showLoading: false });
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const saveDispatcherCallRequirement = async (stageName) => {
    setActionLoading(true);
    try {
      const response = await apiRequest(`${API_URL}/api/orders/${order.id}/dispatcher-call-requirement`, {
        method: 'PUT',
        body: JSON.stringify({
          stage_name: stageName || 'none'
        })
      });
      setShowDispatcherCallRequirementModal(false);
      showToast('success', response?.message || (stageName ? 'Признак созвона сохранён' : 'Признак созвона снят'));
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const saveDepartmentChange = async () => {
    if (!departmentDraft) {
      showToast('info', 'Выберите подразделение');
      return;
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, withDispatcherAccess({
        method: 'PUT',
        body: JSON.stringify({
          department_id: Number(departmentDraft),
          assigned_to: null,
          recalculate_department_prices: departmentRepriceDraft
        })
      }));
      showToast('success', departmentRepriceDraft ? 'Подразделение и цены обновлены' : 'Подразделение изменено');
      setShowDepartmentModal(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openDispatcherAddressModal = async () => {
    if (actionLoading) return;
    if (!canUseDispatcherAddress) {
      showToast('info', dispatcherAddressUnavailableReason);
      return;
    }
    haptic('open');
    setDispatcherAddressDraft({
      address: dispatcherAddressCurrentText || '',
      addressId: dispatcherAddressField === 'delivery_address' ? getCurrentReturnAddressId(order) : getCurrentPickupAddressId(order),
      lat: dispatcherAddressField === 'delivery_address' ? getCurrentReturnLat(order) : getCurrentPickupLat(order),
      lng: dispatcherAddressField === 'delivery_address' ? getCurrentReturnLng(order) : getCurrentPickupLng(order),
      dadataLat: null,
      dadataLng: null,
      yandexLat: null,
      yandexLng: null,
      isCompleteAddress: true
    });
    setDispatcherAddressesOpen(false);
    setDispatcherAddressSuggestions([]);
    setShowDispatcherAddressSuggestions(false);
    setShowDispatcherAddressModal(true);

    if (!clientAddresses.length && order?.client_id) {
      try {
        const data = await apiRequest(`${API_URL}/api/clients/${getClientAddressType(order)}/${order.client_id}/addresses`);
        setClientAddresses(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Ошибка загрузки адресов клиента:', error);
      }
    }
  };

  const fetchDispatcherAddressSuggestions = async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setDispatcherAddressSuggestions([]);
      setShowDispatcherAddressSuggestions(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: trimmed })
      });
      const data = await response.json();
      const suggestions = Array.isArray(data) ? data : (data.suggestions || []);
      setDispatcherAddressSuggestions(suggestions);
      setShowDispatcherAddressSuggestions(suggestions.length > 0);
    } catch {
      setDispatcherAddressSuggestions([]);
      setShowDispatcherAddressSuggestions(false);
    }
  };

  const handleDispatcherAddressChange = (value) => {
    dispatcherCoordinateRequestRef.current += 1;
    setDispatcherAddressDraft((prev) => ({
      ...prev,
      address: value,
      addressId: null,
      lat: null,
      lng: null,
      dadataLat: null,
      dadataLng: null,
      yandexLat: null,
      yandexLng: null,
      isCompleteAddress: false
    }));
    fetchDispatcherAddressSuggestions(value);
  };

  const applyDispatcherSuggestion = (suggestion) => {
    const addressText = getSuggestionAddressText(suggestion);
    const lat = parseSuggestionCoordinate(suggestion?.data?.geo_lat);
    const lng = parseSuggestionCoordinate(suggestion?.data?.geo_lon);
    const requestId = ++dispatcherCoordinateRequestRef.current;
    setDispatcherAddressDraft((prev) => ({
      ...prev,
      address: addressText,
      addressId: null,
      lat,
      lng,
      dadataLat: lat,
      dadataLng: lng,
      yandexLat: null,
      yandexLng: null,
      isCompleteAddress: isCompleteServiceAddress(suggestion)
    }));
    resolvePreferredAddressCoordinates({ apiKey: yandexApiKey, addressText, data: suggestion?.data || {} })
      .then((coordinates) => {
        if (requestId !== dispatcherCoordinateRequestRef.current) return;
        setDispatcherAddressDraft((prev) => (
          prev.address === addressText
            ? {
                ...prev,
                lat: coordinates.latitude,
                lng: coordinates.longitude,
                dadataLat: coordinates.dadataLatitude,
                dadataLng: coordinates.dadataLongitude,
                yandexLat: coordinates.yandexLatitude,
                yandexLng: coordinates.yandexLongitude
              }
            : prev
        ));
      });
    setDispatcherAddressSuggestions([]);
    setShowDispatcherAddressSuggestions(false);
    setTimeout(() => {
      dispatcherAddressInputRef.current?.blur();
      document.activeElement?.blur?.();
    }, 50);
  };

  const selectDispatcherSuggestion = (suggestion) => {
    const validation = getServiceAddressValidation(suggestion);
    if (validation.isComplete && !validation.hasStreet) {
      setAddressWithoutStreetSuggestion({ target: 'dispatcher', suggestion });
      return;
    }
    applyDispatcherSuggestion(suggestion);
  };

  const applyDispatcherAddress = (address) => {
    const addressText = address?.address_full || address || '';
    const coordinates = readStoredAddressCoordinates(typeof address === 'object' ? address : {});
    setDispatcherAddressDraft((prev) => ({
      ...prev,
      address: addressText,
      addressId: typeof address === 'object' ? address.id : null,
      lat: typeof address === 'object' ? coordinates.latitude : prev.lat,
      lng: typeof address === 'object' ? coordinates.longitude : prev.lng,
      dadataLat: typeof address === 'object' ? coordinates.dadataLatitude : prev.dadataLat,
      dadataLng: typeof address === 'object' ? coordinates.dadataLongitude : prev.dadataLng,
      yandexLat: typeof address === 'object' ? coordinates.yandexLatitude : prev.yandexLat,
      yandexLng: typeof address === 'object' ? coordinates.yandexLongitude : prev.yandexLng,
      isCompleteAddress: true
    }));
    setDispatcherAddressesOpen(false);
    setShowDispatcherAddressSuggestions(false);
  };

  const saveDispatcherAddress = async () => {
    const addressText = dispatcherAddressDraft.address.trim();
    if (!addressText) {
      showToast('info', 'Укажите адрес');
      return;
    }
    if (!dispatcherAddressDraft.addressId && (dispatcherAddressDraft.lat === null || dispatcherAddressDraft.lng === null)) {
      showToast('info', 'Выберите адрес из подсказок или из сохранённых адресов клиента');
      return;
    }
    if (!dispatcherAddressDraft.addressId && !dispatcherAddressDraft.isCompleteAddress) {
      showToast('info', incompleteServiceAddressMessage());
      return;
    }
    setActionLoading(true);
    try {
      const savedAddress = clientAddresses.find((address) => Number(address.id) === Number(dispatcherAddressDraft.addressId));
      const resolvedCoordinates = await resolvePreferredAddressCoordinates({
        apiKey: yandexApiKey,
        addressText,
        data: {
          latitude: dispatcherAddressDraft.lat ?? savedAddress?.latitude,
          longitude: dispatcherAddressDraft.lng ?? savedAddress?.longitude,
          dadata_latitude: dispatcherAddressDraft.dadataLat ?? savedAddress?.dadata_latitude,
          dadata_longitude: dispatcherAddressDraft.dadataLng ?? savedAddress?.dadata_longitude,
          yandex_latitude: dispatcherAddressDraft.yandexLat ?? savedAddress?.yandex_latitude,
          yandex_longitude: dispatcherAddressDraft.yandexLng ?? savedAddress?.yandex_longitude
        }
      });
      const isDeliveryAddress = dispatcherAddressField === 'delivery_address';
      const editingRepeatPickup = isRepeatOrder && !isDeliveryAddress;
      const payload = editingRepeatPickup
        ? {
            pickup_mode: dispatcherPickupDraft.mode,
            pickup_address_text: addressText,
            pickup_address_id: dispatcherAddressDraft.addressId,
            pickup_lat: resolvedCoordinates.latitude,
            pickup_lng: resolvedCoordinates.longitude,
            pickup_dadata_lat: resolvedCoordinates.dadataLatitude,
            pickup_dadata_lng: resolvedCoordinates.dadataLongitude,
            pickup_yandex_lat: resolvedCoordinates.yandexLatitude,
            pickup_yandex_lng: resolvedCoordinates.yandexLongitude
          }
        : (
          isDeliveryAddress
            ? {
                delivery_address: addressText,
                delivery_address_id: dispatcherAddressDraft.addressId,
                delivery_lat: resolvedCoordinates.latitude,
                delivery_lng: resolvedCoordinates.longitude,
                delivery_dadata_lat: resolvedCoordinates.dadataLatitude,
                delivery_dadata_lng: resolvedCoordinates.dadataLongitude,
                delivery_yandex_lat: resolvedCoordinates.yandexLatitude,
                delivery_yandex_lng: resolvedCoordinates.yandexLongitude
              }
            : {
                pickup_address: addressText,
                pickup_address_id: dispatcherAddressDraft.addressId,
                pickup_lat: resolvedCoordinates.latitude,
                pickup_lng: resolvedCoordinates.longitude,
                pickup_dadata_lat: resolvedCoordinates.dadataLatitude,
                pickup_dadata_lng: resolvedCoordinates.dadataLongitude,
                pickup_yandex_lat: resolvedCoordinates.yandexLatitude,
                pickup_yandex_lng: resolvedCoordinates.yandexLongitude
              }
        );
      await apiRequest(
        editingRepeatPickup
          ? `${API_URL}/api/orders/${order.id}/revisions/active/pickup-settings`
          : `${API_URL}/api/orders/${order.id}`,
        withDispatcherAccess({
          method: editingRepeatPickup ? 'PUT' : 'PUT',
          body: JSON.stringify(payload)
        })
      );
      const nextResolvedMapCoords = resolvedCoordinates.latitude !== null && resolvedCoordinates.longitude !== null
        ? { lat: resolvedCoordinates.latitude, lng: resolvedCoordinates.longitude }
        : null;
      setOrder((prev) => {
        if (!prev) return prev;
        if (editingRepeatPickup) {
          return {
            ...prev,
            active_revision: prev.active_revision ? {
              ...prev.active_revision,
              pickup_address_id: dispatcherAddressDraft.addressId,
              pickup_address_text: addressText,
              pickup_lat: resolvedCoordinates.latitude,
              pickup_lng: resolvedCoordinates.longitude
            } : prev.active_revision
          };
        }
        return {
          ...prev,
          ...payload,
          ...(isDeliveryAddress
            ? { delivery_address_text: addressText }
            : { pickup_address_text: addressText })
        };
      });
      setResolvedMapCoords(nextResolvedMapCoords);
      showToast('success', editingRepeatPickup ? 'Адрес повторного забора обновлён' : 'Адрес обновлён');
      setShowDispatcherAddressModal(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const assignOrderTo = async (userId, { successMessage } = {}) => {
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({ assigned_to: userId })
      });
      showToast('success', successMessage || (userId ? 'Исполнитель назначен' : 'Исполнитель снят'));
      setShowTakeOrderConfirm(false);
      setShowAssigneeModal(false);
      setAvailableAssigneeFlows([]);
      setAssigneeStageDraft('');
      setAssigneeDraft(userId ? String(userId) : '');
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const takeOrderIntoWork = async () => {
    if (!currentWorkflowUserId) {
      showToast('error', 'Не удалось определить текущего пользователя');
      return;
    }
    await assignOrderTo(currentWorkflowUserId, { successMessage: 'Заказ взят в работу' });
  };

  const assignWashingFlowTo = async (stageName, userId, { successMessage } = {}) => {
    if (!stageName) {
      showToast('error', 'Не удалось определить поток стирки');
      return;
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/washing-flows/${stageName}/assignee`, {
        method: 'PUT',
        body: JSON.stringify({ assigned_to: userId })
      });
      showToast('success', successMessage || (userId ? 'Исполнитель потока назначен' : 'Исполнитель потока снят'));
      setShowAssigneeModal(false);
      setAvailableAssigneeFlows([]);
      setAssigneeStageDraft('');
      setAssigneeDraft(userId ? String(userId) : UNASSIGN_ASSIGNEE_VALUE);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const takeWashingFlowIntoWork = async () => {
    if (!currentListStage || !['washing_laundry', 'washing_cleaning'].includes(currentListStage)) {
      showToast('error', 'Не удалось определить поток стирки');
      return;
    }

    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/washing-flows/${currentListStage}/take`, {
        method: 'POST'
      });
      showToast('success', 'Поток взят в работу');
      setShowTakeOrderConfirm(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const selectedAssigneeLabel = useMemo(() => {
    if (!assigneeDraft || assigneeDraft === UNASSIGN_ASSIGNEE_VALUE) return 'снять исполнителя';
    const match = selectedAssigneeUsers.find((user) => String(user.id) === String(assigneeDraft));
    return match?.name || match?.login || 'выбранного исполнителя';
  }, [assigneeDraft, selectedAssigneeUsers]);

  const assigneeDraftIsUnassign = !assigneeDraft || assigneeDraft === UNASSIGN_ASSIGNEE_VALUE;
  const isAssigneeDraftUnchanged = String(selectedAssigneeCurrentValue) === String(assigneeDraft || UNASSIGN_ASSIGNEE_VALUE);
  const assigneeModalSaveDisabled = actionLoading
    || (isFlowAssigneeSelection && !selectedAssigneeFlow)
    || (isFlowAssigneeSelection && selectedAssigneeFlow?.can_assign === false)
    || isAssigneeDraftUnchanged;
  const saveAssigneeSelection = () => {
    const targetUserId = assigneeDraft && assigneeDraft !== UNASSIGN_ASSIGNEE_VALUE ? Number(assigneeDraft) : null;
    if (isFlowAssigneeSelection && selectedAssigneeFlow && ['washing_laundry', 'washing_cleaning'].includes(selectedAssigneeFlow.stage_name)) {
      return assignWashingFlowTo(selectedAssigneeFlow.stage_name, targetUserId);
    }
    return assignOrderTo(targetUserId);
  };

  const markVisibleCommentsRead = async () => {
    const unreadComments = comments.filter((comment) => !comment.is_read);
    if (!orderId || unreadComments.length === 0) return;
    setComments((prev) => prev.map((comment) => ({ ...comment, is_read: true })));
    try {
      await Promise.all(unreadComments.map((comment) => (
        apiRequest(`${API_URL}/api/orders/${orderId}/comments/${comment.id}/read`, { method: 'POST' })
      )));
    } catch (error) {
      console.error('Ошибка отметки комментариев прочитанными:', error);
      await loadCommentsAndHistory();
    }
  };

  useEffect(() => {
    const loadApiKey = async () => {
      try {
        const data = await apiRequest(`${API_URL}/api/settings/yandex_maps_api_key`);
        setYandexApiKey(data.value || '');
      } catch (error) {
        console.error('Ошибка загрузки API ключа:', error);
      }
    };
    loadApiKey();
  }, []);

  useEffect(() => {
    if (orderId) loadOrder();
  }, [orderId, connectionUnavailable]);

  useEffect(() => {
    if (loadError?.kind !== 'offline_unavailable' && !offlineOrderSnapshotAt) return undefined;

    const retryLoad = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      loadOrder({ showLoading: true });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        retryLoad();
      }
    };

    window.addEventListener('online', retryLoad);
    window.addEventListener('offline-data-ready', retryLoad);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', retryLoad);
      window.removeEventListener('offline-data-ready', retryLoad);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadError?.kind, orderId, offlineOrderSnapshotAt]);

  useEffect(() => {
    setInvoiceDraftData(null);
    setShowInvoiceEditor(false);
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    const modeKey = dispatcherModeEnabled ? 'dispatcher' : 'standard';
    if (dispatcherModeReloadRef.current === null) {
      dispatcherModeReloadRef.current = modeKey;
      return;
    }
    if (dispatcherModeReloadRef.current === modeKey) return;
    dispatcherModeReloadRef.current = modeKey;
    loadOrder({ showLoading: false });
  }, [dispatcherModeEnabled, orderId]);

  useEffect(() => {
    if (!openedFromNotification || !workflow || !onNavigate) return;
    if (workflow.is_privileged || workflow.is_current_assignee || workflow.is_creator) return;
    showToast('error', 'Заказ больше не назначен вам');
    onNavigate('/my-orders', { replace: true });
  }, [openedFromNotification, workflow, onNavigate]);

  useEffect(() => {
    if (openedFromNotification || orderBackPreparedRef.current || !order?.id || !orderBackRoute) return;

    const currentRoute = `${location.pathname}${location.search || ''}`;

    try {
      const baseUrl = new URL(window.location.href);
      const backUrl = new URL(window.location.href);
      backUrl.hash = `#${orderBackRoute}`;

      window.history.replaceState(
        {
          ...(window.history.state || {}),
          codexBackTarget: orderBackRoute
        },
        '',
        backUrl.toString()
      );

      const orderUrl = `${baseUrl.origin}/#${currentRoute}`;
      window.history.pushState(
        {
          ...(window.history.state || {}),
          codexOrderEntry: true
        },
        '',
        orderUrl
      );

      orderBackPreparedRef.current = true;
    } catch (error) {
      console.error('Не удалось подготовить возврат со страницы заказа:', error);
    }
  }, [
    openedFromNotification,
    order?.id,
    orderBackRoute,
    location.pathname,
    location.search
  ]);

  useEffect(() => {
    if (!openedFromNotification || notificationBackPreparedRef.current || !order?.id) return;

    const backRoute = '/my-orders';
    const currentRoute = `${location.pathname}${location.search || ''}`;

    if (order.status) {
      sessionStorage.setItem('myOrdersStage', order.status);
      sessionStorage.setItem('myOrdersBackStageStrict', '1');
    }

    try {
      const baseUrl = new URL(window.location.href);
      const backUrl = new URL(window.location.href);
      backUrl.hash = `#${backRoute}`;

      window.history.replaceState(
        {
          ...(window.history.state || {}),
          codexBackTarget: backRoute
        },
        '',
        backUrl.toString()
      );

      const orderUrl = `${baseUrl.origin}/#${currentRoute}`;
      window.history.pushState(
        {
          ...(window.history.state || {}),
          codexNotificationOrderEntry: true
        },
        '',
        orderUrl
      );

      notificationBackPreparedRef.current = true;
    } catch (error) {
      console.error('Не удалось подготовить возврат после перехода из уведомления:', error);
    }
  }, [
    openedFromNotification,
    order?.id,
    order?.status,
    location.pathname,
    location.search
  ]);

  useEffect(() => {
    loadCommentsAndHistory();
  }, [orderId]);

  useEffect(() => {
    if (expandedSections.comments) {
      markVisibleCommentsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSections.comments, comments]);

  useEffect(() => {
    setFloorsDraft(Math.max(0, Math.min(99, Number(order?.floors_count || 0))));
  }, [order?.floors_count]);

  useEffect(() => {
    setDispatcherPickupDraft({
      mode: isCleaningRepeatOrder
        ? 'pickup'
        : (order?.active_revision?.pickup_mode || (order?.self_delivery ? 'self_delivery' : 'pickup')),
      pickupDate: getDateParts(getRepeatPickupDate(order) || order?.pickup_date)?.iso || '',
      pickupTime: getRepeatPickupTimeSlot(order) || order?.pickup_time_slot || ''
    });
  }, [isCleaningRepeatOrder, order?.active_revision?.pickup_mode, order?.active_revision?.pickup_date, order?.active_revision?.pickup_time_slot, order?.self_delivery, order?.pickup_date, order?.pickup_time_slot]);

  useEffect(() => {
    const savedReturnType = getReturnDeliveryType(order);
    const savedReturnDate = getCurrentReturnDate(order);
    const savedReturnTimeSlot = getCurrentReturnTimeSlot(order);
    const hasFutureSavedReturnSchedule = isFutureScheduleValue(savedReturnDate, savedReturnTimeSlot);
    const hasCompleteSavedReturnSettings = savedReturnType === 'self_pickup' || (
      savedReturnType === 'delivery'
      && hasFutureSavedReturnSchedule
      && !!getCurrentReturnAddressText(order)
      && !!(
        getCurrentReturnAddressId(order)
        || (getCurrentReturnLat(order) !== null && getCurrentReturnLng(order) !== null)
      )
    );
    const targetStageName = savedReturnType === 'self_pickup' ? 'handover' : (savedReturnType === 'delivery' ? 'delivery' : null);
    const targetStageRow = targetStageName ? (order?.stages || []).find((s) => s.stage_name === targetStageName) : null;
    const savedReturnAssignedTo = targetStageRow?.assigned_to ? String(targetStageRow.assigned_to) : '';

    setApprovalDraft({
      returnType: savedReturnType,
      deliveryDate: hasFutureSavedReturnSchedule ? (getDateParts(savedReturnDate)?.iso || '') : '',
      deliveryTime: hasFutureSavedReturnSchedule ? (savedReturnTimeSlot || '') : '',
      deliveryAddress: getCurrentReturnAddressText(order) || '',
      deliveryAddressId: getCurrentReturnAddressId(order),
      deliveryLat: getCurrentReturnLat(order),
      deliveryLng: getCurrentReturnLng(order),
      deliveryDadataLat: null,
      deliveryDadataLng: null,
      deliveryYandexLat: null,
      deliveryYandexLng: null,
      deliveryAddressComplete: true,
      callRequired: order?.dispatcher_call_required_stage === 'delivery',
      returnAssignedTo: savedReturnAssignedTo
    });
    setApprovalEditMode(!hasCompleteSavedReturnSettings);
  }, [
    order?.dispatcher_call_required_stage,
    order?.return_delivery_type,
    order?.status,
    order?.delivery_date,
    order?.delivery_time_slot,
    order?.delivery_address_text,
    order?.delivery_address_id,
    order?.delivery_lat,
    order?.delivery_lng,
    order?.active_revision?.return_delivery_type,
    order?.active_revision?.return_date,
    order?.active_revision?.return_time_slot,
    order?.active_revision?.return_address_text,
    order?.active_revision?.return_address_id,
    order?.active_revision?.return_lat,
    order?.active_revision?.return_lng,
    order?.stages
  ]);

  // Загрузка доступных исполнителей для этапа возврата (самозабор -> выдача, доставка -> доставка)
  useEffect(() => {
    if (!order?.id || !isReturnSettingsStage || !approvalDraft.returnType) {
      setApprovalAssignees([]);
      return;
    }

    let cancelled = false;
    const fetchApprovalAssignees = async () => {
      setApprovalAssigneesLoading(true);
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          return_type: approvalDraft.returnType
        });
        if (order.department_id) {
          params.set('department_id', order.department_id);
        }
        const response = await fetch(`${API_URL}/api/orders/${order.id}/approval-assignees?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Не удалось загрузить исполнителей');
        }
        const data = await response.json();
        if (cancelled) return;
        const users = Array.isArray(data?.users) ? data.users : [];
        setApprovalAssignees(users);
        if (approvalDraft.returnAssignedTo && !users.some((u) => String(u.id) === String(approvalDraft.returnAssignedTo))) {
          setApprovalDraft((prev) => ({ ...prev, returnAssignedTo: '' }));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Ошибка загрузки исполнителей на этап возврата:', error);
          setApprovalAssignees([]);
        }
      } finally {
        if (!cancelled) {
          setApprovalAssigneesLoading(false);
        }
      }
    };

    fetchApprovalAssignees();

    return () => {
      cancelled = true;
    };
  }, [order?.id, order?.department_id, isReturnSettingsStage, approvalDraft.returnType]);

  useEffect(() => {
    if (!isReturnSettingsStage) {
      setApprovalAddressEditorOpen(false);
      setApprovalSavedAddressesOpen(false);
      setShowApprovalAddressSuggestions(false);
      setApprovalRoutesOpen(false);
      setApprovalRouteItems([]);
    }
  }, [isReturnSettingsStage]);

  useEffect(() => {
    if (!isReturnSettingsStage || !canEditApprovalFields || approvalDraft.returnType !== 'delivery') return;
    if (!approvalSelectedAddressText) {
      setApprovalAddressEditorOpen(true);
    }
  }, [isReturnSettingsStage, canEditApprovalFields, approvalDraft.returnType, approvalSelectedAddressText]);

  useEffect(() => {
    localStorage.setItem('new_order_routes_department_scope', approvalRouteDepartmentScope);
  }, [approvalRouteDepartmentScope]);

  useEffect(() => {
    if (!order?.department_id && approvalRouteDepartmentScope === 'selected') {
      setApprovalRouteDepartmentScope('all');
    }
  }, [approvalRouteDepartmentScope, order?.department_id]);

  useEffect(() => {
    localStorage.setItem('approval_routes_department_view', approvalRouteDepartmentView);
  }, [approvalRouteDepartmentView]);

  useEffect(() => {
    localStorage.setItem('approval_routes_show_department_badge', approvalRouteShowDepartmentBadge ? 'true' : 'false');
  }, [approvalRouteShowDepartmentBadge]);

  useEffect(() => {
    localStorage.setItem('approval_routes_show_executor', approvalRouteShowExecutor ? 'true' : 'false');
  }, [approvalRouteShowExecutor]);

  useEffect(() => {
    localStorage.setItem('approval_routes_hide_region_in_address', approvalRouteHideRegion ? 'true' : 'false');
  }, [approvalRouteHideRegion]);

  useEffect(() => {
    setApprovalRoutesMapFocusRequest(null);
  }, [approvalDraft.deliveryDate]);

  useEffect(() => () => {
    if (approvalAddressDebounceRef.current) {
      clearTimeout(approvalAddressDebounceRef.current);
    }
  }, []);

  useEffect(() => {
    if (isReturnSettingsStage && approvalDraft.returnType === 'delivery' && approvalDraft.deliveryDate) {
      fetchApprovalRouteItems(
        approvalDraft.deliveryDate,
        approvalRouteDepartmentScope === 'selected' ? order?.department_id : null
      );
    } else {
      setApprovalRouteItems([]);
    }
  }, [approvalDraft.deliveryDate, approvalDraft.returnType, approvalRouteDepartmentScope, isReturnSettingsStage, order?.department_id, order?.service_type]);

  useEffect(() => {
    const parseCoordinate = (value) => (
      value === null || value === undefined || String(value).trim() === ''
        ? Number.NaN
        : Number(value)
    );
    const latitude = parseCoordinate(approvalDraft.deliveryLat);
    const longitude = parseCoordinate(approvalDraft.deliveryLng);
    const addressText = approvalSelectedAddressText;
    if (
      !isReturnSettingsStage
      || approvalDraft.returnType !== 'delivery'
      || !addressText
    ) {
      setApprovalRouteSuggestions([]);
      setApprovalRouteSuggestionsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setApprovalRouteSuggestionsLoading(true);
    const params = new URLSearchParams({
      stage_name: 'delivery',
      service_type: order?.service_type || 'washing',
      address_text: addressText,
      exclude_order_id: String(order?.id || '')
    });
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      params.set('latitude', String(latitude));
      params.set('longitude', String(longitude));
    }

    fetch(`${API_URL}/api/orders/route-suggestions?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error('Не удалось получить подсказки маршрута');
        return response.json();
      })
      .then((data) => {
        setApprovalRouteSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Ошибка подсказок маршрута:', error);
          setApprovalRouteSuggestions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setApprovalRouteSuggestionsLoading(false);
      });

    return () => controller.abort();
  }, [
    approvalDraft.deliveryLat,
    approvalDraft.deliveryLng,
    approvalDraft.returnType,
    approvalSelectedAddressText,
    currentUserType,
    currentUserTypeLoaded,
    isReturnSettingsStage,
    order?.id,
    order?.service_type
  ]);

  useEffect(() => {
    const deferredStage = String(order?.status || '').trim().toLowerCase();
    const addressText = String(orderAddress || '').trim();
    if (
      !showDeferredResolveModal
      || !isDeferredOrder
      || !['pickup', 'delivery', 'cleaning'].includes(deferredStage)
      || !addressText
    ) {
      setDeferredRouteSuggestions([]);
      setDeferredRouteSuggestionsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      stage_name: deferredStage,
      service_type: order?.service_type || 'washing',
      address_text: addressText,
      exclude_order_id: String(order?.id || ''),
    });
    const latitude = Number(mapCoords?.lat);
    const longitude = Number(mapCoords?.lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      params.set('latitude', String(latitude));
      params.set('longitude', String(longitude));
    }

    setDeferredRouteSuggestionsLoading(true);
    fetch(`${API_URL}/api/orders/route-suggestions?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Не удалось получить подсказки маршрута');
        return response.json();
      })
      .then((data) => {
        setDeferredRouteSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Ошибка подсказок для отложенного задания:', error);
          setDeferredRouteSuggestions([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDeferredRouteSuggestionsLoading(false);
      });

    return () => controller.abort();
  }, [
    isDeferredOrder,
    mapCoords?.lat,
    mapCoords?.lng,
    order?.id,
    order?.service_type,
    order?.status,
    orderAddress,
    showDeferredResolveModal,
  ]);

  useEffect(() => {
    if (!order?.client_id || !isReturnSettingsStage) {
      setClientAddresses([]);
      return;
    }

    let cancelled = false;
    const loadClientAddresses = async () => {
      try {
        const data = await apiRequest(`${API_URL}/api/clients/${getClientAddressType(order)}/${order.client_id}/addresses`);
        if (!cancelled) {
          setClientAddresses(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Ошибка загрузки адресов клиента:', error);
          setClientAddresses([]);
        }
      }
    };

    loadClientAddresses();
    return () => {
      cancelled = true;
    };
  }, [order?.client_id, order?.client_type, isReturnSettingsStage]);

  useEffect(() => {
    if (!approvalDraft.deliveryAddressId || !clientAddresses.length) return;
    const savedAddress = clientAddresses.find((address) => Number(address.id) === Number(approvalDraft.deliveryAddressId));
    if (!savedAddress) return;
    const coordinates = readStoredAddressCoordinates(savedAddress);
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryLat: coordinates.latitude ?? prev.deliveryLat,
      deliveryLng: coordinates.longitude ?? prev.deliveryLng,
      deliveryDadataLat: coordinates.dadataLatitude,
      deliveryDadataLng: coordinates.dadataLongitude,
      deliveryYandexLat: coordinates.yandexLatitude,
      deliveryYandexLng: coordinates.yandexLongitude
    }));
  }, [approvalDraft.deliveryAddressId, clientAddresses]);

  useEffect(() => {
    if (!order?.order_number) return undefined;

    window.dispatchEvent(new CustomEvent('orderheadertitle', {
      detail: {
        title: `Заказ №${order.order_number}`,
        orderNumber: order.order_number,
        urgent: order.priority === 'urgent',
        emergencyDispatch: Boolean(order.emergency_dispatch),
        clientVip: Boolean(order.client_vip),
        dispatcherPriority: Boolean(dispatcherPriorityScope),
        dispatcherCallRequiredStage: dispatcherCallRequiredStage || null,
        rework: Boolean(order.active_rework)
      }
    }));

    return () => {
      window.dispatchEvent(new CustomEvent('orderheadertitle', {
        detail: {
          title: '',
          orderNumber: '',
          urgent: false,
          emergencyDispatch: false,
          clientVip: false,
          dispatcherPriority: false,
          dispatcherCallRequiredStage: null,
          rework: false
        }
      }));
    };
  }, [dispatcherCallRequiredStage, dispatcherPriorityScope, order?.active_rework, order?.client_vip, order?.emergency_dispatch, order?.order_number, order?.priority]);

  const runAction = async (url, successMessage, options = {}) => {
    const { silent = false, onSuccess, ...requestOptions } = options;
    setActionLoading(true);
    try {
      const response = await apiRequest(url, { method: 'POST', ...requestOptions });
      if (!silent) showToast('success', successMessage);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
      if (onSuccess) await onSuccess(response);
      return true;
    } catch (error) {
      showToast('error', error.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const startRepeatOrder = async ({ reason, pickupMode, pickupDate, pickupTimeSlot }) => {
    const normalizedReason = String(reason || '').trim();
    const effectivePickupMode = order?.service_type === 'cleaning' ? 'pickup' : (pickupMode || 'pickup');
    if (!normalizedReason) {
      showToast('info', 'Укажите причину повторного выполнения');
      return;
    }
    if (effectivePickupMode === 'pickup') {
      if (!pickupDate || !pickupTimeSlot) {
        showToast('info', order?.service_type === 'cleaning' ? 'Для повтора химчистки выберите дату и интервал' : 'Для повторного забора выберите дату и интервал');
        return;
      }
      if (!repeatPickupSourceAddressText) {
        showToast('error', order?.service_type === 'cleaning' ? 'В заказе не найден адрес для повторной химчистки' : 'В заказе не найден адрес, откуда забирать вещи на повтор');
        return;
      }
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/revisions`, {
        method: 'POST',
        body: JSON.stringify({
          reason: normalizedReason,
          pickup_mode: effectivePickupMode,
          pickup_date: effectivePickupMode === 'pickup' ? formatDateInputToApiIso(pickupDate) : null,
          pickup_time_slot: effectivePickupMode === 'pickup' ? pickupTimeSlot : null
        })
      });
      showToast('success', 'Повтор открыт');
      setShowRepeatOrderModal(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleRepeatItem = async (item, selected) => {
    if (!item?.id) return;
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/revisions/active/items/${item.id}`, {
        method: selected ? 'DELETE' : 'POST'
      });
      showToast('success', selected ? 'Предмет снят с повтора' : 'Предмет отмечен на повтор');
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const completeCurrentStage = async () => {
    const completedStage = order.status;
    const ok = await runAction(
      `${API_URL}/api/orders/${order.id}/stages/${order.status}/complete`,
      isFinalPaymentStage ? 'Заказ закрыт' : 'Этап завершён'
    );
    if (!ok) return;
    setShowCompleteConfirm(false);
    if (completedStage) {
      sessionStorage.setItem('myOrdersStage', completedStage);
    }
    if (onNavigate) {
      if (accessSource === 'routes') {
        onNavigate('/routes', { replace: true });
        return;
      }
      onNavigate('/my-orders', { replace: true });
    }
  };

  const requestRework = async ({ itemIds, comment }) => {
    const normalizedIds = Array.isArray(itemIds) ? itemIds.filter(Boolean) : [];
    const normalizedComment = String(comment || '').trim();
    if (normalizedIds.length === 0) {
      showToast('info', 'Выберите хотя бы один предмет');
      return;
    }
    if (normalizedComment.length < 3) {
      showToast('info', 'Опишите, что нужно доработать');
      return;
    }

    const fromDeferredDelivery = isDeferredOrder && order?.status === 'delivery';
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/rework`, {
        method: 'POST',
        body: JSON.stringify({
          item_ids: normalizedIds,
          comment: normalizedComment
        })
      });
      showToast('success', 'Заказ возвращён на доработку');
      setShowReworkModal(false);
      sessionStorage.setItem('myOrdersStage', fromDeferredDelivery ? 'washing' : 'drying_packing');
      if (fromDeferredDelivery && onNavigate) {
        onNavigate('/my-orders?tab=my&stage=deferred', { replace: true });
        return;
      }
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const deferCurrentOrder = async ({ reasonCode, comment }) => {
    const normalizedComment = String(comment || '').trim();
    if (!reasonCode || normalizedComment.length < 3) {
      showToast('info', 'Выберите причину и добавьте короткий комментарий');
      return;
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/defer`, {
        method: 'POST',
        body: JSON.stringify({
          reason_code: reasonCode,
          comment: normalizedComment.slice(0, 300),
        }),
      });
      showToast('success', 'Задание передано диспетчеру');
      setShowDeferModal(false);
      sessionStorage.setItem('myOrdersStage', order.status);
      if (onNavigate) {
        onNavigate(`/my-orders?${new URLSearchParams({ tab: 'my', stage: order.status }).toString()}`, { replace: true });
        return;
      }
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openDeferredResolveModal = async () => {
    if (!canResolveDeferredOrder) return;
    haptic('open');
    setShowDeferredResolveModal(true);
    setDeferredAssigneesLoading(true);
    try {
      const data = await apiRequest(`${API_URL}/api/orders/${order.id}/available-assignees`);
      setDeferredAssignees(Array.isArray(data?.users) ? data.users : []);
    } catch (error) {
      setDeferredAssignees([]);
      showToast('error', error.message);
    } finally {
      setDeferredAssigneesLoading(false);
    }
  };

  const resolveDeferredOrder = async ({ resolution, scheduledDate, timeSlot, assignedTo }) => {
    if (!scheduledDate || !timeSlot) {
      showToast('info', 'Укажите новые дату и интервал');
      return;
    }
    if (resolution === 'assigned' && !assignedTo) {
      showToast('info', 'Выберите исполнителя');
      return;
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/deferred/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          resolution,
          scheduled_date: formatDateInputToApiIso(scheduledDate),
          time_slot: timeSlot,
          assigned_to: resolution === 'assigned' ? Number(assignedTo) : null,
        }),
      });
      showToast('success', 'Задание возвращено в работу');
      setShowDeferredResolveModal(false);
      if (onNavigate) {
        onNavigate('/my-orders?tab=my&stage=deferred', { replace: true });
        return;
      }
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelOrder = async (reasonValue) => {
    const reason = String(reasonValue || '').trim();
    if (!reason) {
      showToast('info', 'Укажите причину отмены заказа');
      return;
    }
    const ok = await runAction(`${API_URL}/api/orders/${order.id}/cancel`, 'Заказ отменён', {
      body: JSON.stringify({ reason })
    });
    if (!ok) return;
    setShowCancelConfirm(false);
    if (isDeferredOrder && onNavigate) {
      onNavigate('/my-orders?tab=my&stage=deferred', { replace: true });
    }
  };

  const cancelActiveRevision = async (reasonValue) => {
    const reason = String(reasonValue || '').trim();
    if (!reason) {
      showToast('info', 'Укажите причину отмены повтора');
      return;
    }
    const ok = await runAction(`${API_URL}/api/orders/${order.id}/revisions/active/cancel`, 'Повтор отменён', {
      body: JSON.stringify({ reason })
    });
    if (!ok) return;
    setShowCancelConfirm(false);
  };

  const handleCompleteButtonClick = () => {
    if (actionLoading) return;
    if (canUseDispatcherCorrections) {
      if (isWashingFlowOrder && !allWashingFlowsAssigned) {
        showToast('info', 'Назначьте исполнителя на каждый незавершённый поток Стирки');
        return;
      }
      if (!isWashingFlowOrder && !order.assigned_to) {
        showToast('info', 'Назначьте исполнителя перед завершением этапа диспетчером');
        return;
      }
    }
    if (!canCompleteStage) {
      const missingList = workflow?.requirements?.missing_requirements || [];
      showToast('info', missingList.length
        ? `Перед переходом нужно:\n${missingList.map((item) => `- ${item}`).join('\n')}`
        : 'Пока нельзя завершить этап');
      return;
    }
    haptic('open');
    setShowCompleteConfirm(true);
  };

  const handleMarkOrderPaidClick = () => {
    if (actionLoading) return;
    if (!canUseWorkActions) return;
    if (!isFinalPaymentStage) {
      showToast('error', 'Отметить оплату можно только на финальных этапах');
      return;
    }
    if (isCompanyClient) {
      showToast('info', 'Оплата юрлица подтверждается в разделе расчётов после поступления денег');
      return;
    }
    if (order?.payment_status === 'paid') {
      showToast('info', 'Заказ уже отмечен как оплаченный');
      return;
    }
    if (!String(order?.payment_method || '').trim()) {
      showToast('info', 'Сначала укажите способ оплаты');
      return;
    }
    haptic('open');
    setShowPaidConfirm(true);
  };

  const markOrderPaid = async () => {
    if (!canUseWorkActions) return;
    if (!isFinalPaymentStage) {
      showToast('error', 'Отметить оплату можно только на финальных этапах');
      return;
    }
    if (isCompanyClient) {
      showToast('info', 'Оплата юрлица подтверждается через раздел расчётов');
      return;
    }
    if (!String(order?.payment_method || '').trim()) {
      showToast('info', 'Сначала укажите способ оплаты');
      return;
    }
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({ payment_status: 'paid' })
      });
      setShowPaidConfirm(false);
      showToast('success', 'Заказ отмечен как оплаченный');
      await loadOrder({ showLoading: false });
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const savePaymentMethod = async (method) => {
    const normalized = String(method || '').trim();
    if (!canUseWorkActions || !isFinalPaymentStage || !normalized) return;
    if (isCompanyClient) {
      showToast('info', 'Для юрлица используется только расчётный счёт');
      return;
    }
    if (order?.payment_status === 'paid') {
      showToast('info', 'После отметки об оплате способ оплаты менять нельзя');
      return;
    }
    if (normalized === String(order?.payment_method || '')) return;
    haptic('selection');
    setPaymentMethodDraft(normalized);
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({ payment_method: normalized })
      });
      showToast('success', 'Способ оплаты сохранён');
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
    } catch (error) {
      setPaymentMethodDraft(String(order?.payment_method || ''));
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const saveInvoiceDraft = async (nextDrafts) => {
    if (!canUseWorkActions || !order?.id || !isCompanyClient) return false;

    haptic('heavy');
    setInvoiceDraftSaving(true);
    try {
      const response = await apiRequest(`${API_URL}/api/orders/${order.id}/documents-draft`, {
        method: 'PUT',
        body: JSON.stringify(nextDrafts)
      });
      setInvoiceDraftData({
        invoice_number: response?.order?.invoice_number || invoiceDraftData?.invoice_number || null,
        invoice_prepared_at: response?.order?.invoice_prepared_at || new Date().toISOString(),
        invoice_prepared_by: response?.order?.invoice_prepared_by || null,
        invoice_prepared_by_name: response?.order?.invoice_prepared_by_name || '',
        act_number: response?.order?.act_number || invoiceDraftData?.act_number || null,
        act_prepared_at: response?.order?.act_prepared_at || new Date().toISOString(),
        act_prepared_by: response?.order?.act_prepared_by || null,
        act_prepared_by_name: response?.order?.act_prepared_by_name || '',
        invoice: response?.invoice || nextDrafts?.invoice || null,
        act: response?.act || nextDrafts?.act || null,
      });
      showToast('success', response?.message || 'Документы сохранены');
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
      return true;
    } catch (error) {
      showToast('error', error.message);
      return false;
    } finally {
      setInvoiceDraftSaving(false);
    }
  };

  const downloadOrderPdf = async ({ url, fileName, successMessage, errorMessage, setLoadingState }) => {
    haptic('selection');
    setLoadingState(true);
    try {
      const blob = await fetchProtectedBlob(url);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      showToast('success', successMessage);
      await loadOrder({ showLoading: false });
    } catch (error) {
      showToast('error', error.message || errorMessage);
    } finally {
      setLoadingState(false);
    }
  };

  const printOrderPdf = async ({
    url,
    title,
    loadingMessage,
    popupErrorMessage,
    successMessage,
    errorMessage,
    setLoadingState,
  }) => {
    haptic('selection');
    setLoadingState(true);

    let printWindow = null;
    let objectUrl = null;

    try {
      printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error(popupErrorMessage);
      }

      printWindow.document.write(`
        <html lang="ru">
          <head>
            <title>${title}</title>
          </head>
          <body style="margin:0;padding:24px;font-family:system-ui,-apple-system,sans-serif;color:#0f172a;">
            <div style="font-size:14px;">${loadingMessage}</div>
          </body>
        </html>
      `);
      printWindow.document.close();

      const blob = await fetchProtectedBlob(url);
      objectUrl = URL.createObjectURL(blob);

      const triggerPrint = () => {
        try {
          printWindow?.focus();
          printWindow?.print();
        } catch (printError) {
          console.warn('Document print trigger failed', printError);
        }
      };

      printWindow.onload = () => {
        window.setTimeout(triggerPrint, 350);
      };
      printWindow.location.href = objectUrl;
      window.setTimeout(triggerPrint, 1800);
      window.setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 60000);

      showToast('success', successMessage);
      await loadOrder({ showLoading: false });
    } catch (error) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      showToast('error', error.message || errorMessage);
    } finally {
      setLoadingState(false);
    }
  };

  const handleDownloadInvoicePdf = async (options = {}) => {
    if (!order?.id || !isCompanyClient) return;
    const isPreClosing = options?.version === 'pre_closing';
    if (!isPreClosing && !invoiceIsPrepared) {
      haptic('warning');
      showToast('info', invoicePdfUnavailableReason || 'Сначала сохраните счёт');
      return;
    }
    if (invoiceDownloading) return;

    await downloadOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/invoice-pdf${isPreClosing ? '?version=pre_closing' : ''}`,
      fileName: isPreClosing ? invoiceAttachmentFileName.replace('.pdf', '_до_закрытия.pdf') : invoiceAttachmentFileName,
      successMessage: isPreClosing ? 'PDF счёта (до закрытия) скачан' : 'PDF счёта скачан',
      errorMessage: 'Не удалось скачать PDF счёта',
      setLoadingState: setInvoiceDownloading,
    });
  };

  const handlePrintInvoicePdf = async () => {
    if (!order?.id || !isCompanyClient) return;
    if (!invoiceIsPrepared) {
      haptic('warning');
      showToast('info', invoicePdfUnavailableReason || 'Сначала сохраните счёт');
      return;
    }
    if (invoicePrinting) return;

    await printOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/invoice-pdf`,
      title: `Счёт ${order?.invoice_number ? `№ ${order.invoice_number}` : ''}`,
      loadingMessage: 'Готовим PDF счёта для печати...',
      popupErrorMessage: 'Разрешите всплывающие окна, чтобы открыть счёт для печати',
      successMessage: 'PDF счёта открыт для печати',
      errorMessage: 'Не удалось открыть PDF счёта для печати',
      setLoadingState: setInvoicePrinting,
    });
  };

  const handleDownloadActPdf = async (options = {}) => {
    if (!order?.id || !isCompanyClient) return;
    const isPreClosing = options?.version === 'pre_closing';
    if (!isPreClosing && !actIsPrepared) {
      haptic('warning');
      showToast('info', actPdfUnavailableReason || 'Сначала сохраните акт');
      return;
    }
    if (actDownloading) return;

    await downloadOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/act-pdf${isPreClosing ? '?version=pre_closing' : ''}`,
      fileName: isPreClosing ? actAttachmentFileName.replace('.pdf', '_до_закрытия.pdf') : actAttachmentFileName,
      successMessage: isPreClosing ? 'PDF акта (до закрытия) скачан' : 'PDF акта скачан',
      errorMessage: 'Не удалось скачать PDF акта',
      setLoadingState: setActDownloading,
    });
  };

  const handlePrintActPdf = async () => {
    if (!order?.id || !isCompanyClient) return;
    if (!actIsPrepared) {
      haptic('warning');
      showToast('info', actPdfUnavailableReason || 'Сначала сохраните акт');
      return;
    }
    if (actPrinting) return;

    await printOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/act-pdf`,
      title: `Акт ${order?.act_number ? `№ ${order.act_number}` : ''}`,
      loadingMessage: 'Готовим PDF акта для печати...',
      popupErrorMessage: 'Разрешите всплывающие окна, чтобы открыть акт для печати',
      successMessage: 'PDF акта открыт для печати',
      errorMessage: 'Не удалось открыть PDF акта для печати',
      setLoadingState: setActPrinting,
    });
  };

  const handleDownloadDocumentsPackagePdf = async (options = {}) => {
    if (!order?.id || !isCompanyClient) return;
    const isPreClosing = options?.version === 'pre_closing';
    if (!isPreClosing && !documentsPackageIsPrepared) {
      haptic('warning');
      showToast('info', documentsPackageUnavailableReason || 'Сначала сохраните счёт и акт');
      return;
    }
    if (documentsPackageDownloading) return;

    await downloadOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/documents-package-pdf${isPreClosing ? '?version=pre_closing' : ''}`,
      fileName: isPreClosing ? documentsPackageFileName.replace('.pdf', '_до_закрытия.pdf') : documentsPackageFileName,
      successMessage: isPreClosing ? 'Пакет документов (до закрытия) скачан' : 'Пакет документов скачан',
      errorMessage: 'Не удалось скачать пакет документов',
      setLoadingState: setDocumentsPackageDownloading,
    });
  };

  const handlePrintDocumentsPackagePdf = async () => {
    if (!order?.id || !isCompanyClient) return;
    if (!documentsPackageIsPrepared) {
      haptic('warning');
      showToast('info', documentsPackageUnavailableReason || 'Сначала сохраните счёт и акт');
      return;
    }
    if (documentsPackagePrinting) return;

    await printOrderPdf({
      url: `${API_URL}/api/orders/${order.id}/documents-package-pdf`,
      title: `Пакет документов ${order?.order_number ? `по заказу ${order.order_number}` : ''}`.trim(),
      loadingMessage: 'Готовим пакет документов для печати...',
      popupErrorMessage: 'Разрешите всплывающие окна, чтобы открыть пакет документов для печати',
      successMessage: 'Пакет документов открыт для печати',
      errorMessage: 'Не удалось открыть пакет документов для печати',
      setLoadingState: setDocumentsPackagePrinting,
    });
  };

  const saveFloorsCount = async () => {
    if (!canUseWorkActions) return;
    const floorsCount = Math.max(0, Math.min(99, Number(floorsDraft) || 0));
    haptic('heavy');
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}`, {
        method: 'PUT',
        body: JSON.stringify({ floors_count: floorsCount })
      });
      showToast('success', 'Количество этажей обновлено');
      await loadOrder({ showLoading: false });
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const setApprovalField = (field, value) => {
    if (field === 'returnType') {
      setApprovalDraft((prev) => {
        if (
          value === 'delivery'
          && !prev.deliveryAddress.trim()
          && pickupAddressQuickText
          && !order?.self_delivery
        ) {
          return {
            ...prev,
            returnType: value,
            deliveryAddress: pickupAddressQuickText,
            deliveryAddressId: getCurrentPickupAddressId(order),
            deliveryLat: getCurrentPickupLat(order),
            deliveryLng: getCurrentPickupLng(order),
            deliveryDadataLat: null,
            deliveryDadataLng: null,
            deliveryYandexLat: null,
            deliveryYandexLng: null,
            deliveryAddressComplete: true
          };
        }
        return {
          ...prev,
          [field]: value,
          ...(value === 'self_pickup' ? { callRequired: false } : {})
        };
      });
      if (value === 'self_pickup') {
        setApprovalAddressEditorOpen(false);
        setApprovalSavedAddressesOpen(false);
        setShowApprovalAddressSuggestions(false);
      } else if (value === 'delivery') {
        setApprovalAddressEditorOpen(!approvalSelectedAddressText && !pickupAddressQuickText);
        setApprovalSavedAddressesOpen(false);
        setShowApprovalAddressSuggestions(false);
      }
      return;
    }

    setApprovalDraft((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const applyApprovalRouteSuggestion = (suggestion) => {
    if (!suggestion) return;
    haptic('selection');
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryDate: suggestion.date_key,
      deliveryTime: suggestion.time_slot
    }));
  };

  const fetchApprovalAddressSuggestions = async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setApprovalAddressSuggestions([]);
      setShowApprovalAddressSuggestions(false);
      return;
    }
    const requestId = ++approvalAddressRequestIdRef.current;
    try {
      const response = await fetch(`${API_URL}/api/dadata/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ query: trimmed, count: 6 })
      });
      const data = await response.json();
      if (requestId !== approvalAddressRequestIdRef.current) return;
      const suggestions = Array.isArray(data) ? data : (data.suggestions || []);
      setApprovalAddressSuggestions(suggestions);
      setShowApprovalAddressSuggestions(suggestions.length > 0);
    } catch {
      if (requestId !== approvalAddressRequestIdRef.current) return;
      setApprovalAddressSuggestions([]);
      setShowApprovalAddressSuggestions(false);
    }
  };

  const handleApprovalAddressChange = (value) => {
    approvalCoordinateRequestRef.current += 1;
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryAddress: value,
      deliveryAddressId: null,
      deliveryLat: null,
      deliveryLng: null,
      deliveryDadataLat: null,
      deliveryDadataLng: null,
      deliveryYandexLat: null,
      deliveryYandexLng: null,
      deliveryAddressComplete: false
    }));
    setApprovalSavedAddressesOpen(false);
    if (approvalAddressDebounceRef.current) {
      clearTimeout(approvalAddressDebounceRef.current);
    }
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      approvalAddressRequestIdRef.current += 1;
      setApprovalAddressSuggestions([]);
      setShowApprovalAddressSuggestions(false);
      return;
    }
    approvalAddressDebounceRef.current = setTimeout(() => {
      fetchApprovalAddressSuggestions(value);
    }, 220);
  };

  const clearApprovalAddressInput = () => {
    approvalCoordinateRequestRef.current += 1;
    approvalAddressRequestIdRef.current += 1;
    if (approvalAddressDebounceRef.current) {
      clearTimeout(approvalAddressDebounceRef.current);
      approvalAddressDebounceRef.current = null;
    }
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryAddress: '',
      deliveryAddressId: null,
      deliveryLat: null,
      deliveryLng: null,
      deliveryDadataLat: null,
      deliveryDadataLng: null,
      deliveryYandexLat: null,
      deliveryYandexLng: null
    }));
    setApprovalAddressSuggestions([]);
    setShowApprovalAddressSuggestions(false);
    setTimeout(() => approvalAddressInputRef.current?.focus(), 0);
  };

  const applyApprovalSuggestion = (suggestion) => {
    const addressText = getSuggestionAddressText(suggestion);
    const lat = parseSuggestionCoordinate(suggestion?.data?.geo_lat);
    const lng = parseSuggestionCoordinate(suggestion?.data?.geo_lon);
    const requestId = ++approvalCoordinateRequestRef.current;
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryAddress: addressText,
      deliveryAddressId: null,
      deliveryLat: lat,
      deliveryLng: lng,
      deliveryDadataLat: lat,
      deliveryDadataLng: lng,
      deliveryYandexLat: null,
      deliveryYandexLng: null,
      deliveryAddressComplete: isCompleteServiceAddress(suggestion)
    }));
    resolvePreferredAddressCoordinates({ apiKey: yandexApiKey, addressText, data: suggestion?.data || {} })
      .then((coordinates) => {
        if (requestId !== approvalCoordinateRequestRef.current) return;
        setApprovalDraft((prev) => (
          prev.deliveryAddress === addressText
            ? {
                ...prev,
                deliveryLat: coordinates.latitude,
                deliveryLng: coordinates.longitude,
                deliveryDadataLat: coordinates.dadataLatitude,
                deliveryDadataLng: coordinates.dadataLongitude,
                deliveryYandexLat: coordinates.yandexLatitude,
                deliveryYandexLng: coordinates.yandexLongitude
              }
            : prev
        ));
      });
    setApprovalAddressSuggestions([]);
    setShowApprovalAddressSuggestions(false);
    setApprovalAddressEditorOpen(false);
    setTimeout(() => {
      approvalAddressInputRef.current?.blur();
      document.activeElement?.blur?.();
    }, 50);
  };

  const selectApprovalSuggestion = (suggestion) => {
    const validation = getServiceAddressValidation(suggestion);
    if (validation.isComplete && !validation.hasStreet) {
      setAddressWithoutStreetSuggestion({ target: 'approval', suggestion });
      return;
    }
    applyApprovalSuggestion(suggestion);
  };

  const applyApprovalAddress = (address) => {
    const addressText = address?.address_full || address || '';
    const coordinates = readStoredAddressCoordinates(typeof address === 'object' ? address : {});
    setApprovalDraft((prev) => ({
      ...prev,
      deliveryAddress: addressText,
      deliveryAddressId: typeof address === 'object' ? address.id : getCurrentPickupAddressId(order),
      deliveryLat: typeof address === 'object' ? coordinates.latitude : getCurrentPickupLat(order),
      deliveryLng: typeof address === 'object' ? coordinates.longitude : getCurrentPickupLng(order),
      deliveryDadataLat: typeof address === 'object' ? coordinates.dadataLatitude : null,
      deliveryDadataLng: typeof address === 'object' ? coordinates.dadataLongitude : null,
      deliveryYandexLat: typeof address === 'object' ? coordinates.yandexLatitude : null,
      deliveryYandexLng: typeof address === 'object' ? coordinates.yandexLongitude : null,
      deliveryAddressComplete: true
    }));
    setApprovalAddressEditorOpen(false);
    setApprovalSavedAddressesOpen(false);
    setApprovalAddressSuggestions([]);
    setShowApprovalAddressSuggestions(false);
  };

  const fetchApprovalRouteItems = async (deliveryDate, departmentId) => {
    if (!deliveryDate || !isReturnSettingsStage || approvalDraft.returnType !== 'delivery') {
      setApprovalRouteItems([]);
      return;
    }

    setApprovalRoutesLoading(true);
    try {
      const params = new URLSearchParams({ target_date: deliveryDate });
      params.set('all_departments', 'true');
      if (order?.service_type) {
        params.set('service_type', order.service_type);
      }
      if (departmentId) {
        params.set('department_id', departmentId);
      }

      const response = await fetch(`${API_URL}/api/orders/route-schedule?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Не удалось загрузить назначенные выезды');
      }

      const data = await response.json();
      setApprovalRouteItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки назначенных выездов:', error);
      setApprovalRouteItems([]);
    } finally {
      setApprovalRoutesLoading(false);
    }
  };

  const saveApprovalSettings = async (options = {}) => {
    const dispatcherAccess = Boolean(options?.dispatcherAccess);
    const approvedCostChangeConfirmed = Boolean(options?.approvedCostChangeConfirmed);
    if (dispatcherAccess) {
      if (!canUseDispatcherScheduleSettings) {
        showToast('info', dispatcherScheduleUnavailableReason);
        return false;
      }
    } else if (!canUseWorkActions) {
      return false;
    }
    if (!approvalDraft.returnType) {
      showToast('info', 'Выберите способ возврата клиенту');
      return false;
    }
    if (
      isReturnSettingsFinalStage
      && approvalHasSavedReturnSettings
      && !approvalDraftIsSaved
      && !approvedCostChangeConfirmed
    ) {
      setApprovedCostChangeConfirm({
        title: 'Изменить условия возврата?',
        text: 'Заказ уже находится на финальном этапе. Изменение способа, даты или адреса возврата может пересчитать согласованную стоимость.',
        confirmLabel: 'Сохранить изменения',
        action: () => saveApprovalSettings({ ...options, approvedCostChangeConfirmed: true })
      });
      return false;
    }

    const savingRepeatReturn = isRepeatOrder && !!order?.active_revision;
    const dispatcherCallRequiredStage = isPrivilegedUser && approvalDraft.returnType === 'delivery' && approvalDraft.callRequired
      ? 'delivery'
      : null;
    const returnAssignedToValue = approvalDraft.returnAssignedTo ? Number(approvalDraft.returnAssignedTo) : null;
    const payload = savingRepeatReturn
      ? { return_delivery_type: approvalDraft.returnType, return_assigned_to: returnAssignedToValue }
      : { return_delivery_type: approvalDraft.returnType, return_assigned_to: returnAssignedToValue };

    if (approvalDraft.returnType === 'self_pickup') {
      Object.assign(
        payload,
        savingRepeatReturn
          ? {
              return_address_text: '',
              return_address_id: null,
              return_lat: null,
              return_lng: null,
              return_date: null,
              return_time_slot: null
            }
          : {
              delivery_address: '',
              delivery_address_id: null,
              delivery_lat: null,
              delivery_lng: null,
              delivery_date: null,
              delivery_time_slot: null,
              ...(isReturnSettingsFinalStage ? { status: 'handover' } : {})
            }
      );
    } else {
      const addressText = approvalDraft.deliveryAddress.trim();
      if (!approvalDraft.deliveryDate || !approvalDraft.deliveryTime || !addressText) {
        showToast('info', 'Для доставки клиенту укажите дату, интервал и адрес');
        return false;
      }
      if (!approvalAddressConfirmed) {
        showToast('info', approvalDraft.deliveryAddressId
          ? 'Выберите адрес из подсказок или из сохранённых адресов клиента'
          : incompleteServiceAddressMessage());
        return false;
      }

      const savedAddress = clientAddresses.find((address) => Number(address.id) === Number(approvalDraft.deliveryAddressId));
      const resolvedCoordinates = await resolvePreferredAddressCoordinates({
        apiKey: yandexApiKey,
        addressText,
        data: {
          latitude: approvalDraft.deliveryLat ?? savedAddress?.latitude,
          longitude: approvalDraft.deliveryLng ?? savedAddress?.longitude,
          dadata_latitude: approvalDraft.deliveryDadataLat ?? savedAddress?.dadata_latitude,
          dadata_longitude: approvalDraft.deliveryDadataLng ?? savedAddress?.dadata_longitude,
          yandex_latitude: approvalDraft.deliveryYandexLat ?? savedAddress?.yandex_latitude,
          yandex_longitude: approvalDraft.deliveryYandexLng ?? savedAddress?.yandex_longitude
        }
      });

      Object.assign(
        payload,
        savingRepeatReturn
          ? {
              return_address_text: addressText,
              return_address_id: approvalDraft.deliveryAddressId || null,
              return_lat: resolvedCoordinates.latitude,
              return_lng: resolvedCoordinates.longitude,
              return_dadata_lat: resolvedCoordinates.dadataLatitude,
              return_dadata_lng: resolvedCoordinates.dadataLongitude,
              return_yandex_lat: resolvedCoordinates.yandexLatitude,
              return_yandex_lng: resolvedCoordinates.yandexLongitude,
              return_date: formatDateInputToApiIso(approvalDraft.deliveryDate),
              return_time_slot: approvalDraft.deliveryTime
            }
          : {
              delivery_address: addressText,
              delivery_address_id: approvalDraft.deliveryAddressId || null,
              delivery_lat: resolvedCoordinates.latitude,
              delivery_lng: resolvedCoordinates.longitude,
              delivery_dadata_lat: resolvedCoordinates.dadataLatitude,
              delivery_dadata_lng: resolvedCoordinates.dadataLongitude,
              delivery_yandex_lat: resolvedCoordinates.yandexLatitude,
              delivery_yandex_lng: resolvedCoordinates.yandexLongitude,
              delivery_date: formatDateInputToApiIso(approvalDraft.deliveryDate),
              delivery_time_slot: approvalDraft.deliveryTime,
              ...(isReturnSettingsFinalStage ? { status: 'delivery' } : {})
            }
      );
    }

    const buildPutOptions = (bodyPayload) => {
      const requestOptions = {
        method: 'PUT',
        body: JSON.stringify(bodyPayload)
      };
      return dispatcherAccess ? withDispatcherAccess(requestOptions) : requestOptions;
    };

    haptic('heavy');
    setActionLoading(true);
    try {
      if (savingRepeatReturn) {
        await apiRequest(
          `${API_URL}/api/orders/${order.id}/revisions/active/return-settings`,
          buildPutOptions(payload)
        );
        if (isPrivilegedUser) {
          await apiRequest(`${API_URL}/api/orders/${order.id}`, buildPutOptions({
            dispatcher_call_required_stage: dispatcherCallRequiredStage
          }));
        }
      } else {
        await apiRequest(`${API_URL}/api/orders/${order.id}`, buildPutOptions(
          isPrivilegedUser
            ? {
                ...payload,
                dispatcher_call_required_stage: dispatcherCallRequiredStage
              }
            : payload
        ));
      }
      showToast('success', 'Условия возврата сохранены');
      setApprovalEditMode(false);
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
      return true;
    } catch (error) {
      showToast('error', error.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const resetApprovalSettings = async () => {
    if (!canUseWorkActions) return false;

    setActionLoading(true);
    try {
      const clearingRepeatReturn = isRepeatOrder && !!order?.active_revision;
      if (clearingRepeatReturn) {
        await apiRequest(
          `${API_URL}/api/orders/${order.id}/revisions/active/return-settings`,
          {
            method: 'PUT',
            body: JSON.stringify({
              return_delivery_type: null,
              return_assigned_to: null,
              return_address_text: '',
              return_address_id: null,
              return_lat: null,
              return_lng: null,
              return_date: null,
              return_time_slot: null
            })
          }
        );
        if (isPrivilegedUser) {
          await apiRequest(`${API_URL}/api/orders/${order.id}`, {
            method: 'PUT',
            body: JSON.stringify({
              dispatcher_call_required_stage: null
            })
          });
        }
      } else {
        await apiRequest(
          `${API_URL}/api/orders/${order.id}`,
          {
            method: 'PUT',
            body: JSON.stringify(
              isPrivilegedUser
                ? {
                    return_delivery_type: null,
                    return_assigned_to: null,
                    delivery_address: '',
                    delivery_address_id: null,
                    delivery_lat: null,
                    delivery_lng: null,
                    delivery_date: null,
                    delivery_time_slot: null,
                    dispatcher_call_required_stage: null
                  }
                : {
                    return_delivery_type: null,
                    return_assigned_to: null,
                    delivery_address: '',
                    delivery_address_id: null,
                    delivery_lat: null,
                    delivery_lng: null,
                    delivery_date: null,
                    delivery_time_slot: null
                  }
            )
          }
        );
      }
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
      setApprovalEditMode(true);
      setApprovalAddressEditorOpen(false);
      setApprovalSavedAddressesOpen(false);
      showToast('success', 'Сохранённые условия возврата сняты');
      return true;
    } catch (error) {
      showToast('error', error.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const saveDispatcherPickupSettings = async () => {
    if (!canUseDispatcherScheduleSettings) {
      showToast('info', dispatcherScheduleUnavailableReason);
      return false;
    }
    const effectiveMode = isCleaningRepeatOrder ? 'pickup' : dispatcherPickupDraft.mode;
    if (!effectiveMode) {
      showToast('info', 'Выберите способ приёма заказа');
      return false;
    }

    const payload = {};

    if (effectiveMode === 'self_delivery') {
      if (mainItemsCount <= 0) {
        showToast('info', 'Нельзя перевести на самодоставку: корзина пуста');
        return false;
      }
      Object.assign(payload, {
        self_delivery: true,
        pickup_date: null,
        pickup_time_slot: null,
        status: 'washing',
        assigned_to: null
      });
    } else {
      if (!dispatcherPickupDraft.pickupDate || !dispatcherPickupDraft.pickupTime) {
        showToast('info', 'Для доставки от клиента укажите дату и интервал');
        return false;
      }

      Object.assign(payload, {
        self_delivery: false,
        status: 'pickup',
        pickup_date: formatDateInputToApiIso(dispatcherPickupDraft.pickupDate),
        pickup_time_slot: dispatcherPickupDraft.pickupTime
      });
    }

    setActionLoading(true);
    try {
      if (isRepeatOrder) {
        const repeatPayload = {
          pickup_mode: effectiveMode,
          ...(effectiveMode === 'pickup'
            ? {
                pickup_date: formatDateInputToApiIso(dispatcherPickupDraft.pickupDate),
                pickup_time_slot: dispatcherPickupDraft.pickupTime
              }
            : {})
        };
        await apiRequest(`${API_URL}/api/orders/${order.id}/revisions/active/pickup-settings`, withDispatcherAccess({
          method: 'PUT',
          body: JSON.stringify(repeatPayload)
        }));
      } else {
        await apiRequest(`${API_URL}/api/orders/${order.id}`, withDispatcherAccess({
          method: 'PUT',
          body: JSON.stringify(payload)
        }));
      }
      showToast(
        'success',
        effectiveMode === 'self_delivery'
          ? (isRepeatOrder ? 'Повтор переведён на самодоставку' : 'Заказ переведён на самодоставку')
          : (isRepeatOrder
            ? (isCleaningRepeatOrder ? 'Параметры повтора обновлены' : 'Повторный забор обновлён')
            : 'Дата забора обновлена')
      );
      await loadOrder({ showLoading: false });
      await loadCommentsAndHistory();
      return true;
    } catch (error) {
      showToast('error', error.message);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const addItem = async (item, approvedCostChangeConfirmed = false) => {
    if (!canChangeOrderComposition) {
      showToast('error', 'Добавление предметов на этом этапе доступно только через Журнал заказов');
      return;
    }
    if (requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Заказ уже согласован или находится на финальном клиентском этапе. После изменения корзины итоговая стоимость для клиента будет пересчитана.',
        confirmLabel: 'Изменить',
        action: () => addItem(item, true)
      });
      return;
    }
    await runAction(`${API_URL}/api/orders/${order.id}/items`, 'Предмет добавлен', {
      body: JSON.stringify({
        order_id: order.id,
        service_id: item.service_id,
        item_name: item.name,
        quantity: item.quantity || 1,
        unit: item.unit,
        price: item.price || 0,
        regular_price: item.regular_price ?? item.price ?? 0,
        promotion_price: item.promotion_price ?? null,
        is_additional: false
      })
    });
    setShowItemSelector(false);
  };

  const addOrderService = async (service, approvedCostChangeConfirmed = false) => {
    if (!canEditOrderServices) {
      showToast('error', 'Добавление услуг на этом этапе доступно только через Журнал заказов');
      return;
    }
    if (requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Добавление услуги изменит итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Добавить',
        action: () => addOrderService(service, true)
      });
      return;
    }
    const nominalValue = Number(service.price || 0);
    const amount = service.unitType === 'percent' ? paymentTotals.base * nominalValue / 100 : nominalValue;
    await runAction(`${API_URL}/api/orders/${order.id}/items`, 'Услуга заказа добавлена', {
      body: JSON.stringify({
        order_id: order.id,
        service_id: service.service_id,
        item_name: service.name,
        quantity: service.quantity || 1,
        unit: service.unit || 'шт',
        price: amount,
        is_additional: true,
        additional_type: 'order',
        notes: service.unitType === 'percent' ? `+${nominalValue}%` : `+${money(nominalValue)}`
      }),
      silent: true
    });
  };

  const removeOrderService = async (serviceId, approvedCostChangeConfirmed = false) => {
    if (!canEditOrderServices) {
      showToast('error', 'Удаление услуг на этом этапе доступно только через Журнал заказов');
      return;
    }
    if (requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Удаление услуги изменит итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Удалить',
        action: () => removeOrderService(serviceId, true)
      });
      return;
    }
    const item = additionalItems.find((entry) => entry.additional_type === 'order' && entry.service_id === serviceId);
    if (item) {
      await runAction(`${API_URL}/api/orders/${order.id}/items/${item.id}`, 'Услуга заказа удалена', {
        method: 'DELETE',
        silent: true
      });
    }
  };

  const addDiscount = async (discount, approvedCostChangeConfirmed = false) => {
    if (!canEditDiscounts) {
      showToast('error', 'Добавление скидок на этом этапе доступно только через Журнал заказов');
      return;
    }
    if (discountItems.some((item) => Number(item.discount_id) === Number(discount.id))) return;
    if (discount.system_code === 'no_delivery') {
      const noDeliveries = discountOrderContext.selfDelivery && !discountOrderContext.hasPickupDelivery && !discountOrderContext.hasReturnDelivery;
      if (!noDeliveries) {
        showToast('error', 'Скидка «Заказ без доставок» доступна только если не было доставки от клиента и доставки до клиента.');
        return;
      }
    }
    if (discount.system_code === 'wholesale_order') {
      const minItems = Number(discount.min_items_count || 1);
      if (mainItemsCount < minItems) {
        showToast('error', `Скидка «Оптовый заказ» доступна от ${minItems} предметов. Сейчас в заказе: ${mainItemsCount}.`);
        return;
      }
    }
    const value = Number(discount.discount_value || 0);
    const amount = discount.discount_type === 'percent' ? paymentTotals.base * value / 100 : value;
    if (requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Добавление скидки изменит итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Добавить',
        action: () => addDiscount(discount, true)
      });
      return;
    }
    await runAction(`${API_URL}/api/orders/${order.id}/items`, 'Скидка добавлена', {
      body: JSON.stringify({
        order_id: order.id,
        item_name: discount.name,
        quantity: 1,
        unit: 'шт',
        price: -amount,
        is_additional: true,
        additional_type: 'discount',
        discount_id: discount.id,
        discount_snapshot: JSON.stringify({
          id: discount.id,
          name: discount.name,
          discount_type: discount.discount_type,
          discount_value: value,
          system_code: discount.system_code || null,
          template_type: discount.template_type || null,
          client_type: discount.client_type || (order.client_type || 'b2c'),
          scope: discount.scope || 'order',
          service_id: discount.service_id || null,
          group_id: discount.group_id || null,
          service_type: discount.service_type || null,
          allowed_roles: discount.allowed_roles || 'all',
          min_order_amount: discount.min_order_amount ?? null,
          min_items_count: discount.min_items_count ?? null,
          max_discount_amount: discount.max_discount_amount ?? null
        }),
        notes: discount.discount_type === 'percent' ? `-${value}%` : `-${money(value)}`
      }),
      silent: true
    });
  };

  const removeDiscount = async (discountId, options = {}) => {
    if (!canEditDiscounts) {
      showToast('error', 'Удаление скидок на этом этапе доступно только через Журнал заказов');
      return;
    }
    if (requiresApprovedCostChangeConfirm && !options.approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Удаление скидки изменит итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Удалить',
        action: () => removeDiscount(discountId, { ...options, approvedCostChangeConfirmed: true })
      });
      return;
    }
    const item = additionalItems.find((entry) => entry.additional_type === 'discount' && entry.discount_id === discountId);
    if (item) {
      const query = options.confirmSystemRemoval ? '?confirm_system_discount_removal=true' : '';
      const ok = await runAction(`${API_URL}/api/orders/${order.id}/items/${item.id}${query}`, 'Скидка удалена', {
        method: 'DELETE',
        silent: true
      });
      if (!ok) {
        throw new Error('Не удалось снять скидку');
      }
    }
  };

  const replaceItem = async (itemId, item, approvedCostChangeConfirmed = false) => {
    if (!canChangeOrderComposition) {
      showToast('error', 'Замена предметов на этом этапе доступна только через Журнал заказов');
      return;
    }
    if (requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Замена предмета изменит состав и может изменить итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Заменить',
        action: () => replaceItem(itemId, item, true)
      });
      return;
    }
    await runAction(`${API_URL}/api/orders/${order.id}/items/${itemId}`, 'Предмет заменён', {
      method: 'PUT',
      body: JSON.stringify({
        service_id: item.service_id,
        item_name: item.name,
        quantity: item.quantity || 1,
        unit: item.unit,
        price: item.price || 0,
        regular_price: item.regular_price ?? item.price ?? 0,
        promotion_price: item.promotion_price ?? null
      })
    });
    setShowItemSelector(false);
  };

  const deleteItem = async (itemId, options = {}) => {
    const item = (order?.items || []).find((entry) => entry.id === itemId);
    const isOrderServiceItem = item?.is_additional && item?.additional_type === 'order';
    const isDiscountItem = item?.is_additional && item?.additional_type === 'discount';

    if (isOrderServiceItem) {
      if (!canEditOrderServices) {
        showToast('error', 'Удаление услуг на этом этапе доступно только через Журнал заказов');
        return false;
      }
    } else if (isDiscountItem) {
      if (!canEditDiscounts) {
        showToast('error', 'Удаление скидок на этом этапе доступно только через Журнал заказов');
        return false;
      }
      if (!canEditAppliedDiscount(item)) {
        showToast('error', 'У вас нет прав для изменения этой скидки');
        return false;
      }
    } else if (!canDeleteCartItems) {
      showToast('error', 'Удаление предметов доступно только через Журнал заказов');
      return false;
    }

    if (requiresApprovedCostChangeConfirm && !options.approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Изменить согласованную стоимость?',
        text: 'Удаление позиции изменит состав и итоговую стоимость уже согласованного заказа.',
        confirmLabel: 'Удалить',
        action: () => deleteItem(itemId, { ...options, approvedCostChangeConfirmed: true })
      });
      return false;
    }

    const { silent = false, successMessage = 'Предмет удалён' } = options;
    await runAction(`${API_URL}/api/orders/${order.id}/items/${itemId}`, successMessage, {
      method: 'DELETE',
      silent
    });
    setDeleteCandidate(null);
  };

  const submitComment = async (draftText) => {
    if (!canAddComments) return;
    const text = String(draftText || '').trim();
    if (!text) return;
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text })
      });
      setShowCommentModal(false);
      showToast('success', 'Комментарий добавлен');
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const isUploadingPhotoRef = useRef(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadingPlaceholder, setUploadingPlaceholder] = useState(null);

  const uploadPhoto = async ({ file, itemId, caption }) => {
    if (!canAddPhotos || isUploadingPhotoRef.current || actionLoading) return;
    isUploadingPhotoRef.current = true;
    setActionLoading(true);
    setUploadProgress(0);

    const tempPreviewUrl = URL.createObjectURL(file);
    setUploadingPlaceholder({
      previewUrl: tempPreviewUrl,
      caption: caption?.trim() || file.name,
      progress: 0,
    });

    try {
      // На клиенте сжимаем только если файл превышает серверный лимит 11.5 МБ;
      // в остальных случаях сжатием (или сохранением оригинала) управляет сервер по настройкам.
      let preparedPhoto = file;
      if (file.size > 11.5 * 1024 * 1024) {
        preparedPhoto = await compressImageFile(file);
      }

      const formData = new FormData();
      formData.append('file', preparedPhoto);
      if (itemId) formData.append('item_id', String(itemId));
      if (caption?.trim()) formData.append('caption', caption.trim());

      await uploadProtectedFileWithProgress({
        url: `${API_URL}/api/orders/${order.id}/photos`,
        formData,
        onProgress: (percent) => {
          setUploadProgress(percent);
          setUploadingPlaceholder((prev) => (prev ? { ...prev, progress: percent } : null));
        },
      });
      setShowPhotoModal(false);
      showToast('success', 'Фото добавлено');
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      isUploadingPhotoRef.current = false;
      setActionLoading(false);
      setUploadProgress(null);
      setUploadingPlaceholder(null);
      URL.revokeObjectURL(tempPreviewUrl);
    }
  };

  const deletePhoto = async (photo) => {
    if (isLimitedArchiveAccess) return;
    setActionLoading(true);
    try {
      await apiRequest(`${API_URL}/api/orders/${order.id}/photos/${photo.id}`, {
        method: 'DELETE'
      });
      setSelectedPhoto(null);
      showToast('success', 'Фото удалено');
      await loadCommentsAndHistory();
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const saveItemDetails = async (updatedItem, approvedCostChangeConfirmed = false) => {
    const currentItem = mainItems.find((item) => item.id === updatedItem?.id);
    const canEditMeasurementsThisItem = !!(
      currentItem && (
        canChangeOrderComposition
        || (canOpenItemEditor && !['drying_packing', 'approval', 'delivery', 'handover', 'completed'].includes(order?.status) && canProcessSpecificItem(currentItem))
      )
    );
    const canEditServicesThisItem = !!(
      currentItem && (
        canChangeOrderComposition
        || (canOpenItemEditor && canProcessSpecificItem(currentItem))
      )
    );
    const canEditCellThisItem = !!(
      currentItem
      && currentItem?.processing_route === 'cleaning'
      && (
        (order?.status === 'drying_packing' && canProcessItems && canProcessSpecificItem(currentItem))
        || (['approval', 'delivery', 'handover'].includes(order?.status) && (hasEffectivePrivilegedAccess || canUseDispatcherCorrections))
      )
    );
    const canEditCurrentItem = canEditMeasurementsThisItem || canEditServicesThisItem || canEditCellThisItem;
    if (!canEditCurrentItem) {
      showToast('error', 'Редактирование предмета недоступно на данном этапе');
      return false;
    }

    if ((canEditMeasurementsThisItem || canEditServicesThisItem) && requiresApprovedCostChangeConfirm && !approvedCostChangeConfirmed) {
      setApprovedCostChangeConfirm({
        title: 'Пересчитать согласованную стоимость?',
        text: 'Изменение веса, площади или доп. услуг предмета пересчитает стоимость уже согласованного заказа.',
        confirmLabel: 'Сохранить и пересчитать',
        action: () => saveItemDetails(updatedItem, true)
      });
      return false;
    }

    const payload = {};
    if (canEditMeasurementsThisItem) {
      payload.weight = updatedItem.weight;
      payload.area = updatedItem.area;
      payload.length = updatedItem.length ?? null;
      payload.width = updatedItem.width ?? null;
    }
    if (canEditServicesThisItem) {
      payload.additional_service_ids = JSON.stringify((updatedItem.additionalServices || []).map((item) => item.id));
      payload.additional_services_snapshot = JSON.stringify((updatedItem.additionalServices || []).map((service) => ({
        id: service.id,
        name: service.name,
        price: service.current_price ?? service.base_price ?? service.price ?? 0,
        unit_type: service.current_unit_type || service.unitType || 'rub',
        unit: service.unit || 'шт',
        service_type: service.service_type || null,
        execution_credit_stage: service.execution_credit_stage || null
      })));
    }
    if (canEditCellThisItem && updatedItem.storage_cell_id !== undefined) {
      payload.storage_cell_id = updatedItem.storage_cell_id;
    }

    const saved = await runAction(`${API_URL}/api/orders/${order.id}/items/${updatedItem.id}`, 'Предмет обновлён', {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (saved) setSelectedItem(null);
    return saved;
  };

  const markItemCompleted = async (item) => {
    if (!canProcessItems || !canProcessSpecificItem(item) || !item?.id) {
      showToast('error', 'Отметить предмет выполненным может только исполнитель или сотрудник с включённым щитом диспетчера');
      return false;
    }
    const currentDryingStorage = getItemDryingStorage(item);
    if (order?.status === 'drying_packing' && item?.processing_route === 'cleaning' && storageCellsRequired && !currentDryingStorage) {
      haptic('open');
      setStorageCellSheet({ mode: 'complete-one', items: [item] });
      return false;
    }
    return runAction(`${API_URL}/api/orders/${order.id}/items/${item.id}/complete`, 'Предмет выполнен');
  };

  const completeItemWithStorageCell = async (assignments) => {
    const item = storageCellSheet?.items?.[0];
    if (!item?.id) return false;
    const storageCellId = assignments[String(item.id)];
    const completed = await runAction(
      `${API_URL}/api/orders/${order.id}/items/${item.id}/complete`,
      'Предмет выполнен и размещён в ячейке',
      { body: JSON.stringify({ storage_cell_id: storageCellId }) }
    );
    if (completed) setStorageCellSheet(null);
    return completed;
  };

  const saveItemStorageCell = async (assignments) => {
    const item = storageCellSheet?.items?.[0];
    if (!item?.id) return false;
    const storageCellId = assignments[String(item.id)];
    const saved = await runAction(
      `${API_URL}/api/orders/${order.id}/items/${item.id}/storage-cell`,
      'Ячейка предмета сохранена',
      {
        method: 'PUT',
        body: JSON.stringify({ storage_cell_id: storageCellId })
      }
    );
    if (saved) setStorageCellSheet(null);
    return saved;
  };

  const reopenItemProcessing = async (item) => {
    if (!canProcessItems || !canProcessSpecificItem(item) || !item?.id) {
      showToast('error', 'Вернуть предмет в работу может только исполнитель или сотрудник с включённым щитом диспетчера');
      return false;
    }
    return runAction(`${API_URL}/api/orders/${order.id}/items/${item.id}/reopen`, 'Предмет возвращён в работу');
  };

  const completeAllProcessingItems = async (assignments = {}) => {
    if (!canProcessItems) {
      showToast('error', 'Массовое завершение предметов доступно только исполнителю или через щит диспетчера');
      return false;
    }
    return runAction(
      `${API_URL}/api/orders/${order.id}/items/complete-all`,
      'Все предметы отмечены выполненными',
      {
        body: JSON.stringify({ storage_cells: assignments }),
        onSuccess: (response) => {
          if (response?.workflow?.permissions?.can_complete_stage) {
            setShowCompleteConfirm(true);
          }
        }
      }
    );
  };

  useEffect(() => {
    if (mapCoords) {
      setResolvedMapCoords(null);
      setIsResolvingMapCoords(false);
      return;
    }
    setResolvedMapCoords(null);
  }, [mapCoords?.lat, mapCoords?.lng, order?.id]);

  useEffect(() => {
    if (!isOrderMapExpanded || mapCoords || !yandexApiKey || !orderAddress.trim()) return undefined;

    const cacheKey = orderAddress.trim().toLowerCase();
    if (mapGeocodeCacheRef.current.has(cacheKey)) {
      const cached = mapGeocodeCacheRef.current.get(cacheKey);
      setResolvedMapCoords(cached);
      setIsResolvingMapCoords(false);
      return undefined;
    }

    let cancelled = false;

    const resolveMapCoords = async () => {
      setIsResolvingMapCoords(true);
      try {
        let resolved = null;
        if (yandexApiKey) {
          try {
            const ymaps = await loadYandexMaps(yandexApiKey);
            if (!cancelled) {
              const result = await ymaps.geocode(orderAddress, { results: 1 });
              const firstGeoObject = result?.geoObjects?.get?.(0);
              const coords = firstGeoObject?.geometry?.getCoordinates?.();

              if (Array.isArray(coords) && coords.length === 2 && coords.every((value) => Number.isFinite(Number(value)))) {
                const lat = Number(coords[0]);
                const lng = Number(coords[1]);
                if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                  resolved = { lat, lng };
                }
              }
            }
          } catch {}
        }

        if (!resolved && !cancelled) {
          try {
            const osmCoords = await geocodeAddressWithNominatim(orderAddress);
            if (osmCoords) {
              resolved = { lat: osmCoords.lat, lng: osmCoords.lng };
            }
          } catch {}
        }

        if (resolved) {
          mapGeocodeCacheRef.current.set(cacheKey, resolved);
          if (!cancelled) {
            setResolvedMapCoords(resolved);
          }
        } else {
          mapGeocodeCacheRef.current.set(cacheKey, null);
          if (!cancelled) {
            setResolvedMapCoords(null);
          }
        }
      } catch {
        mapGeocodeCacheRef.current.set(cacheKey, null);
        if (!cancelled) {
          setResolvedMapCoords(null);
        }
      } finally {
        if (!cancelled) {
          setIsResolvingMapCoords(false);
        }
      }
    };

    resolveMapCoords();

    return () => {
      cancelled = true;
    };
  }, [isOrderMapExpanded, mapCoords?.lat, mapCoords?.lng, orderAddress, yandexApiKey]);

  useEffect(() => {
    if (!order?.id || !resolvedMapCoords || mapCoords) return;

    const targetPrefix = order.status === 'delivery' ? 'delivery' : 'pickup';
    const persistKey = `${order.id}:${targetPrefix}:${resolvedMapCoords.lat}:${resolvedMapCoords.lng}`;
    if (persistedMapCoordsRef.current.has(persistKey)) return;
    persistedMapCoordsRef.current.add(persistKey);

    const payload = {
      stage_name: targetPrefix,
      latitude: resolvedMapCoords.lat,
      longitude: resolvedMapCoords.lng
    };

    let cancelled = false;

    const persistResolvedCoords = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/orders/${order.id}/route-coordinates`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        if (!response.ok) return;
        if (!cancelled) {
          setOrder((prev) => (prev ? {
            ...prev,
            ...(targetPrefix === 'delivery'
              ? { delivery_lat: resolvedMapCoords.lat, delivery_lng: resolvedMapCoords.lng }
              : { pickup_lat: resolvedMapCoords.lat, pickup_lng: resolvedMapCoords.lng })
          } : prev));
        }
      } catch (error) {
        console.error('Не удалось сохранить координаты заказа:', error);
      }
    };

    persistResolvedCoords();

    return () => {
      cancelled = true;
    };
  }, [mapCoords, order?.id, order?.status, resolvedMapCoords]);

  useEffect(() => {
    if (!orderMapHasBeenOpened || !hasCoords || !mapRef.current) {
      setIsInitializingOrderMap(false);
      return;
    }
    if (mapProvider === 'yandex' && !yandexApiKey) {
      setIsInitializingOrderMap(false);
      return;
    }
    let cancelled = false;
    let stopSuppressingOpenMapsButton = null;

    const initMap = async () => {
      setIsInitializingOrderMap(true);
      try {
        const coords = [effectiveMapCoords.lng, effectiveMapCoords.lat];

        if (mapProvider === 'osm') {
          if (mapInstanceRef.current) {
            if (mapEngineRef.current === 'leaflet') {
              mapInstanceRef.current.remove?.();
            } else {
              mapInstanceRef.current.destroy?.();
            }
            mapInstanceRef.current = null;
          }
          if (mapRef.current) {
            try {
              delete mapRef.current._leaflet_id;
            } catch {}
            mapRef.current.innerHTML = '';
            mapRef.current.className = mapRef.current.className
              .replace(/leaflet-[^\s]+/g, '')
              .trim();
          }
          const map = L.map(mapRef.current, {
            center: [effectiveMapCoords.lat, effectiveMapCoords.lng],
            zoom: ORDER_MAP_DEFAULT_ZOOM,
            zoomControl: false,
            attributionControl: false
          });
          const tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
          L.tileLayer(tileUrl, { maxZoom: 19, subdomains: ['a', 'b', 'c'] }).addTo(map);

          const isRevision = (order?.revisions || []).some((revision) => !getRevisionEndDate(revision));
          const markerColor = isRevision ? '#ea580c' : '#2563eb';
          const marker = L.marker([effectiveMapCoords.lat, effectiveMapCoords.lng], {
            icon: L.divIcon({
              className: 'leaflet-div-icon',
              html: `<div style="width:24px;height:24px;border-radius:999px;background:${markerColor};border:3px solid #ffffff;box-shadow:0 2px 8px rgba(0,0,0,0.3);transform:translate(-50%,-50%);"></div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0]
            })
          }).addTo(map);

          mapMarkerRef.current = marker;
          map.on('moveend zoomend', () => {
            const c = map.getCenter();
            const z = map.getZoom();
            mapLocationRef.current = {
              center: [c.lng, c.lat],
              zoom: z,
              azimuth: 0,
              tilt: 0
            };
          });

          mapLocationRef.current = {
            center: [effectiveMapCoords.lng, effectiveMapCoords.lat],
            zoom: ORDER_MAP_DEFAULT_ZOOM,
            azimuth: 0,
            tilt: 0
          };
          mapEngineRef.current = 'leaflet';
          setIsOrderMap3DSupported(false);
          setIsOrderMapTilted(false);
          mapInstanceRef.current = map;
          setTimeout(() => {
            if (!cancelled && mapInstanceRef.current && mapEngineRef.current === 'leaflet') {
              mapInstanceRef.current.invalidateSize?.({ animate: false });
            }
          }, 50);
          syncCurrentLocationMarker();
          return;
        }

        if (!forceLegacyOrderMapRef.current) {
          try {
            const { ymaps3 } = await loadYandexMapsV3(yandexApiKey);
            const { YMapDefaultMarker } = await ymaps3.import('@yandex/ymaps3-default-ui-theme');
            if (cancelled || !mapRef.current) return;

            if (mapInstanceRef.current) {
              if (mapEngineRef.current === 'leaflet') {
                mapInstanceRef.current.remove?.();
              } else {
                mapInstanceRef.current.destroy?.();
              }
              mapInstanceRef.current = null;
            }
            if (mapRef.current) {
              mapRef.current.innerHTML = '';
              mapRef.current.className = mapRef.current.className
                .replace(/leaflet-[^\s]+/g, '')
                .trim();
            }

            const map = new ymaps3.YMap(mapRef.current, {
              location: {
                center: coords,
                zoom: ORDER_MAP_DEFAULT_ZOOM
              },
              copyrightsPosition: 'bottom right',
              distribution: false,
              distributionPosition: 'bottom right',
              mode: 'vector',
              behaviors: ['drag', 'scrollZoom', 'pinchZoom', 'pinchRotate', 'dblClick', 'magnifier', 'mouseTilt']
            });

            map.addChild(new ymaps3.YMapDefaultSchemeLayer({
              theme: isDarkTheme ? 'dark' : 'light'
            }));
            map.addChild(new ymaps3.YMapDefaultFeaturesLayer({
              zIndex: 1800
            }));

            const listener = new ymaps3.YMapListener({
              layer: 'any',
              onUpdate: ({ location, camera }) => {
                if (!location?.center) return;
                mapLocationRef.current = {
                  center: location.center,
                  zoom: Number.isFinite(location.zoom) ? location.zoom : mapLocationRef.current.zoom,
                  azimuth: Number.isFinite(camera?.azimuth) ? camera.azimuth : mapLocationRef.current.azimuth,
                  tilt: Number.isFinite(camera?.tilt) ? camera.tilt : mapLocationRef.current.tilt
                };
                setIsOrderMapTilted((camera?.tilt || 0) > 0.05);
              }
            });
            map.addChild(listener);

            const marker = new YMapDefaultMarker({
              coordinates: coords,
              color: (order?.revisions || []).some((revision) => !getRevisionEndDate(revision)) ? 'orange' : 'blue',
              size: 'normal',
              iconName: 'fallback'
            });

            mapMarkerRef.current = marker;
            map.addChild(marker);
            mapLocationRef.current = {
              center: coords,
              zoom: ORDER_MAP_DEFAULT_ZOOM,
              azimuth: 0,
              tilt: 0
            };
            mapEngineRef.current = 'v3';
            stopSuppressingOpenMapsButton = suppressYandexOpenMapsButton(mapRef.current);
            setIsOrderMap3DSupported(true);
            setIsOrderMapTilted(false);
            mapInstanceRef.current = map;
            syncCurrentLocationMarker();
            return;
          } catch (v3Error) {
            forceLegacyOrderMapRef.current = true;
            console.warn('Яндекс.Карты v3 недоступны, используем fallback на v2:', v3Error);
          }
        }

        const ymaps = await loadYandexMaps(yandexApiKey);
        if (cancelled || !mapRef.current) return;

        if (mapInstanceRef.current) {
          if (mapEngineRef.current === 'leaflet') {
            mapInstanceRef.current.remove?.();
          } else {
            mapInstanceRef.current.destroy?.();
          }
          mapInstanceRef.current = null;
        }
        if (mapRef.current) {
          mapRef.current.innerHTML = '';
          mapRef.current.className = mapRef.current.className
            .replace(/leaflet-[^\s]+/g, '')
            .trim();
        }

        const legacyCoords = [effectiveMapCoords.lat, effectiveMapCoords.lng];
        const map = new ymaps.Map(mapRef.current, {
          center: legacyCoords,
          zoom: ORDER_MAP_DEFAULT_ZOOM,
          controls: []
        }, {
          suppressMapOpenBlock: true
        });

        const marker = new ymaps.Placemark(legacyCoords, {}, {
          preset: (order?.revisions || []).some((revision) => !getRevisionEndDate(revision))
            ? 'islands#orangeDotIcon'
            : 'islands#blueDotIcon'
        });

        map.geoObjects.add(marker);
        map.events.add('boundschange', () => {
          const center = map.getCenter?.();
          const zoom = map.getZoom?.();
          if (!center) return;
          mapLocationRef.current = {
            center: [Number(center[1]), Number(center[0])],
            zoom: Number.isFinite(zoom) ? zoom : mapLocationRef.current.zoom,
            azimuth: 0,
            tilt: 0
          };
        });

        mapMarkerRef.current = marker;
        mapLocationRef.current = {
          center: coords,
          zoom: ORDER_MAP_DEFAULT_ZOOM,
          azimuth: 0,
          tilt: 0
        };
        mapEngineRef.current = 'v2';
        setIsOrderMap3DSupported(false);
        setIsOrderMapTilted(false);
        mapInstanceRef.current = map;
        syncCurrentLocationMarker();
      } catch (error) {
        console.error('Ошибка инициализации карты:', error);
        showToast('error', error.message);
      } finally {
        if (!cancelled) {
          setIsInitializingOrderMap(false);
        }
      }
    };

    initMap();

    return () => {
      cancelled = true;
      stopSuppressingOpenMapsButton?.();
      if (mapInstanceRef.current) {
        try {
          if (typeof mapInstanceRef.current.remove === 'function') {
            mapInstanceRef.current.remove();
          } else if (typeof mapInstanceRef.current.destroy === 'function') {
            mapInstanceRef.current.destroy();
          }
        } catch {}
        mapInstanceRef.current = null;
      }
      if (mapRef.current) {
        try {
          delete mapRef.current._leaflet_id;
        } catch {}
      }
      mapMarkerRef.current = null;
      currentLocationMarkerRef.current = null;
    };
  }, [orderMapHasBeenOpened, mapProvider, yandexApiKey, hasCoords, effectiveMapCoords?.lat, effectiveMapCoords?.lng, isDarkTheme, order?.revisions, syncCurrentLocationMarker]);

  useEffect(() => {
    if (!isOrderMapExpanded || !mapInstanceRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (!mapInstanceRef.current || !mapLocationRef.current.center) return;
      if (mapEngineRef.current === 'v3') {
        mapInstanceRef.current.update({
          location: {
            center: mapLocationRef.current.center,
            zoom: mapLocationRef.current.zoom
          },
          camera: {
            azimuth: mapLocationRef.current.azimuth,
            tilt: mapLocationRef.current.tilt
          }
        });
        return;
      }
      if (mapEngineRef.current === 'leaflet') {
        mapInstanceRef.current.invalidateSize?.();
        return;
      }
      mapInstanceRef.current.container?.fitToViewport?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOrderMapExpanded]);

  if (loading) {
    return <OrderDetailSkeleton />;
  }

  if (!order) {
    const isOfflineUnavailable = loadError?.kind === 'offline_unavailable';
    const EmptyIcon = isOfflineUnavailable ? AlertTriangle : Package;
    return (
      <div className={`rounded-lg border bg-white p-8 text-center shadow-sm dark:bg-gray-900 ${
        isOfflineUnavailable
          ? 'border-amber-200 dark:border-amber-900'
          : 'border-transparent dark:border-gray-800'
      }`}>
        <EmptyIcon className={`mx-auto mb-4 h-12 w-12 ${
          isOfflineUnavailable ? 'text-amber-500 dark:text-amber-300' : 'text-gray-400'
        }`} />
        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {loadError?.title || 'Заказ не найден'}
        </p>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-5 text-gray-600 dark:text-gray-400">
          {loadError?.message || 'Проверьте ссылку или попробуйте открыть заказ позже.'}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => loadOrder({ showLoading: true })}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Обновить</span>
          </button>
          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate('/my-orders', { replace: true })}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-gray-100 px-4 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Мои заказы
            </button>
          )}
        </div>
      </div>
    );
  }

  const completeLabel = isFinalPaymentStage ? 'Закрыть заказ' : 'Завершить этап';
  const completeConfirmText = canUseDispatcherCorrections
    ? (
      isWashingFlowOrder
        ? `${isFinalPaymentStage ? 'Заказ будет закрыт диспетчером.' : 'Этап будет завершён диспетчером.'} В статистике по предметам сохранятся исполнители потоков: ${washingFlowStatsSummaryText || 'исполнители не назначены'}.`
        : `${isFinalPaymentStage ? 'Заказ будет закрыт диспетчером.' : 'Этап будет завершён диспетчером.'} В статистику выполнение пойдёт назначенному исполнителю: ${order.assignee_name || 'исполнитель не назначен'}.`
    )
    : isFinalPaymentStage
      ? 'После подтверждения заказ будет закрыт.'
      : 'После подтверждения этап будет завершён.';

  const toggleOrderMapFullscreen = async () => {
    try {
      if (document.fullscreenElement === mapWrapperRef.current) {
        haptic('close');
        await document.exitFullscreen?.();
      } else {
        haptic('open');
        await mapWrapperRef.current?.requestFullscreen?.();
      }
    } catch (error) {
      console.error('Ошибка полноэкранного режима карты заказа:', error);
    }
  };

  const toggleOrderMapDimension = () => {
    if (!isOrderMap3DSupported || !mapInstanceRef.current || !mapLocationRef.current.center) return;
    const nextTilted = !isOrderMapTilted;
    haptic('tap');
    mapInstanceRef.current.update({
      location: {
        center: mapLocationRef.current.center,
        zoom: mapLocationRef.current.zoom
      },
      camera: {
        azimuth: mapLocationRef.current.azimuth || 0,
        tilt: nextTilted ? ORDER_MAP_3D_TILT : 0
      }
    });
    mapLocationRef.current = {
      ...mapLocationRef.current,
      azimuth: mapLocationRef.current.azimuth || 0,
      tilt: nextTilted ? ORDER_MAP_3D_TILT : 0
    };
    setIsOrderMapTilted(nextTilted);
  };

  const openOrderMapConfirm = (url, label) => {
    if (!url || url === '#') return;
    haptic('open');
    setMapConfirmModal({
      url,
      label,
      address: orderAddress
    });
  };

  const centerOrderMapOnPoint = () => {
    if (!mapInstanceRef.current || !effectiveMapCoords) return;
    haptic('tap');
    const center = [effectiveMapCoords.lng, effectiveMapCoords.lat];
    if (mapEngineRef.current === 'leaflet') {
      mapInstanceRef.current.setView(
        [center[1], center[0]],
        mapLocationRef.current.zoom || ORDER_MAP_DEFAULT_ZOOM,
        { animate: true }
      );
      return;
    }
    if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.setLocation({
        center,
        zoom: mapLocationRef.current.zoom || ORDER_MAP_DEFAULT_ZOOM,
        duration: 180
      });
      return;
    }

    mapInstanceRef.current.setCenter?.(
      [center[1], center[0]],
      mapLocationRef.current.zoom || ORDER_MAP_DEFAULT_ZOOM,
      { duration: 180 }
    );
  };

  const centerOrderMapOnCurrentLocation = (coordinates) => {
    if (!mapInstanceRef.current) return;
    currentLocationCoordsRef.current = coordinates;
    syncCurrentLocationMarker(coordinates);
    if (mapEngineRef.current === 'leaflet') {
      mapInstanceRef.current.setView(
        [coordinates[1], coordinates[0]],
        CURRENT_LOCATION_MAP_ZOOM,
        { animate: true }
      );
    } else if (mapEngineRef.current === 'v3') {
      mapInstanceRef.current.setLocation({
        center: coordinates,
        zoom: CURRENT_LOCATION_MAP_ZOOM,
        duration: 250
      });
    } else {
      mapInstanceRef.current.setCenter?.(
        [coordinates[1], coordinates[0]],
        CURRENT_LOCATION_MAP_ZOOM,
        { duration: 250 }
      );
    }
    mapLocationRef.current = {
      ...mapLocationRef.current,
      center: coordinates,
      zoom: CURRENT_LOCATION_MAP_ZOOM
    };
  };

  const showOrderMapLocationError = (message) => {
    showToast('error', message);
  };

  const toggleOrderMapDisclosure = () => {
    const nextExpanded = !isOrderMapExpanded;
    haptic(nextExpanded ? 'open' : 'close');
    if (nextExpanded) setOrderMapEverOpenedKey(orderMapDisclosureKey);
    setOrderMapDisclosure({ key: orderMapDisclosureKey, expanded: nextExpanded });
  };

    return (
    <div className="flex min-h-full flex-col gap-4 desktop-content-container-wide">
      <section className={`-mx-4 -mt-4 lg:mx-0 lg:mt-0 lg:rounded-2xl overflow-hidden rounded-b-lg border-b bg-white shadow-sm dark:bg-gray-900 ${pageAccent.border}`}>
        {!isOrderMapExpanded && !isOrderMapFullscreen ? (
          <button
            type="button"
            onClick={toggleOrderMapDisclosure}
            aria-expanded={isOrderMapExpanded}
            aria-controls="order-address-map-panel"
            className="group flex w-full items-center justify-between gap-3 bg-gradient-to-r from-white via-white to-gray-50/80 px-4 py-3 text-left transition-colors hover:from-gray-50 hover:to-gray-100/80 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800/70 dark:hover:from-gray-800 dark:hover:to-gray-800"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${accent.soft} ${accent.border} ${accent.text}`}>
                <MapPin className="h-4 w-4" />
              </span>
              <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Карта заказа</span>
            </span>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/80 text-gray-500 shadow-sm transition-colors group-hover:text-gray-700 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-400 dark:group-hover:text-gray-200">
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
        ) : null}
        {(isOrderMapExpanded || orderMapHasBeenOpened) ? (hasCoords && (yandexApiKey || mapProvider === 'osm') ? (
          <div ref={mapWrapperRef} className={`relative ${!isOrderMapExpanded ? 'hidden' : ''} ${isOrderMapFullscreen ? 'h-screen bg-gray-100 p-3 dark:bg-slate-900' : ''}`}>
            <div
              className={`relative isolate z-0 w-full overflow-hidden bg-gray-100 ${isOrderMapFullscreen ? 'h-[calc(100dvh-24px)] rounded-2xl' : 'h-[210px] dark:bg-gray-800 sm:h-[240px]'}`}
            >
              <div
                id="order-address-map-panel"
                ref={mapRef}
                className="order-detail-map h-full w-full"
              />
            </div>
            {isInitializingOrderMap ? (
              <MapLoadingSkeleton className={isOrderMapFullscreen ? 'rounded-2xl' : ''} />
            ) : null}
            {isOrderMapFullscreen ? (
              <>
                <div className="pointer-events-none absolute left-3 top-3 sm:left-5 sm:top-5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto">
                    <MapProviderToggle
                      className="h-11 w-11"
                      provider={mapProvider}
                      onChange={(nextProvider) => {
                        if (mapInstanceRef.current) {
                          if (mapEngineRef.current === 'leaflet') {
                            mapInstanceRef.current.remove?.();
                          } else {
                            mapInstanceRef.current.destroy?.();
                          }
                          mapInstanceRef.current = null;
                        }
                        if (mapRef.current) {
                          mapRef.current.innerHTML = '';
                          mapRef.current.className = mapRef.current.className
                            .replace(/leaflet-[^\s]+/g, '')
                            .trim();
                        }
                        setMapProvider(nextProvider);
                      }}
                    />
                  </div>
                </div>

                <div className="pointer-events-none absolute left-3 bottom-3 sm:left-5 sm:bottom-5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openOrderMapConfirm(orderMapsUrl, 'Яндекс.Карты')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                      title="Открыть в Яндекс.Картах"
                      aria-label="Открыть в Яндекс.Картах"
                    >
                      <img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openOrderMapConfirm(orderNavigatorUrl, 'Яндекс.Навигатор')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                      title="Открыть в Яндекс.Навигаторе"
                      aria-label="Открыть в Яндекс.Навигаторе"
                    >
                      <img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" />
                    </button>
                    {orderDgisUrl && orderDgisUrl !== '#' && (
                      <button
                        type="button"
                        onClick={() => openOrderMapConfirm(orderDgisUrl, '2ГИС')}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                        title="Открыть в 2ГИС"
                        aria-label="Открыть в 2ГИС"
                      >
                        <img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="pointer-events-none absolute right-3 top-3 sm:right-5 sm:top-5 z-20 flex flex-col items-end gap-2">
                  <div className="pointer-events-auto flex flex-col items-end gap-2">
                    <button
                      type="button"
                      onClick={toggleOrderMapFullscreen}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                      title="Свернуть карту"
                      aria-label="Свернуть карту"
                    >
                      <Minimize2 className="h-4 w-4" />
                    </button>

                    <button
                      type="button"
                      onClick={centerOrderMapOnPoint}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                      title="Центрировать на точке"
                      aria-label="Центрировать на точке"
                    >
                      <LocateFixed className="h-4 w-4" />
                    </button>

                    <MapCurrentLocationButton
                      onLocated={centerOrderMapOnCurrentLocation}
                      onError={showOrderMapLocationError}
                    />

                    <button
                      type="button"
                      onClick={toggleOrderMapDimension}
                      disabled={!isOrderMap3DSupported}
                      className={`inline-flex h-10 min-w-[48px] items-center justify-center rounded-2xl border border-gray-200 bg-white/95 px-2.5 text-xs font-semibold text-gray-700 shadow-sm backdrop-blur transition-colors dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 ${isOrderMap3DSupported ? 'hover:bg-white dark:hover:bg-gray-900' : 'cursor-not-allowed opacity-50'}`}
                      title={isOrderMap3DSupported ? (isOrderMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                      aria-label={isOrderMap3DSupported ? (isOrderMapTilted ? 'Переключить в 2D' : 'Переключить в 3D') : '3D недоступен для текущего ключа Яндекс.Карт'}
                    >
                      {isOrderMapTilted ? '2D' : '3D'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="pointer-events-none absolute left-2.5 bottom-2.5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openOrderMapConfirm(orderMapsUrl, 'Яндекс.Карты')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                      title="Открыть в Яндекс.Картах"
                      aria-label="Открыть в Яндекс.Картах"
                    >
                      <img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openOrderMapConfirm(orderNavigatorUrl, 'Яндекс.Навигатор')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                      title="Открыть в Яндекс.Навигаторе"
                      aria-label="Открыть в Яндекс.Навигаторе"
                    >
                      <img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" />
                    </button>
                    {orderDgisUrl && orderDgisUrl !== '#' && (
                      <button
                        type="button"
                        onClick={() => openOrderMapConfirm(orderDgisUrl, '2ГИС')}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-800 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-100 dark:hover:bg-gray-900"
                        title="Открыть в 2ГИС"
                        aria-label="Открыть в 2ГИС"
                      >
                        <img src={dgisIcon} alt="2ГИС" className="h-5 w-5 object-contain" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="pointer-events-none absolute left-2.5 top-2.5 z-20 flex items-center gap-2">
                  <div className="pointer-events-auto">
                    <MapProviderToggle
                      provider={mapProvider}
                      onChange={(nextProvider) => {
                        if (mapInstanceRef.current) {
                          if (mapEngineRef.current === 'leaflet') {
                            mapInstanceRef.current.remove?.();
                          } else {
                            mapInstanceRef.current.destroy?.();
                          }
                          mapInstanceRef.current = null;
                        }
                        if (mapRef.current) {
                          mapRef.current.innerHTML = '';
                          mapRef.current.className = mapRef.current.className
                            .replace(/leaflet-[^\s]+/g, '')
                            .trim();
                        }
                        setMapProvider(nextProvider);
                      }}
                    />
                  </div>
                </div>

                <div className="pointer-events-none absolute right-2.5 top-2.5 z-20 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={toggleOrderMapFullscreen}
                    className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                    title="Развернуть карту"
                    aria-label="Развернуть карту"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={centerOrderMapOnPoint}
                    className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-gray-700 shadow-sm backdrop-blur transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-gray-900"
                    title="Центрировать на точке"
                    aria-label="Центрировать на точке"
                  >
                    <LocateFixed className="h-4 w-4" />
                  </button>
                  <MapCurrentLocationButton
                    onLocated={centerOrderMapOnCurrentLocation}
                    onError={showOrderMapLocationError}
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleOrderMapDisclosure}
                  className="pointer-events-auto cursor-pointer touch-manipulation absolute bottom-0 left-1/2 z-30 flex h-6 min-w-[44px] -translate-x-1/2 translate-y-[17%] items-center justify-center rounded-full border border-gray-200 bg-white/90 px-3 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                  title="Свернуть карту"
                  aria-label="Свернуть карту"
                >
                  <ChevronDown className="h-4 w-4 rotate-180 pointer-events-none" />
                </button>
              </>
            )}
          </div>
        ) : (
          <div id="order-address-map-panel" className={`relative flex h-[190px] flex-col items-center justify-center bg-gray-100 px-4 text-center dark:bg-gray-800 sm:h-[220px] ${!isOrderMapExpanded ? 'hidden' : ''}`}>
            {isResolvingMapCoords ? (
              <MapLoadingSkeleton />
            ) : (
              <>
                <MapPin className="mb-3 h-10 w-10 text-gray-400" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {yandexApiKey ? 'Нет координат для карты' : 'Ключ Яндекс.Карт не настроен'}
                </p>
              </>
            )}
            {isResolvingMapCoords ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Это может занять пару секунд
              </p>
            ) : null}
            <button
              type="button"
              onClick={toggleOrderMapDisclosure}
              className="pointer-events-auto cursor-pointer touch-manipulation absolute bottom-0 left-1/2 z-30 flex h-6 min-w-[44px] -translate-x-1/2 translate-y-[17%] items-center justify-center rounded-full border border-gray-200 bg-white/90 px-3 text-gray-600 shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
              title="Свернуть карту"
              aria-label="Свернуть карту"
            >
              <ChevronDown className="h-4 w-4 rotate-180 pointer-events-none" />
            </button>
          </div>
        )) : null}
        <div className={`grid border-t text-sm lg:grid-cols-2 ${pageAccent.border}`}>
          <div className={`px-4 py-2.5 lg:border-r ${pageAccent.border}`}>
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className={`inline-flex h-7 max-w-full items-center truncate rounded-full border px-2.5 text-[11px] font-semibold leading-none ${lifecycleStatus.className}`}>
                    {lifecycleStatus.label}
                  </span>
                  <span className={`inline-flex h-7 max-w-full items-center gap-1.5 truncate rounded-full border px-2.5 text-[11px] font-semibold leading-none ${accent.soft} ${accent.border} ${accent.text}`}>
                    <StageIcon className="h-3.5 w-3.5 shrink-0" />
                    {stageConfig.label}
                  </span>
                </div>
                {canShowProductionForecast && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('open');
                      setShowProductionForecastModal(true);
                    }}
                    className="order-header-badge-button relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-indigo-200 bg-indigo-50 p-0 text-indigo-700 shadow-sm transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300 dark:hover:bg-indigo-950/70 dark:focus:ring-offset-gray-900"
                    title="Ориентировочные сроки"
                    aria-label="Открыть прогноз готовности и доставки"
                  >
                    <CalendarClock className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_minmax(132px,auto)] items-start gap-x-3 gap-y-1.5">
                <div className="space-y-1.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 truncate text-[13px] font-normal text-gray-700 dark:text-gray-200">
                      {order.department_name || 'Подразделение не выбрано'}
                    </span>
                  </div>
                  {order?.status === 'drying_packing' && order?.active_rework && activeReworkFlowSummaries.length > 0 ? (
                    <div className="min-w-0 space-y-1">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <Package className="h-3.5 w-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
                        <span className="min-w-0 truncate text-[11px] font-normal text-gray-700 dark:text-gray-200">
                          {primaryAssigneeName || 'Сушка: не назначен'}
                        </span>
                      </div>
                      {activeReworkFlowSummaries.map((flow) => {
                        const FlowIcon = flow.icon;
                        return (
                          <div key={flow.stage_name} className="flex min-w-0 items-center gap-1.5">
                            <span className="relative flex shrink-0 items-center">
                              <FlowIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                              <RotateCcw className="absolute -bottom-0.5 -right-1 h-2 w-2 text-amber-600 dark:text-amber-400" />
                            </span>
                            <span className="min-w-0 truncate text-[11px] font-normal text-amber-800 dark:text-amber-300">
                              {flow.label}: {flow.assignee_name || 'Не назначен'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : hasMultipleWashingFlowAssigneeSummary ? (
                    <div className="min-w-0 space-y-1">
                      {washingFlowAssigneeSummaries.map((flow) => {
                        const FlowIcon = flow.icon;
                        return (
                          <div key={flow.stage_name} className="flex min-w-0 items-center gap-1.5">
                            <FlowIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="min-w-0 truncate text-[11px] font-normal text-gray-700 dark:text-gray-200">
                              {flow.assignee_name || 'Не назначен'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : hasSingleWashingFlowAssigneeSummary ? (
                    <div className="flex min-w-0 items-start gap-2">
                      {(() => {
                        const singleFlow = washingFlowAssigneeSummaries[0];
                        const SingleFlowIcon = singleFlow?.icon || UserCheck;
                        return <SingleFlowIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />;
                      })()}
                      <span className="min-w-0 truncate text-[11px] font-normal text-gray-700 dark:text-gray-200">
                        {primaryAssigneeName}
                      </span>
                    </div>
                  ) : (
                    <div className="flex min-w-0 items-start gap-2">
                      <UserCheck className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                      <span className="min-w-0 truncate text-[11px] font-normal text-gray-700 dark:text-gray-200">
                        {primaryAssigneeName}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-1 min-w-[132px] text-right">
                  <div className="flex min-w-0 items-center justify-end gap-2">
                    <StageSummaryPrimaryIcon className={`h-4 w-4 shrink-0 ${stageSummaryMeta.primaryIconClassName}`} />
                    <span className={`min-w-0 truncate text-[12px] font-medium ${stageSummaryMeta.primaryClassName}`}>
                      {stageSummaryMeta.primary}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center justify-end gap-2">
                    <StageSummarySecondaryIcon className={`h-4 w-4 shrink-0 ${stageSummaryMeta.secondaryIconClassName}`} />
                    <span className={`min-w-0 truncate text-[11px] ${stageSummaryMeta.secondaryClassName}`}>
                      {stageSummaryMeta.secondary}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 px-4 py-2.5 dark:border-gray-800 lg:border-t-0">
            <div className="grid gap-y-1.5">
              <div className="grid grid-cols-[16px_minmax(0,1fr)] items-center gap-x-2">
                <ClientIcon className={`h-4 w-4 shrink-0 ${pageAccent.text}`} />
                <span className="min-w-0 truncate text-[13px] font-medium text-gray-900 dark:text-gray-100">
                  {isCompanyClient ? (order.client_name || 'Организация не указана') : clientTypeLabel}
                </span>
              </div>
              <div className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-x-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                {orderAddress ? (
                  <span className="min-w-0 text-[13px] font-normal leading-[1.25] text-gray-700 dark:text-gray-200">
                    {orderAddress}
                  </span>
                ) : (
                  <span className="min-w-0 text-[13px] font-normal leading-[1.25] text-gray-700 dark:text-gray-200">Адрес не указан</span>
                )}
              </div>
              {phoneHref ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    haptic('open');
                    setPhoneActionModal({
                      phone: formattedPhone || order?.client_phone || 'Телефон не указан',
                      href: phoneHref
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      haptic('open');
                      setPhoneActionModal({
                        phone: formattedPhone || order?.client_phone || 'Телефон не указан',
                        href: phoneHref
                      });
                    }
                  }}
                  className="grid cursor-pointer grid-cols-[16px_minmax(0,1fr)] items-start gap-x-2"
                >
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                  <span className={`min-w-0 text-[13px] font-medium leading-[1.25] ${pageAccent.text}`}>
                    {formattedPhone}
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-x-2">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                  <span className="text-[13px] font-normal leading-[1.25] text-gray-700 dark:text-gray-200">Телефон не указан</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {isDeferredOrder && order.deferred_event && (
        <section className="-mx-4 border-y border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/70 dark:bg-amber-950/25 dark:text-amber-100 sm:mx-0 sm:rounded-lg sm:border">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white">
              <Clock className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-semibold">Задание отложено</span>
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {formatTimezoneDate(order.deferred_event.deferred_at, true)}
                </span>
              </div>
              <div className="mt-1 font-medium text-amber-900 dark:text-amber-100">
                {order.deferred_event.reason_label}
              </div>
              <div className="mt-0.5 leading-5 text-amber-800 dark:text-amber-200">
                {order.deferred_event.comment}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-700 dark:text-amber-300">
                <span>Отложил: {order.deferred_event.deferred_by_name || 'не указано'}</span>
                <span>Было у: {order.deferred_event.previous_assignee_name || 'не указано'}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {order.status === 'drying_packing' && isMixedProcessingFlowOrder && (
        <section className="-mx-4 border-y border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-100 sm:mx-0 sm:rounded-lg sm:border">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
            <div>
              <div className="font-semibold">Совмещённый заказ</div>
              <p className="mt-0.5 leading-5 text-amber-800 dark:text-amber-200">
                В заказе есть предметы двух потоков: «Прачечная» и «Чистка».
              </p>
            </div>
          </div>
        </section>
      )}

      {order.status === 'drying_packing' && order.active_rework && (
        <section className="-mx-4 border-y border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-100 sm:mx-0 sm:rounded-lg sm:border">
          <div className="flex items-start gap-2">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <div className="font-semibold">Предметы на перестире</div>
              <p className="mt-0.5 leading-5 text-amber-800 dark:text-amber-200">
                Часть предметов возвращена на доработку в поток стирки/чистки. Завершить сушку и упаковку можно будет после их возвращения.
              </p>
            </div>
          </div>
        </section>
      )}

      {hasExtraInfo && (
        <section className="-mx-4 border-y border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-gray-800 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-gray-100 sm:mx-0 sm:rounded-lg sm:border">
          <div className="mb-1 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 font-medium text-blue-700 dark:text-blue-300">
              <Info className="h-4 w-4 shrink-0" />
              Доп. информация
            </div>
            {extraInfoUpdatedAt && (
              <span className="shrink-0 text-[11px] font-normal text-blue-600/80 dark:text-blue-300/80">
                {formatTimezoneDate(extraInfoUpdatedAt, true).slice(0, -3)}
              </span>
            )}
          </div>
          <div className="space-y-1 leading-5">
            {extraInfoParts.map((item, index) => (
              <p key={index} className="whitespace-pre-wrap break-all">
                {item}
              </p>
            ))}
          </div>
        </section>
      )}

      <div className="space-y-1">
        {canShowDispatcherAccordion && (
          <AccordionSection
            title="Диспетчерская"
            icon={Shield}
            className="order-0"
            open={expandedSections.dispatcher}
            onToggle={() => toggleSection('dispatcher')}
          >
            {() => (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic(dispatcherModeEnabled ? 'close' : 'open');
                  setDispatcherModeEnabled((prev) => !prev);
                }}
                disabled={actionLoading}
                title={dispatcherModeEnabled ? 'Выключить диспетчерский доступ' : 'Включить диспетчерский доступ'}
                aria-label={dispatcherModeEnabled ? 'Выключить диспетчерский доступ' : 'Включить диспетчерский доступ'}
                className={`${DISPATCHER_ICON_BUTTON_BASE} ${
                  dispatcherModeEnabled
                    ? 'border-violet-300 bg-violet-100 text-violet-700 hover:bg-violet-200 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/60'
                    : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Shield className="h-4.5 w-4.5" />
              </button>

              <button
                type="button"
                onClick={toggleEmergencyDispatch}
                disabled={actionLoading}
                aria-disabled={!canUseEmergencyDispatch}
                title={canUseEmergencyDispatch ? (isEmergencyDispatch ? 'Снять экстренный выезд' : 'Экстренный выезд') : emergencyDispatchUnavailableReason}
                aria-label="Экстренный выезд"
                className={`${DISPATCHER_ICON_BUTTON_BASE} ${isEmergencyDispatch ? 'border-fuchsia-300 bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200 dark:border-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-300' : canUseEmergencyDispatch ? 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 dark:border-fuchsia-900 dark:bg-fuchsia-950/30 dark:text-fuchsia-300' : 'border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500'}`}
              >
                <Siren className="h-4.5 w-4.5" />
              </button>

              {canOpenDispatcherScheduleSettings && (
                <button
                  type="button"
                  onClick={() => {
                    if (!canUseDispatcherScheduleSettings) {
                      showToast('info', dispatcherScheduleUnavailableReason);
                      return;
                    }
                    haptic('open');
                    setShowDispatcherReturnModal(true);
                  }}
                  disabled={actionLoading}
                  aria-disabled={!canUseDispatcherScheduleSettings}
                  title={
                    !canUseDispatcherScheduleSettings
                      ? dispatcherScheduleUnavailableReason
                      : isPickupStage
                      ? (isCleaningRepeatOrder ? 'Изменить дату и интервал выезда' : 'Изменить самодоставку или дату забора')
                      : isHandoverStage
                      ? 'Изменить способ возврата или назначить доставку'
                      : 'Изменить дату доставки или перевести на выдачу'
                  }
                  aria-label={
                    !canUseDispatcherScheduleSettings
                      ? dispatcherScheduleUnavailableReason
                      : isPickupStage
                      ? (isCleaningRepeatOrder ? 'Изменить дату и интервал выезда' : 'Изменить самодоставку или дату забора')
                      : isHandoverStage
                      ? 'Изменить способ возврата или назначить доставку'
                      : 'Изменить дату доставки или перевести на выдачу'
                  }
                  className={dispatcherActionClass(
                    canUseDispatcherScheduleSettings,
                    'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50'
                  )}
                >
                  <Calendar className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherDepartment && (
                <button
                  type="button"
                  onClick={openDepartmentModal}
                  disabled={actionLoading}
                  aria-disabled={!canUseDispatcherDepartment}
                  title={canUseDispatcherDepartment ? 'Сменить подразделение' : dispatcherDepartmentUnavailableReason}
                  aria-label={canUseDispatcherDepartment ? 'Сменить подразделение' : dispatcherDepartmentUnavailableReason}
                  className={dispatcherActionClass(
                    canUseDispatcherDepartment,
                    'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50'
                  )}
                >
                  <Building2 className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherNotes && (
                <button
                  type="button"
                  onClick={openDispatcherNotesModal}
                  disabled={actionLoading}
                  title="Доп. информация"
                  aria-label="Доп. информация"
                  className={`${DISPATCHER_ICON_BUTTON_BASE} border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-300 dark:hover:bg-indigo-950/50`}
                >
                  <Info className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherPriority && (
                <button
                  type="button"
                  onClick={openDispatcherPriorityModal}
                  disabled={actionLoading}
                  aria-disabled={isUrgentOrder}
                  title={
                    isUrgentOrder
                      ? 'Для срочного заказа диспетчерский приоритет недоступен'
                      : dispatcherPriorityScope
                        ? `Приоритет: ${dispatcherPrioritySummary}`
                        : 'Приоритет очереди'
                  }
                  aria-label={
                    isUrgentOrder
                      ? 'Для срочного заказа диспетчерский приоритет недоступен'
                      : dispatcherPriorityScope
                        ? `Приоритет: ${dispatcherPrioritySummary}`
                        : 'Приоритет очереди'
                  }
                  className={`${DISPATCHER_ICON_BUTTON_BASE} ${
                    isUrgentOrder
                      ? `${DISPATCHER_ICON_BUTTON_INACTIVE} cursor-not-allowed`
                      : dispatcherPriorityScope
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300 dark:hover:bg-emerald-950/50'
                        : 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:border-teal-900 dark:bg-teal-950/30 dark:text-teal-300 dark:hover:bg-teal-950/50'
                  }`}
                >
                  <ArrowUp className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherCallRequirement && (
                <button
                  type="button"
                  onClick={openDispatcherCallRequirementModal}
                  disabled={actionLoading}
                  aria-disabled={!canSelectPickupDispatcherCall && !canSelectDeliveryDispatcherCall && !dispatcherCallRequiredStage}
                  title={dispatcherCallButtonTitle}
                  aria-label={dispatcherCallButtonTitle}
                  className={`${DISPATCHER_ICON_BUTTON_BASE} ${
                    (!canSelectPickupDispatcherCall && !canSelectDeliveryDispatcherCall && !dispatcherCallRequiredStage)
                      ? `${DISPATCHER_ICON_BUTTON_INACTIVE} cursor-not-allowed`
                      : dispatcherCallRequiredStage
                      ? 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300 dark:hover:bg-sky-950/50'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                >
                  <Phone className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherAddress && (
                <button
                  type="button"
                  onClick={openDispatcherAddressModal}
                  disabled={actionLoading}
                  aria-disabled={!canUseDispatcherAddress}
                  title={canUseDispatcherAddress ? dispatcherAddressLabel : dispatcherAddressUnavailableReason}
                  aria-label={canUseDispatcherAddress ? dispatcherAddressLabel : dispatcherAddressUnavailableReason}
                  className={dispatcherActionClass(
                    canUseDispatcherAddress,
                    'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300 dark:hover:bg-sky-950/50'
                  )}
                >
                  <MapPin className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherAssignee && (
                <button
                  type="button"
                  onClick={() => {
                    if (!canUseDispatcherAssignee) {
                      showToast('info', dispatcherAssigneeUnavailableReason);
                      return;
                    }
                    openAssigneeModal();
                  }}
                  disabled={actionLoading}
                  aria-disabled={!canUseDispatcherAssignee}
                  title={canUseDispatcherAssignee ? dispatcherAssigneeButtonTitle : dispatcherAssigneeUnavailableReason}
                  aria-label={canUseDispatcherAssignee ? dispatcherAssigneeButtonTitle : dispatcherAssigneeUnavailableReason}
                  className={dispatcherActionClass(
                    canUseDispatcherAssignee,
                    'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50'
                  )}
                >
                  <UserCog className="h-4.5 w-4.5" />
                </button>
              )}

              {canOpenDispatcherCancel && (
                <button
                  type="button"
                  onClick={() => {
                    if (!canUseDispatcherCancel) {
                      showToast('info', dispatcherCancelUnavailableReason);
                      return;
                    }
                    haptic('warning');
                    setShowCancelConfirm(true);
                  }}
                  disabled={actionLoading}
                  aria-disabled={!canUseDispatcherCancel}
                  title={canUseDispatcherCancel ? dispatcherCancelButtonTitle : dispatcherCancelUnavailableReason}
                  aria-label={canUseDispatcherCancel ? dispatcherCancelButtonTitle : dispatcherCancelUnavailableReason}
                  className={dispatcherActionClass(
                    canUseDispatcherCancel,
                    'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50'
                  )}
                >
                  <XCircle className="h-4.5 w-4.5" />
                </button>
              )}
            </div>
            )}
          </AccordionSection>
        )}

        {revisionsForDisplay.length > 0 && (
          <AccordionSection
            title="Повторное выполнение"
            icon={History}
            count={revisionsForDisplay.length}
            className="order-0"
            open={expandedSections.repeatInfo}
            onToggle={() => toggleSection('repeatInfo')}
            contentClassName="p-4"
          >
            {() => (
            <div className="space-y-2">
              {revisionsForDisplay.map((revision) => {
                const stageRuns = Array.isArray(revision?.stage_runs) ? revision.stage_runs : [];
                const revisionOpen = !!expandedRevisionIds[revision.id];
                const stageLogOpen = !!expandedRevisionStageLogIds[revision.id];
                const revisionStatusMeta = getRevisionStatusMeta(revision);
                return (
                  <div key={revision.id} className="overflow-hidden rounded-xl border border-amber-200 dark:border-amber-900/40">
                    <button
                      type="button"
                      onClick={() => {
                        haptic(revisionOpen ? 'close' : 'open');
                        toggleRevisionAccordion(revision.id);
                      }}
                      className="flex w-full items-center justify-between gap-3 bg-amber-50 px-3 py-3 text-left dark:bg-amber-950/20"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                            Повтор #{revision.revision_number}
                          </div>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${revisionStatusMeta.className}`}>
                            {revisionStatusMeta.label}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-200">
                          {revision.started_date ? formatTimezoneDate(revision.started_date, true) : 'Дата не указана'}
                        </div>
                      </div>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-amber-700 transition-transform dark:text-amber-200 ${revisionOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {revisionOpen && (
                      <div className="space-y-3 border-t border-amber-100 bg-white p-3 dark:border-amber-900/30 dark:bg-gray-900">
                        <div className="rounded-xl bg-amber-50 px-3 py-2 dark:bg-amber-950/20">
                          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                            Причина повтора
                          </div>
                          <div className="mt-1 text-sm leading-5 text-amber-900 dark:text-amber-100">
                            {revision.reason}
                          </div>
                        </div>

                        <div className="grid gap-2 text-xs text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                          <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                            <div className="font-semibold text-gray-500 dark:text-gray-400">Открыл повтор</div>
                            <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {revision.created_by_name || 'Не указано'}
                            </div>
                          </div>
                          <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                            <div className="font-semibold text-gray-500 dark:text-gray-400">Дата открытия</div>
                            <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {revision.started_date ? formatTimezoneDate(revision.started_date, true) : 'Не указана'}
                            </div>
                          </div>
                          {revision.selected_by_name && (
                            <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                              <div className="font-semibold text-gray-500 dark:text-gray-400">Состав зафиксировал</div>
                              <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                                {revision.selected_by_name}
                              </div>
                            </div>
                          )}
                          {revision.completed_date && (
                            <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                              <div className="font-semibold text-gray-500 dark:text-gray-400">Повтор завершён</div>
                              <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatTimezoneDate(revision.completed_date, true)}
                              </div>
                            </div>
                          )}
                          {revision.cancelled_date && (
                            <>
                              <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                                <div className="font-semibold text-gray-500 dark:text-gray-400">Повтор отменён</div>
                                <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {formatTimezoneDate(revision.cancelled_date, true)}
                                </div>
                              </div>
                              <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                                <div className="font-semibold text-gray-500 dark:text-gray-400">Кто отменил</div>
                                <div className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {revision.cancelled_by_name || 'Не указано'}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {revision.cancel_reason && (
                          <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/20 dark:text-red-200">
                            <div className="font-semibold uppercase tracking-wide">Причина отмены</div>
                            <div className="mt-1 text-sm leading-5">
                              {revision.cancel_reason}
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <button
                            type="button"
                            onClick={() => {
                              haptic(stageLogOpen ? 'close' : 'open');
                              toggleRevisionStageLog(revision.id);
                            }}
                            className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                          >
                            {stageLogOpen ? 'Скрыть журнал этапов' : 'Показать журнал этапов'}
                          </button>
                          {stageLogOpen && (
                            stageRuns.length > 0 ? (
                              <div className="space-y-2">
                                {stageRuns.map((stageRun) => {
                                  const stageLabel = STAGES[stageRun.stage_name]?.label || stageRun.stage_name;
                                  const completionText = stageRun.completed_by_proxy
                                    ? `${stageRun.completer_name || 'Не указано'} · закрыл ${stageRun.completion_actor_name || 'диспетчер'}`
                                    : (stageRun.completer_name || stageRun.completion_actor_name || 'Не указано');
                                  return (
                                    <div key={stageRun.id} className="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-gray-950/40">
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{stageLabel}</div>
                                        {stageRun.completed_date ? (
                                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-200">
                                            Завершён
                                          </span>
                                        ) : revision.cancelled_date ? (
                                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                            Не выполнен
                                          </span>
                                        ) : (
                                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
                                            В работе
                                          </span>
                                        )}
                                      </div>
                                      <div className="mt-1 grid gap-1 text-xs text-gray-600 dark:text-gray-300">
                                        <div>
                                          Начал: {stageRun.starter_name || 'Не указано'}
                                          {stageRun.started_date ? ` · ${formatTimezoneDate(stageRun.started_date, true)}` : ''}
                                        </div>
                                        {stageRun.completed_date && (
                                          <div>
                                            Завершил: {completionText} · {formatTimezoneDate(stageRun.completed_date, true)}
                                          </div>
                                        )}
                                        {stageRun.assignee_name && (
                                          <div>Исполнитель этапа: {stageRun.assignee_name}</div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="rounded-xl border border-dashed border-gray-300 px-3 py-3 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                                Этапы повтора ещё не запускались.
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </AccordionSection>
        )}

        <div className="flex flex-col gap-1">
          <AccordionSection
            title={(
              <span className={paymentRequirementMissing ? 'text-red-600 dark:text-red-400' : ''}>
                Расчёты
              </span>
            )}
            icon={ReceiptText}
            className="order-2"
            open={expandedSections.payments}
            onToggle={() => toggleSection('payments')}
          >
          {() => (
            <>
          <div className="space-y-2 text-sm">
            <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-950/20">
              <div className="mb-2 text-xs font-semibold uppercase text-blue-500">Итог заказа</div>
              <div className="space-y-1.5">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Предметы</span>
                  <span className="text-gray-900 dark:text-gray-100">{money(paymentTotals.base)}</span>
                </div>
                {paymentTotals.services > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Доп. услуги</span>
                    <span className="text-purple-600 dark:text-purple-300">+{money(paymentTotals.services)}</span>
                  </div>
                )}
                {paymentTotals.discounts > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Скидки</span>
                    <span className="text-orange-600 dark:text-orange-300">-{money(paymentTotals.discounts)}</span>
                  </div>
                )}
                <div className="flex justify-between gap-3 border-t border-blue-100 pt-2 dark:border-blue-900">
                  <span className="font-medium text-gray-900 dark:text-gray-100">К оплате</span>
                  <span className="text-base font-semibold text-blue-700 dark:text-blue-300">{money(paymentTotals.final)}</span>
                </div>
              </div>
            </div>
            {isPickupStage && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-4 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                Итоговые цены могут измениться после замеров и доп. услуг предметов.
              </div>
            )}
            {isRepeatOrder && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-4 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                Заказ был оплачен ранее. При повторном выполнении способ оплаты менять нельзя.
              </div>
            )}
            {isApprovalStage && isCompanyClient && (
              <div className="space-y-2 rounded-xl border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-900 dark:bg-blue-950/20">
                <div className="flex items-start justify-between gap-3">
                  <div className={`text-sm font-semibold ${legalDocumentsRequirementMissing ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                    Пакет документов
                  </div>
                  {documentsPackageIsPrepared && (
                    <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200">
                      Пакет готов
                    </span>
                  )}
                </div>

                <div className="rounded-lg bg-white/80 px-3 py-3 dark:bg-gray-900/60">
                  <div className="space-y-1.5">
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Счёт</span>
                      <span className={invoiceIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                        {invoiceIsPrepared ? `№ ${order?.invoice_number || invoiceDraftData?.invoice_number || '—'} сохранён` : 'Не сохранён'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Акт</span>
                      <span className={actIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                        {actIsPrepared ? `№ ${order?.act_number || invoiceDraftData?.act_number || '—'} сохранён` : 'Не сохранён'}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Формат</span>
                      <span className="text-right text-gray-900 dark:text-gray-100">
                        Общий PDF-пакет
                      </span>
                    </div>
                    {invoicePreparedAt && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Счёт подготовили</span>
                        <span className="text-right text-gray-900 dark:text-gray-100">
                          {formatTimezoneDate(invoicePreparedAt, false)}
                          {invoicePreparedByName ? ` · ${invoicePreparedByName}` : ''}
                        </span>
                      </div>
                    )}
                    {actPreparedAt && (
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-500">Акт подготовили</span>
                        <span className="text-right text-gray-900 dark:text-gray-100">
                          {formatTimezoneDate(actPreparedAt, false)}
                          {actPreparedByName ? ` · ${actPreparedByName}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {!documentsPackageIsPrepared && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-4 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                    Для пакета должны быть сохранены и счёт, и акт.
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={openInvoiceEditor}
                    disabled={invoiceDraftLoading}
                    title={invoiceDraftLoading ? 'Открываю документы...' : (canEditOrderDocuments ? 'Редактировать документы' : 'Посмотреть документы')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-blue-500 text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadDocumentsPackagePdf}
                    disabled={!canDownloadDocumentsPackagePdf}
                    title={documentsPackageDownloading ? 'Готовлю PDF...' : (documentsPackageIsPrepared ? 'Скачать пакет PDF' : documentsPackageUnavailableReason)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/40"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handlePrintDocumentsPackagePdf}
                    disabled={!canPrintDocumentsPackagePdf}
                    title={documentsPackagePrinting ? 'Открываю печать...' : (documentsPackageIsPrepared ? 'Печать пакета' : documentsPackageUnavailableReason)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/40"
                  >
                    <Printer className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            {isFinalPaymentStage && (
              <>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Способ оплаты</span>
                  <span className="text-right text-gray-900 dark:text-gray-100">
                    {formatPaymentMethod(isCompanyClient ? SETTLEMENT_PAYMENT_METHOD : order.payment_method)}
                  </span>
                </div>
                {!isCompanyClient && canUseWorkActions && (
                  <div className="grid grid-cols-2 gap-2">
                    {PAYMENT_METHOD_OPTIONS.map((option) => {
                      const active = paymentMethodDraft === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => savePaymentMethod(option.value)}
                          disabled={actionLoading || order.payment_status === 'paid'}
                          className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                            active
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950/30 dark:text-blue-200'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                          } disabled:opacity-50`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {isCompanyClient && (
                  <>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Документы</span>
                      <span className={documentsPackageIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                        {companyDocumentsStatusLabel}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Формат</span>
                      <span className="text-right text-gray-900 dark:text-gray-100">
                        {documentsPackageIsPrepared ? 'Общий PDF-пакет' : 'Пакет появится после сохранения счёта и акта'}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Статус</span>
                  <span className={order.payment_status === 'paid' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'}>
                    {isCompanyClient
                      ? (order.payment_status === 'paid' ? 'Оплата подтверждена' : 'Ожидаем поступление по счёту')
                      : (order.payment_status === 'paid' ? 'Оплачен' : 'Не оплачен')}
                  </span>
                </div>
                {isCompanyClient && order?.payment_received_at && (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Оплату подтвердили</span>
                    <span className="text-right text-gray-900 dark:text-gray-100">
                      {formatTimezoneDate(order.payment_received_at, false)}
                    </span>
                  </div>
                )}
                {isCompanyClient && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-4 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                    После закрытия юрзаказа деньги не попадут в баланс автоматически. Подтверждение оплаты выполняется в разделе «Расчёты».
                  </div>
                )}
              </>
            )}
          </div>
          {isFinalPaymentStage && !isCompanyClient && order.payment_status !== 'paid' && canUseWorkActions && (
            <button
              type="button"
              onClick={handleMarkOrderPaidClick}
              disabled={actionLoading}
              className="mt-3 flex h-8 w-full items-center justify-center gap-1 rounded-lg border-2 border-blue-500 px-3 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
            >
              <Check className="h-3.5 w-3.5" />
              Заказ оплачен
            </button>
          )}
            </>
          )}
      </AccordionSection>

          {isCompanyClient && !isApprovalStage && (
            <AccordionSection
              title={(
                <span className={legalDocumentsRequirementMissing ? 'text-red-600 dark:text-red-400' : ''}>
                  Документы юрлица
                </span>
              )}
              icon={ReceiptText}
              className="order-2"
              open={expandedSections.invoice}
              onToggle={() => toggleSection('invoice')}
            >
            {() => (
              <>
            <div className="space-y-2 text-sm">
              <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-950/20">
                <div className="mb-2 text-xs font-semibold uppercase text-blue-500">Пакет документов</div>
                <div className="space-y-1.5">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Счёт</span>
                    <span className={invoiceIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                      {invoiceIsPrepared ? `№ ${order?.invoice_number || invoiceDraftData?.invoice_number || '—'} сохранён` : 'Не сохранён'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Акт</span>
                    <span className={actIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                      {actIsPrepared ? `№ ${order?.act_number || invoiceDraftData?.act_number || '—'} сохранён` : 'Не сохранён'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Пакет</span>
                    <span className={documentsPackageIsPrepared ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-300'}>
                      {documentsPackageIsPrepared ? 'Готов к печати' : 'Собирается из счёта и акта'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">Формат</span>
                    <span className="text-right text-gray-900 dark:text-gray-100">
                      {formatInvoiceDeliveryMethod(INVOICE_DELIVERY_METHOD_PAPER)} · PDF-пакет
                    </span>
                  </div>
                  {invoicePreparedAt && (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Счёт подготовили</span>
                      <span className="text-right text-gray-900 dark:text-gray-100">
                        {formatTimezoneDate(invoicePreparedAt, false)}
                        {invoicePreparedByName ? ` · ${invoicePreparedByName}` : ''}
                      </span>
                    </div>
                  )}
                  {actPreparedAt && (
                    <div className="flex justify-between gap-3">
                      <span className="text-gray-500">Акт подготовили</span>
                      <span className="text-right text-gray-900 dark:text-gray-100">
                        {formatTimezoneDate(actPreparedAt, false)}
                        {actPreparedByName ? ` · ${actPreparedByName}` : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!documentsPackageIsPrepared && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-4 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                  Для полного комплекта нужно сохранить и счёт, и акт.
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openInvoiceEditor}
                  disabled={invoiceDraftLoading}
                  title={invoiceDraftLoading ? 'Открываю документы...' : (canEditOrderDocuments ? 'Редактировать документы' : 'Посмотреть документы')}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-blue-500 text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleDownloadDocumentsPackagePdf}
                  disabled={!canDownloadDocumentsPackagePdf}
                  title={documentsPackageDownloading ? 'Готовлю PDF...' : (documentsPackageIsPrepared ? 'Скачать пакет PDF' : documentsPackageUnavailableReason)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/40"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handlePrintDocumentsPackagePdf}
                  disabled={!canPrintDocumentsPackagePdf}
                  title={documentsPackagePrinting ? 'Открываю печать...' : (documentsPackageIsPrepared ? 'Печать пакета' : documentsPackageUnavailableReason)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-2 border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900/40"
                >
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            </div>
              </>
            )}
            </AccordionSection>
          )}

        {hasFloorLiftService && isDeliveryStage && (
          <AccordionSection
            title={(
              <span className={floorLiftRequirementMissing ? 'text-red-600 dark:text-red-400' : ''}>
                Подъём на этаж
              </span>
            )}
            icon={StaircaseIcon}
            className="order-3"
            open={expandedSections.floors}
            onToggle={() => toggleSection('floors')}
          >
            {() => (
            <div className="space-y-3">
              {canUseWorkActions ? (
                <>
                  <div className="flex h-11 items-center rounded-lg border border-blue-200 bg-white dark:border-blue-900 dark:bg-gray-900">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setFloorsDraft((prev) => Math.max(0, prev - 1));
                      }}
                      disabled={actionLoading || floorsDraft <= 0}
                      className="flex h-11 w-11 items-center justify-center text-blue-700 disabled:opacity-40 dark:text-blue-200"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      value={floorsDraft}
                      onChange={(event) => setFloorsDraft(Math.max(0, Math.min(99, Number(event.target.value) || 0)))}
                      disabled={actionLoading}
                      className="h-11 flex-1 border-x border-blue-100 bg-transparent text-center text-sm font-semibold text-gray-900 outline-none dark:border-blue-900 dark:text-gray-100"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setFloorsDraft((prev) => Math.min(99, prev + 1));
                      }}
                      disabled={actionLoading || floorsDraft >= 99}
                      className="flex h-11 w-11 items-center justify-center text-blue-700 disabled:opacity-40 dark:text-blue-200"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={saveFloorsCount}
                    disabled={actionLoading || floorsDraft === Number(order?.floors_count || 0)}
                    className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-500 px-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                  >
                    <Check className="h-4 w-4" />
                    Сохранить
                  </button>
                </>
              ) : (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                  {order.floors_count || 0} эт.
                </div>
              )}
            </div>
            )}
          </AccordionSection>
        )}

        {showReturnSettingsSection && (
          <AccordionSection
            title={(
              <span className={approvalRequirementMissing ? 'text-red-600 dark:text-red-400' : ''}>
                Условия возврата
              </span>
            )}
            icon={MapPin}
            className="order-3"
            open={expandedSections.approvalReturn}
            onToggle={() => toggleSection('approvalReturn')}
          >
            {() => (
            <div className="space-y-3">
              {canEditApprovalFields ? (
                <>
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setApprovalField('returnType', 'self_pickup');
                      }}
                      disabled={actionLoading || !canEditApprovalFields}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        approvalDraft.returnType === 'self_pickup'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Самовывоз
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setApprovalField('returnType', 'delivery');
                      }}
                      disabled={actionLoading || !canEditApprovalFields}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        approvalDraft.returnType === 'delivery'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Доставка клиенту
                    </button>
                  </div>

                  {approvalDraft.returnType === 'self_pickup' ? (
                    <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200">
                      После закрытия заказ перейдёт на этап выдачи.
                    </div>
                  ) : approvalDraft.returnType === 'delivery' ? (
                    <>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <DatePicker
                        value={approvalDraft.deliveryDate}
                        onChange={(value) => setApprovalField('deliveryDate', value)}
                        placeholder="Дата возврата"
                        allowPastDates={false}
                        minDate={formatDateInputValue(getNowInSystemTimezone())}
                        inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                      />
                    </div>
                    <div className="flex-1">
                      <TimeSlotPicker
                        value={approvalDraft.deliveryTime}
                        onChange={(value) => setApprovalField('deliveryTime', value)}
                        placeholder="Интервал"
                        disabledBeforeMinutes={isDateTodayInSystemTimezone(approvalDraft.deliveryDate) ? getCurrentMinutesInSystemTimezone() : null}
                        inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                      />
                    </div>
                  </div>

                  {isPrivilegedUser && (
                    <label className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50/70 px-3 py-3 text-sm text-sky-900 dark:border-sky-900/70 dark:bg-sky-950/20 dark:text-sky-100">
                      <input
                        type="checkbox"
                        checked={!!approvalDraft.callRequired}
                        onChange={(event) => setApprovalDraft((prev) => ({ ...prev, callRequired: event.target.checked }))}
                        disabled={actionLoading || !canEditApprovalFields}
                        className="mt-0.5 h-4 w-4 rounded border-sky-300 text-sky-600 focus:ring-sky-500 dark:border-sky-700 dark:bg-sky-950/40"
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 font-medium">
                          <Phone className="h-4 w-4 shrink-0" />
                          Нужен созвон перед возвратом
                        </span>
                      </span>
                    </label>
                  )}

                  {(
                    <RouteSuggestions
                      suggestions={approvalRouteSuggestions}
                      loading={approvalRouteSuggestionsLoading}
                      onApply={applyApprovalRouteSuggestion}
                      showWhenEmpty
                    />
                  )}

                  {approvalDraft.deliveryDate && (
                    <RouteScheduleAccordion
                      title="Назначенные выезды"
                      selectedDate={approvalDraft.deliveryDate}
                      items={approvalRouteItems}
                      loading={approvalRoutesLoading}
                      open={approvalRoutesOpen}
                      onToggle={() => setApprovalRoutesOpen((prev) => !prev)}
                      groupByDepartment={approvalRouteDepartmentView === 'grouped'}
                      showDepartmentOnCard={approvalRouteDepartmentView === 'mixed'}
                      showDepartmentBadge={approvalRouteDepartmentView === 'mixed' && approvalRouteShowDepartmentBadge}
                      showExecutorInCard={approvalRouteShowExecutor}
                      hideRegionInAddress={approvalRouteHideRegion}
                      currentUserId={currentWorkflowUserId}
                      currentUserType={currentUserType || 'user'}
                      activeView={approvalRoutesPanelView}
                      onViewChange={setApprovalRoutesPanelView}
                      onShowOnMap={showApprovalRouteItemOnMap}
                      renderMapContent={() => (
                        <RoutesMap
                          items={approvalRouteItems}
                          loading={approvalRoutesLoading}
                          hideRegionInAddress={approvalRouteHideRegion}
                          currentUserId={currentWorkflowUserId}
                          currentUserType={currentUserType || 'user'}
                          fixedDateKey={approvalDraft.deliveryDate}
                          hideExternalNavigation
                          hideRouteDetailsLink
                          draftRouteItem={draftReturnRouteItem}
                          routeCardAsModal
                          focusRequest={approvalRoutesMapFocusRequest}
                          routeContextBase={{
                            activeTab: 'map',
                            selectedDateKey: approvalDraft.deliveryDate,
                            expandedDateKey: approvalDraft.deliveryDate
                          }}
                        />
                      )}
                      subtitle=""
                      headerAction={(
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            haptic('open');
                            setShowApprovalRouteSettings(true);
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                          title="Настройки списка"
                          aria-label="Настройки списка"
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                      )}
                    />
                  )}

                  {approvalSelectedAddressText && (
                    <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400">Выбранный адрес</div>
                        {canEditApprovalFields && (
                          <button
                            type="button"
                            onClick={() => {
                              haptic(approvalAddressEditorOpen ? 'close' : 'open');
                              setApprovalAddressEditorOpen((prev) => !prev);
                              setApprovalSavedAddressesOpen(false);
                              setShowApprovalAddressSuggestions(false);
                            }}
                            className="text-xs font-medium text-blue-600 dark:text-blue-300"
                          >
                            {approvalAddressEditorOpen ? 'Скрыть изменение' : 'Изменить адрес'}
                          </button>
                        )}
                      </div>
                      <div className="mt-1 break-words text-sm text-gray-900 dark:text-gray-100">{approvalSelectedAddressText}</div>
                      {!order?.self_delivery && pickupAddressQuickText && approvalSelectedAddressText === pickupAddressQuickText && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Это адрес, откуда забирали заказ.
                        </div>
                      )}
                      {!approvalAddressConfirmed && (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                          Адрес нужно выбрать из подсказок или из сохранённых адресов.
                        </div>
                      )}
                    </div>
                  )}

                  {(!approvalSelectedAddressText || approvalAddressEditorOpen) && (
                    <>
                      {!approvalSelectedAddressText && pickupAddressQuickText && !order?.self_delivery && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950/20 dark:text-blue-300">
                          <div className="text-xs font-semibold uppercase opacity-80">Выбранный адрес</div>
                          <div className="mt-1 break-words">Это адрес, откуда забирали заказ</div>
                          <div className="mt-1 break-words text-xs leading-4 opacity-75">{pickupAddressQuickText}</div>
                        </div>
                      )}

                      <div className="relative">
                        <div className="mb-1 flex items-center justify-end gap-2">
                          {approvalSavedAddresses.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                haptic(approvalSavedAddressesOpen ? 'close' : 'open');
                                setApprovalSavedAddressesOpen((prev) => !prev);
                              }}
                              className="text-xs font-medium text-blue-600 dark:text-blue-300"
                            >
                              {approvalSavedAddressesOpen ? 'Скрыть адреса' : `Адреса клиента (${approvalSavedAddresses.length})`}
                            </button>
                          )}
                        </div>
                        <FloatingField label="Адрес возврата" disabled={actionLoading || !canEditApprovalFields}>
                          <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 dark:bg-gray-900">
                            <input
                              ref={approvalAddressInputRef}
                              type="text"
                              value={approvalDraft.deliveryAddress}
                              onChange={(event) => handleApprovalAddressChange(event.target.value)}
                              disabled={actionLoading || !canEditApprovalFields}
                              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-xs placeholder:text-gray-400 dark:text-gray-100"
                              placeholder="Начните вводить адрес..."
                            />
                            {!!approvalDraft.deliveryAddress && canEditApprovalFields && (
                              <button
                                type="button"
                                onClick={() => {
                                  haptic('tap');
                                  clearApprovalAddressInput();
                                }}
                                disabled={actionLoading}
                                className="cart-btn m-0 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 dark:text-gray-500 dark:hover:text-gray-300"
                                aria-label="Очистить адрес"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </FloatingField>

                        {showApprovalAddressSuggestions && approvalAddressSuggestions.length > 0 && (
                          <div className="absolute bottom-full z-20 mb-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                            {approvalAddressSuggestions.map((suggestion, index) => (
                              <button
                                key={`${getSuggestionAddressText(suggestion)}-${index}`}
                                type="button"
                                onClick={() => {
                                  haptic('selection');
                                  selectApprovalSuggestion(suggestion);
                                }}
                                className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700 last:border-b-0"
                              >
                                <div className="break-words font-medium text-gray-900 dark:text-gray-100">
                                  {getSuggestionAddressText(suggestion)}
                                </div>
                                {suggestion?.data?.city && (
                                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{suggestion.data.city}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {approvalSavedAddressesOpen && approvalSavedAddresses.length > 0 && (
                        <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-2 dark:border-gray-800 dark:bg-gray-950/30">
                          {approvalSavedAddresses.map((address) => (
                            <button
                              key={address.id}
                              type="button"
                              onClick={() => {
                                haptic('selection');
                                applyApprovalAddress(address);
                              }}
                              disabled={actionLoading || !canEditReturnSettings}
                              className="flex w-full items-start justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 disabled:opacity-60 dark:bg-gray-900 dark:hover:bg-blue-950/20"
                            >
                              <span className="min-w-0 flex-1 break-words leading-5 text-gray-900 dark:text-gray-100">{address.address_full}</span>
                              <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                                {`${address.order_count || 1} ${pluralizeOrder(address.order_count || 1)}`}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                    </>
                  ) : null}

                  {approvalDraft.returnType && (
                    <div className="pt-1">
                      <AssigneePicker
                        label={approvalDraft.returnType === 'self_pickup' ? 'Исполнитель на выдачу' : 'Исполнитель на доставку'}
                        value={approvalDraft.returnAssignedTo}
                        onChange={(value) => setApprovalField('returnAssignedTo', value)}
                        users={approvalAssignees}
                        loading={approvalAssigneesLoading}
                        disabled={actionLoading || approvalAssigneesLoading || !canEditApprovalFields}
                        fallbackName={savedReturnAssigneeName}
                        modalTitle={approvalDraft.returnType === 'self_pickup' ? 'Исполнитель на выдачу' : 'Исполнитель на доставку'}
                        helperText={
                          approvalDraft.returnType === 'self_pickup'
                            ? 'Сотрудники подразделения с правом на выдачу'
                            : 'Сотрудники подразделения с правом на доставку'
                        }
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
                    <div className="text-[11px] font-semibold uppercase text-gray-500 dark:text-gray-400">
                      {approvalDraft.returnType === 'self_pickup' ? 'Самовывоз' : 'Доставка клиенту'}
                    </div>
                    {approvalDraft.returnType === 'self_pickup' ? (
                      <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        После закрытия заказ перейдёт на этап выдачи.
                      </div>
                    ) : (
                      <div className="mt-1 space-y-1 text-sm text-gray-900 dark:text-gray-100">
                        {approvalDraft.deliveryDate && (
                          <div>{formatTimezoneDate(formatDateInputToApiIso(approvalDraft.deliveryDate), false)}</div>
                        )}
                        {approvalDraft.deliveryTime && (
                          <div>{approvalDraft.deliveryTime}</div>
                        )}
                        {approvalSelectedAddressText && (
                          <div className="break-words">{approvalSelectedAddressText}</div>
                        )}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                      <span className="font-medium text-gray-500 dark:text-gray-400">
                        {approvalDraft.returnType === 'self_pickup' ? 'Исполнитель на выдачу: ' : 'Исполнитель на доставку: '}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {savedReturnAssigneeName || 'Не назначен'}
                      </span>
                    </div>
                  </div>

                  {canEditReturnSettings && (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('open');
                        setApprovalEditMode(true);
                      }}
                      disabled={actionLoading}
                      className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-500 px-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                    >
                      Изменить условия
                    </button>
                  )}
                </>
              )}

              {canEditApprovalFields && (
                <button
                  type="button"
                  onClick={saveApprovalSettings}
                  disabled={
                    actionLoading
                    || approvalDraftIsSaved
                    || !canEditReturnSettings
                    || !approvalDraft.returnType
                    || (
                      approvalDraft.returnType === 'delivery'
                      && (
                        !approvalDraft.deliveryDate
                        || !approvalDraft.deliveryTime
                        || !approvalSelectedAddressText
                        || !approvalAddressConfirmed
                      )
                    )
                  }
                  className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-500 px-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  <Check className="h-4 w-4" />
                  Сохранить условия возврата
                </button>
              )}
            </div>
            )}
          </AccordionSection>
        )}

        <AccordionSection
          title={(
            <span className={cartRequirementMissing ? 'text-red-600 dark:text-red-400' : ''}>
              Корзина
            </span>
          )}
          icon={ShoppingCart}
          count={mainItems.length}
          className="order-1"
          open={expandedSections.cart}
          onToggle={() => toggleSection('cart')}
        >
          {() => (
            <>
          {mainItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              В заказе пока нет предметов
            </div>
          ) : (
            <div className="space-y-1">
              <div className="mb-2 text-xs font-semibold uppercase text-blue-500">Предметы</div>
              {isRepeatOrder && order?.status === 'pickup' && (
                <div className="mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
                  Отметьте предметы, которые фактически забрали у клиента на повторное выполнение.
                </div>
              )}
              {mainItems.map((item, index) => {
                const extraCount = parseAdditionalServicesSnapshot(item.additional_services_snapshot, item.additional_service_ids).length;
                const additionalServicesBreakdown = getItemAdditionalServicesBreakdown(order, item);
                const baseSubtotal = getMainItemBaseSubtotal(order, item);
                const regularBaseSubtotal = getMainItemRegularSubtotal(order, item);
                const promotionBaseSubtotal = getMainItemPromotionSubtotal(order, item);
                const breakdownOpen = !!expandedItemBreakdownIds[item.id];
                const isRevisionSelected = !!item.revision_selected;
                const isRevisionLockedReady = !!item.revision_locked_ready;
                const isReworkPendingWashing = order?.status === 'drying_packing'
                  && order?.active_rework
                  && item?.rework_selected
                  && getItemStageProcessingStatus(item, 'washing') !== 'done'
                  && getItemStageProcessingStatus(item, 'washing') !== 'completed';
                const itemEffectiveStage = (
                  order?.status === 'drying_packing'
                  && order?.active_rework
                  && (['washing_laundry', 'washing_cleaning'].includes(currentListStage) || (!isDryer && ownedWashingFlowStages.includes(item?.processing_stage_name)))
                )
                  ? (item?.processing_stage_name || 'washing')
                  : order.status;
                const isDone = isRevisionLockedReady || getItemStageProcessingStatus(item, itemEffectiveStage) === 'done';
                const processingRouteBadge = getProcessingRouteBadgeMeta(item);
                const ProcessingRouteIcon = processingRouteBadge?.icon;
                const processingStatusLabel = isRevisionLockedReady ? 'Готов' : (isDone ? 'Выполнен' : 'В работе');
                const repeatBadgeLabel = activeRevisionNumber ? `Повтор №${activeRevisionNumber}` : 'Повтор';
                const canProcessThisItem = canProcessSpecificItem(item);
                const dryingStorage = getItemDryingStorage(item);
                const canChangeDryingStorage = item?.processing_route === 'cleaning'
                  && !isRevisionLockedReady
                  && (
                    (order?.status === 'drying_packing' && canProcessItems && canProcessThisItem)
                    || (['approval', 'delivery', 'handover'].includes(order?.status) && (hasEffectivePrivilegedAccess || canUseDispatcherCorrections))
                  );
                const storageMissing = storageCellsRequired
                  && order?.status === 'drying_packing'
                  && item?.processing_route === 'cleaning'
                  && !dryingStorage
                  && !isRevisionLockedReady;
                const canEditThisItem = canChangeOrderComposition
                  || (canOpenItemEditor && canProcessThisItem)
                  || (['approval', 'delivery', 'handover'].includes(order?.status) && item?.processing_route === 'cleaning' && (hasEffectivePrivilegedAccess || canUseDispatcherCorrections));
                const confirmOpen = deleteCandidate?.id === item.id;
                const adjustedUnitPrice = getAdjustedMainItemUnitPrice(order, item);
                const adjustedRegularUnitPrice = getAdjustedMainItemRegularUnitPrice(order, item);
                const adjustedPromotionUnitPrice = getAdjustedMainItemPromotionUnitPrice(order, item);
                const showPromotionPrice = adjustedPromotionUnitPrice !== null
                  && Math.abs(adjustedRegularUnitPrice - adjustedPromotionUnitPrice) > 0.009;
                return (
                  <div key={item.id} className="flex flex-col rounded-lg bg-blue-50 px-3 py-2 dark:bg-blue-900/10">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <div className="font-mono text-xs font-medium text-blue-600 dark:text-blue-300">
                            {getItemDisplayNumber(order, item, index)}
                          </div>
                          {isProductionStage && (
                            <button
                              type="button"
                              onClick={(event) => handleCartBadgeTap(event, `${item.id}:status`, processingStatusLabel)}
                              className={`${ORDER_ITEM_BADGE_BUTTON_CLASS} ${isDone ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100'}`}
                              style={ORDER_ITEM_BADGE_BUTTON_STYLE}
                              title={processingStatusLabel}
                              aria-label={processingStatusLabel}
                            >
                              {isDone ? <Check className="block h-3 w-3 shrink-0" /> : <Clock className="block h-3 w-3 shrink-0" />}
                            </button>
                          )}
                          {isRevisionSelected && (
                            <button
                              type="button"
                              onClick={(event) => handleCartBadgeTap(event, `${item.id}:repeat`, repeatBadgeLabel)}
                              className={`${ORDER_ITEM_BADGE_BUTTON_CLASS} bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200`}
                              style={ORDER_ITEM_BADGE_BUTTON_STYLE}
                              title={repeatBadgeLabel}
                              aria-label={repeatBadgeLabel}
                            >
                              <RefreshCw className="block h-3 w-3 shrink-0" />
                            </button>
                          )}
                          {item.rework_selected && (
                            <button
                              type="button"
                              onClick={(event) => handleCartBadgeTap(event, `${item.id}:rework`, 'Доработка')}
                              className={`${ORDER_ITEM_BADGE_BUTTON_CLASS} bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200`}
                              style={ORDER_ITEM_BADGE_BUTTON_STYLE}
                              title="Доработка"
                              aria-label="Доработка"
                            >
                              <RotateCcw className="block h-3 w-3 shrink-0" />
                            </button>
                          )}
                          {canSelectRepeatItems && !isRevisionSelected && (
                            <button
                              type="button"
                              onClick={(event) => handleCartBadgeTap(event, `${item.id}:not-selected`, 'Не взят на повтор')}
                              className={`${ORDER_ITEM_BADGE_BUTTON_CLASS} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}
                              style={ORDER_ITEM_BADGE_BUTTON_STYLE}
                              title="Не взят на повтор"
                              aria-label="Не взят на повтор"
                            >
                              <Minus className="block h-3 w-3 shrink-0" />
                            </button>
                          )}
                          {processingRouteBadge && ProcessingRouteIcon && (
                            <button
                              type="button"
                              onClick={(event) => handleCartBadgeTap(event, `${item.id}:route`, processingRouteBadge.label)}
                              className={`${ORDER_ITEM_BADGE_BUTTON_CLASS} ${processingRouteBadge.className}`}
                              style={ORDER_ITEM_BADGE_BUTTON_STYLE}
                              title={processingRouteBadge.label}
                              aria-label={processingRouteBadge.label}
                            >
                              <ProcessingRouteIcon className="block h-3 w-3 shrink-0" />
                            </button>
                          )}
                          {dryingStorage && (
                            canChangeDryingStorage ? (
                              <button
                                type="button"
                                onClick={() => {
                                  haptic('open');
                                  setStorageCellSheet({ mode: 'assign-one', items: [item] });
                                }}
                                className="cart-btn inline-flex h-5 items-center rounded-full border border-purple-200 bg-purple-100 px-2 text-[10px] font-semibold text-purple-700 transition-colors hover:bg-purple-200 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300 dark:hover:bg-purple-950/60"
                                title={`Ячейка ${dryingStorage.name}. Нажмите, чтобы изменить`}
                              >
                                Ячейка {dryingStorage.name}
                              </button>
                            ) : (
                              <span className="inline-flex h-5 items-center rounded-full border border-purple-200 bg-purple-100 px-2 text-[10px] font-semibold text-purple-700 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300">
                                Ячейка {dryingStorage.name}
                              </span>
                            )
                          )}
                          {storageMissing && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!canChangeDryingStorage) return;
                                haptic('open');
                                setStorageCellSheet({ mode: 'assign-one', items: [item] });
                              }}
                              disabled={!canChangeDryingStorage || actionLoading}
                              className="cart-btn inline-flex h-5 items-center rounded-full border border-red-300 bg-red-50 px-2 text-[10px] font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50"
                              title="Ячейка не указана"
                            >
                              Ячейка не указана
                            </button>
                          )}
                        </div>
                        <div className="mt-0.5 min-w-0 max-w-full">
                          <div className="break-words text-sm font-medium leading-snug text-gray-900 dark:text-gray-100">
                            {item.item_name}
                          </div>
                        </div>
                        <div className="mt-0.5 flex flex-nowrap items-center gap-2 overflow-hidden text-xs text-gray-500 dark:text-gray-400">
                          <span className="shrink-0 whitespace-nowrap leading-4">
                            {item.unit === 'кг'
                              ? (item.weight ? `${item.weight} кг` : 'Кг. не указан')
                              : (item.unit === 'м²' || item.unit === 'м2')
                                ? (item.area
                                    ? (item.width && item.length ? `${item.area} м² (${item.width}×${item.length} м)` : `${item.area} м²`)
                                    : 'м² не указана')
                                : `${item.quantity || 1} ${item.unit || 'шт'}`}
                          </span>
                          {showPromotionPrice ? (
                            <span className="flex shrink-0 items-center gap-1 whitespace-nowrap leading-4">
                              <span className="font-medium text-emerald-700 dark:text-emerald-300">{money(adjustedPromotionUnitPrice)}</span>
                              <span className="text-[11px] text-gray-400 line-through dark:text-gray-500">{money(adjustedRegularUnitPrice)}</span>
                            </span>
                          ) : (
                            <span className="shrink-0 whitespace-nowrap leading-4">{money(adjustedUnitPrice)}</span>
                          )}
                          {extraCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                haptic(breakdownOpen ? 'close' : 'open');
                                setExpandedItemBreakdownIds((prev) => ({
                                  ...prev,
                                  [item.id]: !prev[item.id]
                                }));
                              }}
                              className="cart-btn m-0 inline-flex min-w-0 items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 font-semibold leading-4 text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                            >
                              <span className="min-w-0 truncate">{extraCount} доп. {pluralizeService(extraCount)}</span>
                              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${breakdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1 pt-3">
                        <span className="mr-1 text-sm font-medium text-gray-900 dark:text-gray-100">{getMainItemDisplayAmount(order, item)}</span>
                        {canEditThisItem && (
                          <button
                            onClick={() => {
                              haptic('open');
                              setSelectedItem(toCartItem(item));
                            }}
                            className="cart-btn flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {isProductionStage && canProcessItems && !isRevisionLockedReady && (
                          isReworkPendingWashing && isDryer ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" title="Предмет перестирывается в потоке стирки/чистки">
                              <RotateCcw className="h-3 w-3" />
                              На перестире
                            </span>
                          ) : canProcessThisItem ? (
                            isDone ? (
                              <button
                                onClick={() => reopenItemProcessing(item)}
                                disabled={actionLoading}
                                className="cart-btn flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-xs text-amber-700 transition-colors hover:bg-amber-200 disabled:opacity-50 dark:bg-amber-900/30 dark:text-amber-300"
                                title="Вернуть в работу"
                                aria-label="Вернуть в работу"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => markItemCompleted(item)}
                                disabled={actionLoading}
                                className="cart-btn flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-xs text-green-700 transition-colors hover:bg-green-200 disabled:opacity-50 dark:bg-green-900/30 dark:text-green-300"
                                title="Отметить выполненным"
                                aria-label="Отметить выполненным"
                              >
                                ✓
                              </button>
                            )
                          ) : null
                        )}
                        {canSelectRepeatItems && (
                          <button
                            type="button"
                            onClick={() => {
                              haptic(isRevisionSelected ? 'close' : 'selection');
                              toggleRepeatItem(item, isRevisionSelected);
                            }}
                            disabled={actionLoading || repeatSelectionLocked}
                            className={`cart-btn rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                              isRevisionSelected
                                ? 'bg-amber-500 text-white hover:bg-amber-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {isRevisionSelected ? 'На повторе' : 'Взять'}
                          </button>
                        )}
                        {canDeleteCartItems && (
                          <button
                            onClick={() => {
                              haptic(confirmOpen ? 'close' : 'warning');
                              setDeleteCandidate(confirmOpen ? null : item);
                            }}
                            disabled={actionLoading}
                            className="cart-btn flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-sm leading-none text-red-600 transition-colors hover:bg-red-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-400"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    {breakdownOpen && extraCount > 0 && (
                      <div className="mt-2 space-y-1.5 border-t border-blue-200 pt-2 dark:border-blue-800">
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Цена предмета</span>
                          {showPromotionPrice && baseSubtotal !== null ? (
                            <span className="flex items-center gap-1">
                              <span className="font-medium text-emerald-700 dark:text-emerald-300">
                                {money(promotionBaseSubtotal)}
                              </span>
                              <span className="text-[11px] text-gray-400 line-through dark:text-gray-500">
                                {money(regularBaseSubtotal)}
                              </span>
                            </span>
                          ) : (
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {baseSubtotal === null ? '?' : money(baseSubtotal)}
                            </span>
                          )}
                        </div>
                        {additionalServicesBreakdown.map((service) => (
                          <div key={service.id} className="flex items-center justify-between gap-3 text-xs">
                            <span className="min-w-0 flex-1 truncate text-gray-600 dark:text-gray-300">
                              {service.name}
                              <span className="ml-1 text-gray-400 dark:text-gray-500">{service.nominal}</span>
                            </span>
                            <span className="shrink-0 font-medium text-blue-700 dark:text-blue-300">
                              {service.amount === null ? '?' : `+${money(service.amount)}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {confirmOpen && (
                      <div className="mt-2 flex items-center gap-2 border-t border-blue-200 pt-2 dark:border-blue-800">
                        <span className="flex-1 text-xs text-gray-500 dark:text-gray-400">Удалить предмет?</span>
                        <button
                          onClick={() => deleteItem(item.id)}
                          disabled={actionLoading}
                          className="cart-btn flex h-6 items-center rounded-full bg-red-500 px-3 text-xs font-medium text-white disabled:opacity-50"
                        >
                          Удалить
                        </button>
                        <button
                          onClick={() => {
                            haptic('close');
                            setDeleteCandidate(null);
                          }}
                          className="cart-btn flex h-6 items-center rounded-full bg-gray-200 px-3 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                        >
                          Отмена
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {isProductionStage && canProcessItems && scopedProcessingItems.length > 0 && (
            <button
              onClick={() => setShowCompleteAllConfirm(true)}
              disabled={actionLoading || (areAllMainItemsDone && scopedItemsMissingRequiredStorage.length === 0)}
              className="mt-3 h-8 rounded-lg border-2 border-green-500 px-3 text-xs font-medium text-green-700 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-green-500 dark:text-green-300 dark:hover:bg-green-950/30"
            >
              {areAllMainItemsDone && scopedItemsMissingRequiredStorage.length > 0
                ? 'Указать ячейки'
                : (hasScopedWashingFlowAccess ? 'Выполнить свой поток' : 'Выполнить все')}
            </button>
          )}

          {additionalItems.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-800">
              {additionalItems.filter((item) => item.additional_type === 'order').length > 0 && (
                <div className="mb-3">
                  <div className="mb-2 text-xs font-semibold uppercase text-purple-500">Услуги заказа</div>
                  <div className="space-y-1">
                    {additionalItems.filter((item) => item.additional_type === 'order').map((item) => {
                      const confirmOpen = deleteCandidate?.id === item.id;
                      return (
                        <div key={item.id} className="flex flex-col rounded-lg bg-purple-50 px-3 py-2 dark:bg-purple-900/10">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.item_name}</div>
                              <div className="text-xs text-purple-500">{getAdditionalNominal(item)}</div>
                            </div>
                            <div className="ml-2 flex shrink-0 items-center gap-1">
                              <span className="mr-1 text-sm font-medium text-purple-600 dark:text-purple-400">{getAdditionalCalculatedAmount(item, paymentTotals.base)}</span>
                              {canEditOrderServices && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    haptic(confirmOpen ? 'close' : 'warning');
                                    setDeleteCandidate(confirmOpen ? null : item);
                                  }}
                                  className="cart-btn flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-sm leading-none text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                          {confirmOpen && (
                            <div className="mt-2 flex items-center gap-2 border-t border-purple-200 pt-2 dark:border-purple-800">
                              <span className="flex-1 text-xs text-gray-500 dark:text-gray-400">Удалить услугу?</span>
                              <button
                                onClick={() => deleteItem(item.id, { silent: true, successMessage: 'Услуга удалена' })}
                                disabled={actionLoading}
                                className="cart-btn flex h-6 items-center rounded-full bg-red-500 px-3 text-xs font-medium text-white disabled:opacity-50"
                              >
                                Удалить
                              </button>
                              <button
                                onClick={() => {
                                  haptic('close');
                                  setDeleteCandidate(null);
                                }}
                                className="cart-btn flex h-6 items-center rounded-full bg-gray-200 px-3 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                              >
                                Отмена
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {additionalItems.filter((item) => item.additional_type === 'discount' || item.discount_id).length > 0 && (
                <div className="mb-3">
                  <div className="mb-2 text-xs font-semibold uppercase text-orange-500">Скидки</div>
                  <div className="space-y-1">
                    {additionalItems.filter((item) => item.additional_type === 'discount' || item.discount_id).map((item) => {
                      const confirmOpen = deleteCandidate?.id === item.id;
                      const canRemoveDiscountItem = canEditDiscounts && canEditAppliedDiscount(item);
                      return (
                        <div key={item.id} className="flex flex-col rounded-lg bg-orange-50 px-3 py-2 dark:bg-orange-900/10">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.item_name}</div>
                              <div className="text-xs text-orange-500">{getAdditionalNominal(item)}</div>
                            </div>
                            <div className="ml-2 flex shrink-0 items-center gap-1">
                              <span className="mr-1 text-sm font-medium text-orange-600 dark:text-orange-400">{getAdditionalCalculatedAmount(item, paymentTotals.base)}</span>
                              {canDeleteCartItems && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    haptic(confirmOpen ? 'close' : 'warning');
                                    setDeleteCandidate(confirmOpen ? null : item);
                                  }}
                                  disabled={!canRemoveDiscountItem}
                                  className={`cart-btn flex h-7 w-7 items-center justify-center rounded-full text-sm leading-none ${
                                    canRemoveDiscountItem
                                      ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                                      : 'bg-gray-200 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                                  }`}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                          {confirmOpen && canRemoveDiscountItem && (
                            <div className="mt-2 flex items-center gap-2 border-t border-orange-200 pt-2 dark:border-orange-800">
                              <span className="flex-1 text-xs text-gray-500 dark:text-gray-400">Удалить скидку?</span>
                              <button
                                onClick={() => deleteItem(item.id, { silent: true, successMessage: 'Скидка удалена' })}
                                disabled={actionLoading}
                                className="cart-btn flex h-6 items-center rounded-full bg-red-500 px-3 text-xs font-medium text-white disabled:opacity-50"
                              >
                                Удалить
                              </button>
                              <button
                                onClick={() => {
                                  haptic('close');
                                  setDeleteCandidate(null);
                                }}
                                className="cart-btn flex h-6 items-center rounded-full bg-gray-200 px-3 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                              >
                                Отмена
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          )}

          {(canChangeOrderComposition || canEditOrderServices || canEditDiscounts) && (
            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
              {canChangeOrderComposition && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('open');
                    setShowItemSelector(true);
                  }}
                  className="flex h-8 items-center justify-center gap-1 rounded-lg border-2 border-blue-500 px-2 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  Предметы
                </button>
              )}
              {canEditOrderServices && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('open');
                    setShowOrderServiceModal(true);
                  }}
                  className={`h-8 rounded-lg border-2 border-purple-400 px-2 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-950/30 ${
                    canChangeOrderComposition ? '' : 'col-start-2'
                  }`}
                >
                  Услуги
                </button>
              )}
              {canEditDiscounts && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('open');
                    setShowDiscountModal(true);
                  }}
                  className={`h-8 rounded-lg border-2 border-orange-400 px-2 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-950/30 ${
                    canChangeOrderComposition ? '' : (canEditOrderServices ? '' : 'col-start-3')
                  }`}
                >
                  Скидки
                </button>
              )}
            </div>
          )}
            </>
          )}
        </AccordionSection>

        <AccordionSection
          title="Комментарии"
          icon={MessageSquare}
          count={comments.length}
          unread={hasUnreadComments}
          className="order-4"
          contentClassName="p-0"
          open={expandedSections.comments}
          onToggle={() => toggleSection('comments')}
        >
          {() => (
          <div className="max-h-[320px] overflow-y-auto p-2">
            <div className="mb-2 flex justify-end">
              {canAddComments && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('open');
                    setShowCommentModal(true);
                  }}
                  className="flex h-8 items-center gap-1 rounded-lg border-2 border-blue-500 px-2.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  Добавить
                </button>
              )}
            </div>
            {comments.length === 0 ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">Комментариев пока нет</p>
            ) : (
              <div className="space-y-1.5 text-sm">
                {comments.map((comment) => (
                  <div key={comment.id} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/70">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{comment.author_name}</span>
                      <span className="shrink-0 text-[11px] text-gray-500">{formatTimezoneDate(comment.created_date, true)}</span>
                    </div>
                    <p className="mt-1 break-words text-xs leading-4 text-gray-700 dark:text-gray-300">{comment.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </AccordionSection>

        <AccordionSection
          title="Фото заказа"
          icon={ImageIcon}
          count={photos.length}
          className="order-5"
          contentClassName="p-0"
          open={expandedSections.photos}
          onToggle={() => toggleSection('photos')}
        >
          {() => (
          <div className="max-h-[340px] overflow-y-auto p-2">
            <div className="mb-2 flex justify-end">
              {canAddPhotos && (
                <button
                  type="button"
                  onClick={() => {
                    haptic('open');
                    setShowPhotoModal(true);
                  }}
                  className="flex h-8 items-center gap-1 rounded-lg border-2 border-blue-500 px-2.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  Добавить
                </button>
              )}
            </div>
            {photos.length === 0 && !uploadingPlaceholder ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">Фото пока нет</p>
            ) : (
              <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10">
                {uploadingPlaceholder && (
                  <div className="group relative overflow-hidden rounded-lg bg-gray-100 text-left ring-2 ring-blue-500 dark:bg-gray-800">
                    <div className="relative aspect-square bg-gray-900">
                      <img
                        src={uploadingPlaceholder.previewUrl}
                        alt="Загрузка"
                        className="h-full w-full object-cover filter blur-[1px] brightness-50"
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                        <CircularProgress
                          progress={uploadingPlaceholder.progress}
                          size={36}
                          strokeWidth={3}
                          indicatorColor="text-white"
                          trackColor="text-white/20"
                          showPercent={true}
                        />
                      </div>
                    </div>
                    <div className="space-y-0.5 px-2 py-1.5">
                      <div className="truncate text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                        {typeof uploadingPlaceholder.progress === 'number' && uploadingPlaceholder.progress < 100
                          ? `Загрузка ${uploadingPlaceholder.progress}%`
                          : 'Сохранение...'}
                      </div>
                      <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                        {uploadingPlaceholder.caption || 'Фото'}
                      </div>
                    </div>
                  </div>
                )}
                {photos.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => {
                      haptic('open');
                      setSelectedPhoto(photo);
                    }}
                    className="group overflow-hidden rounded-lg bg-gray-100 text-left ring-1 ring-gray-200 transition-all hover:ring-2 hover:ring-blue-500/50 dark:bg-gray-800 dark:ring-gray-700"
                  >
                    <div className="aspect-square bg-gray-200 dark:bg-gray-800">
                      <ProtectedImage
                        src={getOrderPhotoUrl(photo, 'thumbnail')}
                        alt={photo.caption || photo.item_name || 'Фото заказа'}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="space-y-0.5 px-2 py-1.5">
                      <div className="truncate text-[11px] font-medium text-gray-900 dark:text-gray-100">
                        {photo.item_number ? photo.item_number : 'К заказу'}
                      </div>
                      <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                        {photo.caption || photo.item_name || photo.uploaded_by_name || 'Без подписи'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </AccordionSection>

        <AccordionSection
          title="История"
          icon={History}
          className="order-6"
          contentClassName="p-0"
          open={expandedSections.history}
          onToggle={() => toggleSection('history')}
        >
          {() => (
          <div className="max-h-[320px] overflow-y-auto p-2">
            {effectiveActivityItems.length === 0 ? (
              <p className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">История пока пустая</p>
            ) : (
              <div className="space-y-1.5 text-sm">
                {effectiveActivityItems.map((item) => {
                  const details = getActivityDetails(item);
                  const snapshot = getActivitySnapshot(item);
                  const snapshotOpen = !!expandedActivityIds[item.id];
                  const repeatBadge = getActivityRepeatBadge(item, order?.revisions || []);
                  const isCreation = item.action === 'order_created';
                  const creator = item.user_name || (isCreation ? order?.creator_name : null);
                  return (
                    <div key={item.id} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/70">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex items-center gap-2">
                          <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{getActivityLabel(item.action)}</span>
                          {repeatBadge && (
                            <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-300">
                              {repeatBadge.label}
                            </span>
                          )}
                        </div>
                        <span className="shrink-0 text-[11px] text-gray-500">{formatTimezoneDate(item.created_date, true)}</span>
                      </div>
                      {details && <div className="mt-1 break-words text-xs leading-4 text-gray-700 dark:text-gray-200">{details}</div>}
                      {snapshot && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              haptic(snapshotOpen ? 'close' : 'open');
                              toggleActivitySnapshot(item.id);
                            }}
                            className="mt-2 inline-flex h-7 items-center gap-1 rounded-lg bg-blue-50 px-2 text-[11px] font-medium text-blue-600 transition-colors hover:bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                          >
                            Состав
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${snapshotOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {snapshotOpen && <InitialOrderSnapshot snapshot={snapshot} />}
                        </>
                      )}
                      {creator ? (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                          {isCreation && <span>Создал:</span>}
                          <span className={isCreation ? 'font-medium text-gray-700 dark:text-gray-200' : ''}>
                            {creator}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </AccordionSection>

        {hasInvoiceAttachment && (
          <AccordionSection
            title="Вложения"
            icon={Paperclip}
            count={documentAttachmentsCount}
            className="order-7"
            contentClassName="p-0"
            open={expandedSections.attachments}
            onToggle={() => toggleSection('attachments')}
          >
            {() => (
            <div className="space-y-2 p-2">
              {invoiceIsPrepared && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-800/70">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 dark:bg-blue-950/20 dark:text-blue-300 dark:ring-blue-900/40">
                          <Paperclip className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Счёт на оплату {order?.invoice_number ? `№ ${order.invoice_number}` : ''}
                          </div>
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {invoiceAttachmentFileName}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {invoicePreparedAt ? `Сохранили ${formatTimezoneDate(invoicePreparedAt, false)}` : 'Счёт сохранён'}
                        {invoicePreparedByName ? ` · ${invoicePreparedByName}` : ''}
                      </div>
                    </div>

                    <div className="flex sm:min-w-[auto] sm:justify-end">
                      <button
                        type="button"
                        onClick={handleDownloadInvoicePdf}
                        disabled={!canDownloadInvoicePdf}
                        title={invoiceDownloading ? 'Готовлю PDF...' : 'Скачать PDF счёта'}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {actIsPrepared && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-800/70">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-300 dark:ring-emerald-900/40">
                          <Paperclip className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Акт {order?.act_number ? `№ ${order.act_number}` : ''}
                          </div>
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {actAttachmentFileName}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {actPreparedAt ? `Сохранили ${formatTimezoneDate(actPreparedAt, false)}` : 'Акт сохранён'}
                        {actPreparedByName ? ` · ${actPreparedByName}` : ''}
                      </div>
                    </div>

                    <div className="flex sm:min-w-[auto] sm:justify-end">
                      <button
                        type="button"
                        onClick={handleDownloadActPdf}
                        disabled={!canDownloadActPdf}
                        title={actDownloading ? 'Готовлю PDF...' : 'Скачать PDF акта'}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {documentsPackageIsPrepared && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-800/70">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 ring-1 ring-violet-100 dark:bg-violet-950/20 dark:text-violet-300 dark:ring-violet-900/40">
                          <Paperclip className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Пакет документов
                          </div>
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {documentsPackageFileName}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Счёт и акт в одном PDF для скачивания и печати
                      </div>
                    </div>

                    <div className="flex sm:min-w-[auto] sm:justify-end">
                      <button
                        type="button"
                        onClick={handleDownloadDocumentsPackagePdf}
                        disabled={!canDownloadDocumentsPackagePdf}
                        title={documentsPackageDownloading ? 'Готовлю PDF...' : 'Скачать пакет PDF'}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {hasPreClosingDocuments && (
                <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                  <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Версия документов до закрытия
                  </div>
                  <div className="space-y-2">
                    {closingInvoiceIsPrepared && (
                      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-3 dark:border-amber-800/60 dark:bg-amber-950/20">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800">
                                <Paperclip className="h-5 w-5" />
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Счёт до закрытия {order?.closing_invoice_number ? `№ ${order.closing_invoice_number}` : ''}
                                </div>
                                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                                  {invoiceAttachmentFileName.replace('.pdf', '_до_закрытия.pdf')}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                              Исходная версия счёта до закрытия заказа
                              {closingInvoicePreparedAt ? ` · ${formatTimezoneDate(closingInvoicePreparedAt, false)}` : ''}
                            </div>
                          </div>

                          <div className="flex sm:min-w-[auto] sm:justify-end">
                            <button
                              type="button"
                              onClick={() => handleDownloadInvoicePdf({ version: 'pre_closing' })}
                              disabled={invoiceDownloading}
                              title="Скачать счёт до закрытия"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {closingActIsPrepared && (
                      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-3 dark:border-amber-800/60 dark:bg-amber-950/20">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800">
                                <Paperclip className="h-5 w-5" />
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Акт до закрытия {order?.closing_act_number ? `№ ${order.closing_act_number}` : ''}
                                </div>
                                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                                  {actAttachmentFileName.replace('.pdf', '_до_закрытия.pdf')}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                              Исходная версия акта до закрытия заказа
                              {closingActPreparedAt ? ` · ${formatTimezoneDate(closingActPreparedAt, false)}` : ''}
                            </div>
                          </div>

                          <div className="flex sm:min-w-[auto] sm:justify-end">
                            <button
                              type="button"
                              onClick={() => handleDownloadActPdf({ version: 'pre_closing' })}
                              disabled={actDownloading}
                              title="Скачать акт до закрытия"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {closingPackageIsPrepared && (
                      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-3 dark:border-amber-800/60 dark:bg-amber-950/20">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-800">
                                <Paperclip className="h-5 w-5" />
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  Пакет документов до закрытия
                                </div>
                                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                                  {documentsPackageFileName.replace('.pdf', '_до_закрытия.pdf')}
                                </div>
                              </div>
                            </div>
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                              Счёт и акт до закрытия в общем PDF-файле
                            </div>
                          </div>

                          <div className="flex sm:min-w-[auto] sm:justify-end">
                            <button
                              type="button"
                              onClick={() => handleDownloadDocumentsPackagePdf({ version: 'pre_closing' })}
                              disabled={documentsPackageDownloading}
                              title="Скачать пакет документов до закрытия"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}
          </AccordionSection>
        )}
      </div>
      </div>

      {canStartRepeat && (
        <section
          className="-mx-4 mt-auto border-y border-gray-200 bg-white/98 px-4 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/98 dark:shadow-[0_-8px_24px_rgba(2,6,23,0.28)] sm:mx-0 sm:rounded-lg sm:border sm:bg-white sm:p-4 sm:shadow-none dark:sm:bg-gray-900"
          style={{ marginBottom: DETAIL_PAGE_BOTTOM_COMPENSATION }}
        >
          <button
            onClick={() => {
              haptic('open');
              setShowRepeatOrderModal(true);
            }}
            disabled={actionLoading}
            className="h-12 w-full rounded-lg bg-amber-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-50"
          >
            {actionLoading ? 'Выполняю...' : 'Открыть повтор'}
          </button>
        </section>
      )}

      {!['completed', 'cancelled'].includes(order.status) && (canResolveDeferredOrder || canUseWorkActions || canTakeAvailableOrder || canTakeAvailableWashingFlow) && (
        <section
          className="-mx-4 mt-auto border-y border-gray-200 bg-white/98 px-4 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/98 dark:shadow-[0_-8px_24px_rgba(2,6,23,0.28)] sm:mx-0 sm:rounded-lg sm:border sm:bg-white sm:p-4 sm:shadow-none dark:sm:bg-gray-900"
          style={{ marginBottom: DETAIL_PAGE_BOTTOM_COMPENSATION }}
        >
          <div className="space-y-2">
            {canResolveDeferredOrder ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    haptic('warning');
                    setShowCancelConfirm(true);
                  }}
                  disabled={actionLoading}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-red-500 bg-red-50 px-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50 dark:bg-red-950/25 dark:text-red-300"
                >
                  <XCircle className="h-4 w-4" />
                  Отменить
                </button>
                {canRequestDeferredRework && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('warning');
                      setShowReworkModal(true);
                    }}
                    disabled={actionLoading || repeatProcessingItems.length === 0}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-amber-500 bg-amber-50 px-3 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-950/25 dark:text-amber-200 dark:hover:bg-amber-950/40"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Вернуть на доработку
                  </button>
                )}
                <button
                  type="button"
                  onClick={openDeferredResolveModal}
                  disabled={actionLoading}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Вернуть в работу
                </button>
              </div>
            ) : canUseWorkActions ? (
              <>
                {canRequestRework && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('warning');
                      setShowReworkModal(true);
                    }}
                    disabled={actionLoading || repeatProcessingItems.length === 0}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-amber-500 bg-amber-50 px-4 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-950/25 dark:text-amber-200 dark:hover:bg-amber-950/40"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Вернуть на доработку
                  </button>
                )}
                {canDeferOrder && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('warning');
                      setShowDeferModal(true);
                    }}
                    disabled={actionLoading}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-amber-500 bg-amber-50 px-4 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-950/25 dark:text-amber-200 dark:hover:bg-amber-950/40"
                  >
                    <Clock className="h-4 w-4" />
                    Отложить задание
                  </button>
                )}
                <button
                  onClick={handleCompleteButtonClick}
                  disabled={actionLoading}
                  aria-disabled={!canCompleteStage}
                  className={`h-12 w-full rounded-lg px-4 text-sm font-semibold text-white ${canCompleteStage ? 'bg-blue-600' : 'cursor-not-allowed bg-blue-600/45'}`}
                >
                  {actionLoading ? 'Выполняю...' : completeLabel}
                </button>
              </>
            ) : (canTakeAvailableOrder || canTakeAvailableWashingFlow) ? (
              <button
                onClick={() => setShowTakeOrderConfirm(true)}
                disabled={actionLoading}
                className="h-12 w-full rounded-lg bg-green-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Выполняю...' : (canTakeAvailableWashingFlow ? 'Взять поток в работу' : 'Взять в работу')}
              </button>
            ) : null}
          </div>
        </section>
      )}

      <ItemSelectorModal
        open={showItemSelector}
        onClose={() => {
          haptic('close');
          setShowItemSelector(false);
        }}
        onAdd={addItem}
        onReplace={replaceItem}
        existingItems={existingMainItems}
        clientType={order.client_type || 'b2c'}
        serviceType={serviceTypeForModal}
        priceAt={order.created_date}
        priceCoefficient={order.price_coefficient || 1}
      />

      <ProductionForecastModal
        open={showProductionForecastModal}
        onClose={() => {
          haptic('close');
          setShowProductionForecastModal(false);
        }}
        orderId={order.id}
        refreshKey={order.updated_date}
      />

      {(() => {
        const selectedMainItem = selectedItem ? (mainItems.find((item) => item.id === selectedItem.id) || selectedItem) : null;
        const canEditMeasurementsSelectedItem = !!(
          selectedMainItem && (
            canChangeOrderComposition
            || (canOpenItemEditor && !['drying_packing', 'approval', 'delivery', 'handover', 'completed'].includes(order?.status) && canProcessSpecificItem(selectedMainItem))
          )
        );
        const canEditServicesSelectedItem = !!(
          selectedMainItem && (
            canChangeOrderComposition
            || (canOpenItemEditor && canProcessSpecificItem(selectedMainItem))
          )
        );
        const canEditStorageCellSelectedItem = !!(
          selectedMainItem?.processing_route === 'cleaning'
          && (
            (order?.status === 'drying_packing' && canProcessItems && canProcessSpecificItem(selectedMainItem))
            || (['approval', 'delivery', 'handover'].includes(order?.status) && (hasEffectivePrivilegedAccess || canUseDispatcherCorrections))
          )
        );
        const canEditSelectedItem = canEditMeasurementsSelectedItem || canEditServicesSelectedItem || canEditStorageCellSelectedItem;
        const currentItemStorage = selectedMainItem ? getItemDryingStorage(selectedMainItem) : null;

        return (
          <ItemDetailModal
            open={!!selectedItem}
            onClose={() => {
              haptic('close');
              setSelectedItem(null);
            }}
            item={selectedItem}
            itemIndex={selectedItem ? mainItems.findIndex((item) => item.id === selectedItem.id) : null}
            itemDisplayNumber={selectedItem
              ? getItemDisplayNumber(
                  order,
                  selectedItem,
                  mainItems.findIndex((item) => item.id === selectedItem.id)
                )
              : ''}
            serviceType={order?.service_type}
            clientType={order?.client_type || 'b2c'}
            onItemUpdate={saveItemDetails}
            canEdit={canEditSelectedItem}
            canEditMeasurements={canEditMeasurementsSelectedItem}
            canEditStorageCell={canEditStorageCellSelectedItem}
            storageCells={cleaningStorageCells}
            storageCell={currentItemStorage}
            orderStatus={order?.status}
            priceAt={order?.created_date}
            priceCoefficient={order?.price_coefficient || 1}
          />
        );
      })()}

      <OrderServiceModal
        open={showOrderServiceModal}
        onClose={() => {
          haptic('close');
          setShowOrderServiceModal(false);
        }}
        onAdd={addOrderService}
        onRemove={removeOrderService}
        clientType={order.client_type || 'b2c'}
        existingItems={existingAdditionalItems}
        priceAt={order.created_date}
      />

      <DiscountSelectorModal
        open={showDiscountModal}
        onClose={() => {
          haptic('close');
          setShowDiscountModal(false);
        }}
        onSelect={addDiscount}
        onRemove={removeDiscount}
        clientType={order.client_type || 'b2c'}
        existingItems={existingAdditionalItems}
        orderContext={{
          ...discountOrderContext,
          itemsCount: mainItemsCount
        }}
        currentUserType={currentUserType}
        currentUserTypeLoaded={currentUserTypeLoaded}
        canManageSystemDiscounts={currentUserTypeLoaded && canEditDiscounts && ['dispatcher', 'admin', 'developer'].includes(currentUserType)}
        onBlocked={(message) => showToast('error', message)}
      />

      <CommentBottomSheet
        open={showCommentModal}
        initialText=""
        onClose={() => setShowCommentModal(false)}
        onSubmit={submitComment}
        loading={actionLoading}
      />

      <ReworkBottomSheet
        open={showReworkModal}
        items={repeatProcessingItems}
        source={isDeferredOrder && order?.status === 'delivery' ? 'deferred_delivery' : 'drying'}
        loading={actionLoading}
        onClose={() => {
          haptic('close');
          setShowReworkModal(false);
        }}
        onSubmit={requestRework}
      />

      <StorageCellAssignmentSheet
        open={showCompleteAllConfirm}
        mode={areAllMainItemsDone ? 'assign-all' : 'complete-all'}
        items={completeAllSheetItems}
        cells={cleaningStorageCells}
        storageRequired={storageCellsRequired}
        loading={actionLoading}
        onClose={() => setShowCompleteAllConfirm(false)}
        onSubmit={async (assignments) => {
          const ok = await completeAllProcessingItems(assignments);
          if (ok) setShowCompleteAllConfirm(false);
        }}
      />

      <StorageCellAssignmentSheet
        open={!!storageCellSheet}
        mode={storageCellSheet?.mode || 'assign-one'}
        items={storageCellSheet?.items || []}
        cells={cleaningStorageCells}
        storageRequired={storageCellsRequired}
        loading={actionLoading}
        onClose={() => setStorageCellSheet(null)}
        onSubmit={storageCellSheet?.mode === 'complete-one'
          ? completeItemWithStorageCell
          : saveItemStorageCell}
      />

      <ConfirmBottomSheet
        open={!!approvedCostChangeConfirm}
        title={approvedCostChangeConfirm?.title || 'Изменить согласованную стоимость?'}
        text={approvedCostChangeConfirm?.text || 'Изменение корзины пересчитает уже согласованную стоимость заказа.'}
        confirmLabel={approvedCostChangeConfirm?.confirmLabel || 'Изменить'}
        loading={actionLoading}
        onClose={() => setApprovedCostChangeConfirm(null)}
        onConfirm={async () => {
          const pendingAction = approvedCostChangeConfirm?.action;
          setApprovedCostChangeConfirm(null);
          await pendingAction?.();
        }}
        icon={AlertTriangle}
        iconClassName="bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40"
        confirmClassName="bg-amber-600 text-white"
        zIndexClass="!z-[100120]"
      />

      <ConfirmBottomSheet
        open={showCompleteConfirm}
        title={completeLabel}
        text={completeConfirmText}
        confirmLabel={completeLabel}
        loading={actionLoading}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={completeCurrentStage}
        icon={CheckCircle}
      >
        {order.status === 'drying_packing' && isMixedProcessingFlowOrder && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-100">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />
              <div>
                <div className="font-semibold">Заказ совмещённый</div>
                <p className="mt-0.5 leading-5 text-amber-800 dark:text-amber-200">
                  В нём есть предметы потоков «Прачечная» и «Чистка». После подтверждения заказ перейдёт на согласование.
                </p>
              </div>
            </div>
          </div>
        )}
      </ConfirmBottomSheet>

      <CancelOrderBottomSheet
        open={showCancelConfirm}
        loading={actionLoading}
        onClose={() => setShowCancelConfirm(false)}
        onSubmit={isDeferredOrder ? cancelOrder : (isRepeatOrder ? cancelActiveRevision : cancelOrder)}
        title={cancelModalConfig.title}
        description={cancelModalConfig.description}
        reasonLabel={cancelModalConfig.reasonLabel}
        placeholder={cancelModalConfig.placeholder}
        submitLabel={cancelModalConfig.submitLabel}
      />

      <DeferOrderBottomSheet
        open={showDeferModal}
        loading={actionLoading}
        onClose={() => setShowDeferModal(false)}
        onSubmit={deferCurrentOrder}
      />

      <ResolveDeferredBottomSheet
        open={showDeferredResolveModal}
        loading={actionLoading}
        assignees={deferredAssignees}
        assigneesLoading={deferredAssigneesLoading}
        routeSuggestions={deferredRouteSuggestions}
        routeSuggestionsLoading={deferredRouteSuggestionsLoading}
        onClose={() => setShowDeferredResolveModal(false)}
        onSubmit={resolveDeferredOrder}
      />

      <RepeatOrderBottomSheet
        open={showRepeatOrderModal}
        loading={actionLoading}
        pickupAddressText={repeatPickupSourceAddressText}
        serviceType={order?.service_type || ''}
        onClose={() => setShowRepeatOrderModal(false)}
        onSubmit={startRepeatOrder}
      />

      <ConfirmBottomSheet
        open={showTakeOrderConfirm}
        title={canTakeAvailableWashingFlow ? 'Взять поток в работу?' : 'Взять заказ в работу?'}
        text={canTakeAvailableWashingFlow ? 'Поток стирки будет назначен на вас.' : 'Заказ будет назначен на вас.'}
        confirmLabel={canTakeAvailableWashingFlow ? 'Взять поток' : 'Взять в работу'}
        loading={actionLoading}
        onClose={() => setShowTakeOrderConfirm(false)}
        onConfirm={canTakeAvailableWashingFlow ? takeWashingFlowIntoWork : takeOrderIntoWork}
        icon={Check}
        iconClassName="bg-green-50 text-green-600 ring-green-100 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-900/40"
        confirmClassName="bg-green-600 text-white"
      />

      <ConfirmBottomSheet
        open={showResetApprovalConfirm}
        title="Сбросить условия возврата?"
        text="После этого нужно будет заново указать способ возврата, дату, интервал и адрес."
        confirmLabel="Сбросить"
        loading={actionLoading}
        onClose={() => setShowResetApprovalConfirm(false)}
        onConfirm={async () => {
          const ok = await resetApprovalSettings();
          if (ok) {
            setShowResetApprovalConfirm(false);
          }
        }}
        icon={AlertTriangle}
        iconClassName="bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40"
        confirmClassName="bg-amber-600 text-white"
      />

      <ConfirmBottomSheet
        open={showPaidConfirm}
        title="Подтвердить оплату?"
        text="После подтверждения заказ будет отмечен как оплаченный. Отменить оплату и изменить способ оплаты будет нельзя."
        confirmLabel="Подтвердить"
        loading={actionLoading}
        onClose={() => setShowPaidConfirm(false)}
        onConfirm={markOrderPaid}
        icon={AlertTriangle}
        iconClassName="bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40"
        confirmClassName="bg-blue-600 text-white"
      />

      {showAssigneeModal && (
        <ModalShell
          open={showAssigneeModal}
          onClose={() => {
            haptic('close');
            setShowAssigneeModal(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {isFlowAssigneeSelection ? 'Исполнители этапов и потоков' : 'Исполнитель этапа'}
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
                    {isFlowAssigneeSelection
                      ? 'Выберите этап или поток для назначения исполнителя.'
                      : 'Доступны сотрудники этого подразделения с правом на текущий этап.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setShowAssigneeModal(false);
                }}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              {assigneesLoading ? (
                <div className="rounded-xl bg-gray-50 px-3 py-4 text-center text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  Загружаю сотрудников...
                </div>
              ) : (
                <>
                  {isFlowAssigneeSelection && (
                    <div className={`grid gap-2 ${availableAssigneeFlows.length > 2 ? 'grid-cols-1 sm:grid-cols-3' : availableAssigneeFlows.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {availableAssigneeFlows.map((flow) => {
                        const FlowIcon = STAGES[flow.stage_name]?.icon || UserCheck;
                        const isSelected = flow.stage_name === selectedAssigneeFlow?.stage_name;
                        return (
                          <button
                            key={flow.stage_name}
                            type="button"
                            onClick={() => {
                              haptic('selection');
                              setAssigneeStageDraft(flow.stage_name);
                            }}
                            disabled={actionLoading}
                            className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                              isSelected
                                ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50/40 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-200 dark:hover:border-blue-900'
                            }`}
                          >
                            <span className="inline-flex items-center gap-2 text-sm font-semibold">
                              <FlowIcon className="h-4 w-4 shrink-0" />
                              {flow.label}
                            </span>
                            <span className="mt-1 block truncate text-xs text-gray-500 dark:text-gray-400">
                              {flow.assignee_name || 'Не назначен'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {isFlowAssigneeSelection && selectedAssigneeFlow?.can_assign === false ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
                      Поток уже завершён, менять исполнителя нельзя.
                    </div>
                  ) : (selectedAssigneeUsers.length === 0 && selectedAssigneeCurrentValue === UNASSIGN_ASSIGNEE_VALUE) ? (
                    <div className="rounded-xl bg-gray-50 px-3 py-4 text-center text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      {isFlowAssigneeSelection
                        ? `Нет доступных исполнителей для потока ${selectedAssigneeStageLabel.toLowerCase()}`
                        : 'Нет доступных исполнителей для этого этапа'}
                    </div>
                  ) : (
                    <Dropdown
                      label={isFlowAssigneeSelection ? 'Исполнитель потока' : 'Исполнитель этапа'}
                      value={assigneeDraft}
                      options={[
                        {
                          value: UNASSIGN_ASSIGNEE_VALUE,
                          label: isFlowAssigneeSelection ? `Снять исполнителя потока ${selectedAssigneeStageLabel}` : 'Снять исполнителя',
                          icon: XCircle
                        },
                        ...selectedAssigneeUsers.map((user) => ({
                          value: user.id,
                          label: `${user.name || user.login}${user.login ? ` · ${user.login}` : ''}`,
                          icon: UserCheck
                        }))
                      ]}
                      onChange={(value) => setAssigneeDraft(value ? String(value) : UNASSIGN_ASSIGNEE_VALUE)}
                      placeholder={isFlowAssigneeSelection ? `Выберите исполнителя для потока ${selectedAssigneeStageLabel.toLowerCase()}` : 'Выберите исполнителя'}
                      disabled={actionLoading}
                      buttonClassName="py-2.5 text-xs"
                      itemClassName="py-2.5 text-xs"
                    />
                  )}
                </>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setShowAssigneeModal(false);
                }}
                disabled={actionLoading}
                className="h-10 rounded-lg border-2 border-gray-300 px-3 text-sm font-semibold text-gray-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={saveAssigneeSelection}
                disabled={assigneeModalSaveDisabled}
                className="h-10 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Сохраняю...' : (assigneeDraftIsUnassign ? 'Снять' : 'Сохранить')}
              </button>
            </div>
        </ModalShell>
      )}

      <PhotoBottomSheet
        open={showPhotoModal}
        order={order}
        items={mainItems}
        loading={actionLoading}
        uploadProgress={uploadProgress}
        onClose={() => setShowPhotoModal(false)}
        onSubmit={uploadPhoto}
      />

      <PhotoViewer
        photo={selectedPhoto}
        loading={actionLoading}
        onClose={() => setSelectedPhoto(null)}
        onDelete={(photo) => setPhotoToDelete(photo)}
        canDelete={!isLimitedArchiveAccess && (currentUserType === 'admin' || currentUserType === 'developer')}
      />

      <ConfirmModal
        isOpen={Boolean(photoToDelete)}
        title="Удалить фото безвозвратно?"
        message="Фотография будет удалена с сервера безвозвратно. Это действие нельзя отменить."
        confirmText="Удалить безвозвратно"
        cancelText="Отмена"
        type="danger"
        onConfirm={async () => {
          const photo = photoToDelete;
          setPhotoToDelete(null);
          if (photo) {
            await deletePhoto(photo);
          }
        }}
        onCancel={() => setPhotoToDelete(null)}
      />

      {showApprovalRouteSettings && (
        <ModalShell
          open={showApprovalRouteSettings}
          onClose={() => {
            haptic('close');
            setShowApprovalRouteSettings(false);
          }}
          variant="window"
          panelClassName="flex max-h-[78dvh] w-full max-w-md flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Настройки списка</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Только для блока назначенных выездов</div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowApprovalRouteSettings(false);
              }}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto p-4">
            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Какие выезды показывать</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteDepartmentScope('all');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    approvalRouteDepartmentScope === 'all'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Все подразделения
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteDepartmentScope('selected');
                  }}
                  disabled={!order?.department_id}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    approvalRouteDepartmentScope === 'selected'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Подразделение заказа
                </button>
              </div>
              {!order?.department_id && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">У заказа не выбрано подразделение.</div>
              )}
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Как показывать подразделения</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteDepartmentView('grouped');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    approvalRouteDepartmentView === 'grouped'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  По подразделениям
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteDepartmentView('mixed');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    approvalRouteDepartmentView === 'mixed'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Общий список
                </button>
              </div>
            </div>

            {approvalRouteDepartmentView === 'mixed' && (
              <div>
                <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать подразделение в карточке</div>
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setApprovalRouteShowDepartmentBadge(true);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvalRouteShowDepartmentBadge
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Показывать
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setApprovalRouteShowDepartmentBadge(false);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      !approvalRouteShowDepartmentBadge
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Скрывать
                  </button>
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать исполнителя в карточке</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteShowExecutor(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    approvalRouteShowExecutor
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteShowExecutor(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !approvalRouteShowExecutor
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать область в адресе</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteHideRegion(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !approvalRouteHideRegion
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setApprovalRouteHideRegion(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    approvalRouteHideRegion
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}

      <ModalShell
        open={Boolean(phoneActionModal)}
        onClose={() => {
          haptic('close');
          setPhoneActionModal(null);
        }}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-900"
      >
        {phoneActionModal && (
          <div>
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
                <Phone className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Телефон клиента</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">{phoneActionModal.phone}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  haptic('selection');
                  setPhoneActionModal(null);
                  const copied = await copyText(phoneActionModal.phone);
                  showToast(copied ? 'success' : 'error', copied ? 'Номер скопирован' : 'Не удалось скопировать номер');
                }}
                className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <span className="inline-flex items-center gap-2">
                  <Copy className="h-4 w-4" />
                  Скопировать
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  if (phoneActionModal.href) {
                    window.location.href = phoneActionModal.href;
                  }
                  setPhoneActionModal(null);
                }}
                className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Позвонить
              </button>
            </div>
          </div>
        )}
      </ModalShell>

      {showDispatcherReturnModal && (
        <ModalShell
          open={showDispatcherReturnModal}
          onClose={() => {
            haptic('close');
            setShowDispatcherReturnModal(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 shadow-sm ring-1 ring-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-900/40">
                <Calendar className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {isPickupStage ? 'Доставка от клиента' : 'Доставка клиенту'}
                </h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                  {isPickupStage
                    ? (isCleaningRepeatOrder
                      ? 'Можно изменить дату и интервал выезда. Повтор останется на этапе Химчистка мебели.'
                      : 'Можно перевести заказ на самодоставку или изменить дату и интервал забора.')
                    : 'Можно изменить дату и интервал доставки или перевести заказ на выдачу.'}
                </p>
              </div>
            </div>

            {isPickupStage ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  {!isCleaningRepeatOrder && (
                    <button
                      type="button"
                      onClick={() => {
                        if (mainItemsCount <= 0) {
                          showToast('info', 'Перевод на самодоставку недоступен: в корзине нет предметов');
                          return;
                        }
                        setDispatcherPickupDraft((prev) => ({ ...prev, mode: 'self_delivery' }));
                      }}
                      disabled={actionLoading}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        dispatcherPickupDraft.mode === 'self_delivery'
                          ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                          : mainItemsCount <= 0
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Самодоставка
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setDispatcherPickupDraft((prev) => ({ ...prev, mode: 'pickup' }))}
                    disabled={actionLoading}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      dispatcherPickupDraft.mode === 'pickup'
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Забор
                  </button>
                </div>

                {dispatcherPickupDraft.mode === 'pickup' ? (
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <DatePicker
                        value={dispatcherPickupDraft.pickupDate}
                        onChange={(value) => setDispatcherPickupDraft((prev) => ({ ...prev, pickupDate: value }))}
                        placeholder="Дата забора"
                        allowPastDates={false}
                        minDate={formatDateInputValue(getNowInSystemTimezone())}
                        inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                      />
                    </div>
                    <div className="flex-1">
                      <TimeSlotPicker
                        value={dispatcherPickupDraft.pickupTime}
                        onChange={(value) => setDispatcherPickupDraft((prev) => ({ ...prev, pickupTime: value }))}
                        placeholder="Интервал"
                        disabledBeforeMinutes={isDateTodayInSystemTimezone(dispatcherPickupDraft.pickupDate) ? getCurrentMinutesInSystemTimezone() : null}
                        inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm leading-5 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    Заказ уйдёт на этап Чистки, а текущий исполнитель будет снят.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setApprovalField('returnType', 'self_pickup');
                      }}
                    disabled={actionLoading}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvalDraft.returnType === 'self_pickup'
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Выдача
                  </button>
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setApprovalField('returnType', 'delivery');
                      }}
                    disabled={actionLoading}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      approvalDraft.returnType === 'delivery'
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Доставка
                  </button>
                </div>

                {approvalDraft.returnType === 'delivery' ? (
                  <>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <DatePicker
                          value={approvalDraft.deliveryDate}
                          onChange={(value) => setApprovalField('deliveryDate', value)}
                          placeholder="Дата доставки"
                          allowPastDates={false}
                          minDate={formatDateInputValue(getNowInSystemTimezone())}
                          inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                        />
                      </div>
                      <div className="flex-1">
                        <TimeSlotPicker
                          value={approvalDraft.deliveryTime}
                          onChange={(value) => setApprovalField('deliveryTime', value)}
                          placeholder="Интервал"
                          disabledBeforeMinutes={isDateTodayInSystemTimezone(approvalDraft.deliveryDate) ? getCurrentMinutesInSystemTimezone() : null}
                          inputClassName="h-10 px-3 pl-8 pr-8 text-[11px] placeholder:text-[11px] sm:text-xs"
                        />
                      </div>
                    </div>

                    {(
                      <RouteSuggestions
                        suggestions={approvalRouteSuggestions}
                        loading={approvalRouteSuggestionsLoading}
                        onApply={applyApprovalRouteSuggestion}
                        showWhenEmpty
                      />
                    )}

                    <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                      {approvalSelectedAddressText || 'Адрес доставки не указан'}
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200">
                    Заказ будет переведён на этап выдачи, клиент заберёт его сам.
                  </div>
                )}

                {approvalDraft.returnType && (
                  <div className="pt-1">
                    <AssigneePicker
                      label={approvalDraft.returnType === 'self_pickup' ? 'Исполнитель на выдачу' : 'Исполнитель на доставку'}
                      value={approvalDraft.returnAssignedTo}
                      onChange={(value) => setApprovalField('returnAssignedTo', value)}
                      users={approvalAssignees}
                      loading={approvalAssigneesLoading}
                      disabled={actionLoading || approvalAssigneesLoading}
                      fallbackName={savedReturnAssigneeName}
                      modalTitle={approvalDraft.returnType === 'self_pickup' ? 'Исполнитель на выдачу' : 'Исполнитель на доставку'}
                      helperText={
                        approvalDraft.returnType === 'self_pickup'
                          ? 'Сотрудники подразделения с правом на выдачу'
                          : 'Сотрудники подразделения с правом на доставку'
                      }
                    />
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setShowDispatcherReturnModal(false);
                }}
                className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={async () => {
                  const saved = isPickupStage
                    ? await saveDispatcherPickupSettings()
                    : await saveApprovalSettings({ dispatcherAccess: true });
                  if (saved) {
                    haptic('success');
                    setShowDispatcherReturnModal(false);
                  }
                }}
                disabled={
                  actionLoading
                  || (
                    isPickupStage
                      ? (
                        dispatcherPickupDraftIsSaved
                        || !dispatcherPickupDraft.mode
                        || (
                          dispatcherPickupDraft.mode === 'self_delivery'
                          && mainItemsCount <= 0
                        )
                        || (
                          dispatcherPickupDraft.mode === 'pickup'
                          && (
                            !dispatcherPickupDraft.pickupDate
                            || !dispatcherPickupDraft.pickupTime
                          )
                        )
                      )
                      : (
                        approvalDraftIsSaved
                        || !approvalDraft.returnType
                        || (
                          approvalDraft.returnType === 'delivery'
                          && (
                            !approvalDraft.deliveryDate
                            || !approvalDraft.deliveryTime
                            || !approvalSelectedAddressText
                            || !approvalAddressConfirmed
                          )
                        )
                      )
                  )
                }
                className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                Сохранить
              </button>
            </div>
        </ModalShell>
      )}

      {showDepartmentModal && (
        <ModalShell
          open={showDepartmentModal}
          onClose={() => {
            haptic('close');
            setShowDepartmentModal(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 shadow-sm ring-1 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-900/40">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Сменить подразделение</h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">Исполнитель будет снят. При желании можно пересчитать базовые цены предметов по коэффициенту нового подразделения.</p>
            </div>
          </div>

          <div className="space-y-3">
            {departmentsLoading ? (
              <div className="rounded-xl bg-gray-50 px-3 py-4 text-center text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                Загружаю подразделения...
              </div>
            ) : (
              <Dropdown
                label="Подразделение"
                value={departmentDraft}
                options={departments.map((department) => {
                  const coeff = getDepartmentPriceCoefficient(department, order?.client_type);
                  return {
                    value: department.id,
                    label: `${department.name || `Подразделение ${department.id}`}${coeff ? ` · x${formatPriceCoefficient(coeff)}` : ''}`,
                    icon: Building2
                  };
                })}
                onChange={(value) => setDepartmentDraft(value ? String(value) : '')}
                placeholder="Выберите подразделение"
                disabled={actionLoading}
                buttonClassName="py-2.5 text-xs"
                itemClassName="py-2.5 text-xs"
              />
            )}

            <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Пересчитать цены</div>
                <div className="mt-0.5 text-xs leading-4 text-gray-500 dark:text-gray-400">
                  Только базовые цены предметов. Доп. услуги и скидки не изменятся.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  setDepartmentRepriceDraft((prev) => !prev);
                }}
                disabled={actionLoading}
                className="toggle-switch shrink-0 disabled:opacity-50"
                style={{
                  '--toggle-bg': departmentRepriceDraft ? 'rgb(245 158 11)' : 'rgb(209 213 219)',
                  '--toggle-height': '22px',
                  '--toggle-width': '42px',
                  '--knob-x': departmentRepriceDraft ? '20px' : '2px',
                }}
                aria-pressed={departmentRepriceDraft}
                aria-label="Пересчитать цены"
              >
                <span className="toggle-switch-knob" />
              </button>
            </label>

            <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
              После смены подразделения текущий исполнитель будет снят.
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowDepartmentModal(false);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('success');
                saveDepartmentChange();
              }}
              disabled={actionLoading || !departmentDraft || String(order?.department_id || '') === String(departmentDraft)}
              className="min-h-[40px] flex-1 rounded-lg bg-amber-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
        </ModalShell>
      )}

      <DispatcherNotesModal
        open={showDispatcherNotesModal}
        currentText={order?.internal_notes || ''}
        loading={actionLoading}
        onClose={() => setShowDispatcherNotesModal(false)}
        onSave={saveDispatcherNotes}
      />

      <DispatcherPriorityModal
        open={showDispatcherPriorityModal}
        currentScope={dispatcherPriorityScope || 'none'}
        currentStage={dispatcherPriorityStage || ''}
        targetStage={currentDispatcherPriorityStage}
        loading={actionLoading}
        onClose={() => setShowDispatcherPriorityModal(false)}
        onSave={saveDispatcherPriority}
      />

      <DispatcherCallRequirementModal
        open={showDispatcherCallRequirementModal}
        currentStage={dispatcherCallRequiredStage || 'none'}
        canSelectPickup={canSelectPickupDispatcherCall}
        canSelectDelivery={canSelectDeliveryDispatcherCall}
        loading={actionLoading}
        onClose={() => setShowDispatcherCallRequirementModal(false)}
        onSave={saveDispatcherCallRequirement}
      />

      {showDispatcherAddressModal && (
        <ModalShell
          open={showDispatcherAddressModal}
          onClose={() => {
            haptic('close');
            setShowDispatcherAddressModal(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 shadow-sm ring-1 ring-sky-100 dark:bg-sky-900/20 dark:text-sky-300 dark:ring-sky-900/40">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Сменить адрес</h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">{dispatcherAddressLabel}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="relative">
              <div className="mb-1 flex items-center justify-end gap-2">
                {clientAddresses.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setDispatcherAddressesOpen((prev) => !prev);
                    }}
                    className="text-xs font-medium text-blue-600 dark:text-blue-300"
                  >
                    {dispatcherAddressesOpen ? 'Скрыть адреса' : `Адреса клиента (${clientAddresses.length})`}
                  </button>
                )}
              </div>
              <FloatingField label="Адрес" disabled={actionLoading}>
                <div className="relative">
                  <input
                    ref={dispatcherAddressInputRef}
                    type="text"
                    value={dispatcherAddressDraft.address}
                    onChange={(event) => handleDispatcherAddressChange(event.target.value)}
                    disabled={actionLoading}
                    className="w-full rounded-xl bg-white px-4 py-2.5 pr-12 text-[15px] text-gray-900 outline-none placeholder:text-[14px] placeholder:text-gray-400 dark:bg-gray-900 dark:text-gray-100"
                    placeholder="Начните вводить адрес..."
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => handleDispatcherAddressChange('')}
                      disabled={actionLoading || !dispatcherAddressDraft.address}
                      className={`pointer-events-auto flex h-5 w-5 items-center justify-center transition-colors ${
                        dispatcherAddressDraft.address
                          ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
                          : 'pointer-events-none text-transparent'
                      }`}
                      title="Очистить поле"
                      aria-label="Очистить поле"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </FloatingField>

              {showDispatcherAddressSuggestions && dispatcherAddressSuggestions.length > 0 && (
                <div className="absolute bottom-full left-0 z-20 mb-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {dispatcherAddressSuggestions.map((suggestion, index) => (
                    <button
                      key={`${getSuggestionAddressText(suggestion)}-${index}`}
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        selectDispatcherSuggestion(suggestion);
                      }}
                      className="w-full border-b border-gray-100 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {getSuggestionAddressText(suggestion)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {dispatcherAddressesOpen && clientAddresses.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800">
                {clientAddresses.map((address) => (
                  <button
                    key={address.id}
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      applyDispatcherAddress(address);
                    }}
                    className="flex w-full items-start justify-between gap-3 rounded-lg bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50 dark:bg-gray-900 dark:hover:bg-blue-950/20"
                  >
                    <span className="min-w-0 flex-1 break-words leading-5 text-gray-900 dark:text-gray-100">{address.address_full}</span>
                    <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 dark:bg-gray-800 dark:text-gray-300">
                      {address.is_primary ? 'Основной' : `${address.order_count || 1} ${pluralizeOrder(address.order_count || 1)}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setShowDispatcherAddressModal(false);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('success');
                saveDispatcherAddress();
              }}
              disabled={actionLoading || !dispatcherAddressDraft.address.trim()}
              className="min-h-[40px] flex-1 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
            >
              Сохранить
            </button>
          </div>
        </ModalShell>
      )}

      {showEmergencyDispatchConfirm && (
        <ModalShell
          open={showEmergencyDispatchConfirm}
          onClose={() => {
            haptic('close');
            setShowEmergencyDispatchConfirm(false);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-5"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-950/40 dark:text-fuchsia-300"><Siren className="h-5 w-5" /></div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{isEmergencyDispatch ? 'Снять экстренный выезд?' : 'Экстренный выезд'}</h3>
          </div>
          <p className="mt-3 text-sm leading-5 text-gray-600 dark:text-gray-300">{isEmergencyDispatch ? 'Снять специальную отметку с заказа?' : 'Исполнитель получит уведомление, что заказ нужно выполнить в экстренном порядке. Продолжить?'}</p>
          <div className="mt-5 flex gap-2">
            <button type="button" onClick={() => { haptic('close'); setShowEmergencyDispatchConfirm(false); }} className="min-h-10 flex-1 rounded-xl bg-gray-100 px-3 text-sm font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-100">Отмена</button>
            <button type="button" onClick={() => { haptic('warning'); confirmEmergencyDispatch(); }} className="min-h-10 flex-1 rounded-xl bg-fuchsia-600 px-3 text-sm font-semibold text-white hover:bg-fuchsia-700">{isEmergencyDispatch ? 'Снять' : 'Подтвердить'}</button>
          </div>
        </ModalShell>
      )}

      {mapConfirmModal && (
        <ModalShell
          open={Boolean(mapConfirmModal)}
          onClose={() => {
            haptic('close');
            setMapConfirmModal(null);
          }}
          container={(isOrderMapFullscreen && mapWrapperRef.current) ? mapWrapperRef.current : undefined}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm ring-1 ring-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:ring-blue-900/40">
              {mapConfirmModal.label === 'Яндекс.Навигатор' ? (
                <Navigation className="h-5 w-5" />
              ) : (
                <MapPin className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Открыть {mapConfirmModal.label}?
              </h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                {mapConfirmModal.address || 'Адрес заказа'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('open');
                window.open(mapConfirmModal.url, '_blank', 'noopener,noreferrer');
                setMapConfirmModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Открыть
            </button>
          </div>
        </ModalShell>
      )}

      <InvoiceEditorModal
        open={showInvoiceEditor}
        onClose={() => setShowInvoiceEditor(false)}
        draft={invoiceDraft}
        actDraft={actDraft}
        invoiceNumber={invoiceDraftData?.invoice_number || order?.invoice_number || null}
        actNumber={invoiceDraftData?.act_number || order?.act_number || null}
        preparedAt={invoicePreparedAt}
        preparedByName={invoicePreparedByName}
        actPreparedAt={actPreparedAt}
        actPreparedByName={actPreparedByName}
        loading={invoiceDraftLoading}
        saving={invoiceDraftSaving}
        downloading={invoiceDownloading}
        printing={invoicePrinting}
        actDownloading={actDownloading}
        actPrinting={actPrinting}
        packageDownloading={documentsPackageDownloading}
        packagePrinting={documentsPackagePrinting}
        canDownload={canDownloadInvoicePdf}
        canPrint={canPrintInvoicePdf}
        canDownloadAct={canDownloadActPdf}
        canPrintAct={canPrintActPdf}
        canDownloadPackage={canDownloadDocumentsPackagePdf}
        canPrintPackage={canPrintDocumentsPackagePdf}
        canEdit={canEditOrderDocuments}
        isCompletedOrder={isOrderCompleted}
        orderFinalAmount={order?.final_amount}
        hasPreClosingDocuments={hasPreClosingDocuments}
        onSave={saveInvoiceDraft}
        onDownload={handleDownloadInvoicePdf}
        onPrint={handlePrintInvoicePdf}
        onDownloadAct={handleDownloadActPdf}
        onPrintAct={handlePrintActPdf}
        onDownloadPackage={handleDownloadDocumentsPackagePdf}
        onPrintPackage={handlePrintDocumentsPackagePdf}
      />

      <AddressWithoutStreetConfirmModal
        suggestion={addressWithoutStreetSuggestion?.suggestion}
        onCancel={() => setAddressWithoutStreetSuggestion(null)}
        onConfirm={() => {
          const pending = addressWithoutStreetSuggestion;
          setAddressWithoutStreetSuggestion(null);
          if (pending?.target === 'approval') applyApprovalSuggestion(pending.suggestion);
          else if (pending?.target === 'dispatcher') applyDispatcherSuggestion(pending.suggestion);
        }}
      />

      {cartBadgeHint && typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            <div
              className="pointer-events-none fixed z-[70] -translate-x-1/2"
              style={cartBadgeHint.style}
            >
              <div className="relative rounded-lg bg-gray-900 px-2 py-1 text-center text-[11px] font-medium leading-none text-white shadow-lg dark:bg-gray-100 dark:text-gray-900">
                {cartBadgeHint.label}
                <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gray-900 dark:bg-gray-100" />
              </div>
            </div>,
            document.body
          )
        : null}

    </div>
  );
};

export default OrderDetail;
